import { Component, useState, useEffect, useCallback, useRef, useMemo } from "react";

// ─── ECharts (bundled / self-hosted — no CDN) ────────────────────────────────
import * as echarts from "echarts/core";
import { BarChart as EBarChart, LineChart as ELineChart, PieChart as EPieChart } from "echarts/charts";
import { GridComponent as EGridComponent, TooltipComponent as ETooltipComponent, LegendComponent as ELegendComponent, MarkLineComponent as EMarkLineComponent, DataZoomComponent as EDataZoomComponent } from "echarts/components";
import { CanvasRenderer as ECanvasRenderer } from "echarts/renderers";
echarts.use([EBarChart, ELineChart, EPieChart, EGridComponent, ETooltipComponent, ELegendComponent, EMarkLineComponent, EDataZoomComponent, ECanvasRenderer]);

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0d1117", color: "#e6edf3" }}>
          <div style={{ textAlign: "center", padding: 32 }}>
            <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</p>
            <p style={{ fontSize: 13, color: "#8b949e", marginBottom: 24 }}>Please refresh the page. If the problem persists, contact support.</p>
            <button onClick={() => window.location.reload()} style={{ background: "#c47a10", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "12px 24px" }}>
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Config ────────────────────────────────────────────────────────────────────
// These values come from your .env.local file in the Next.js backend
//
// API_BASE / ENTRA_REDIRECT_URI are derived from the current origin so ONE build
// serves both production (portal.powervisionbi.com) and staging
// (staging.portal.powervisionbi.com) — the app always talks to the API on the same
// host it was loaded from. Falls back to production for non-browser/SSR contexts.
// NOTE: every origin used here must be registered as a redirect URI on the Entra
// app (ENTRA_CLIENT_ID) or Microsoft sign-in will be rejected on that host.
const APP_ORIGIN = (typeof window !== "undefined" && window.location?.origin) || "https://portal.powervisionbi.com";
const API_BASE = `${APP_ORIGIN}/api`;
const ENTRA_CLIENT_ID = "db805003-64da-4cc7-80eb-8e852d8e651f";
const ENTRA_AUTHORITY = "https://login.microsoftonline.com/common";
const ENTRA_REDIRECT_URI = APP_ORIGIN;
// Google Workspace sign-in — ADDITIVE; the Microsoft/Entra path above is unchanged.
// Paste the Google OAuth Web client ID here once created; empty string hides the button.
const GOOGLE_CLIENT_ID = "266786737934-fr41qlqo7ro0frifptmisinbd516n854.apps.googleusercontent.com";
function decodeJwtPayload(t) { try { return JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))); } catch { return {}; } }
// ─── MSAL (Microsoft Authentication Library) ──────────────────────────────────
// Load MSAL from CDN — add this to your index.html:
// <script src="https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js"></script>
let _msalInstance = null;
function getMsalInstance() {
  if (!window.msal) return null;
  if (_msalInstance) return _msalInstance;
  _msalInstance = new window.msal.PublicClientApplication({
    auth: {
      clientId: ENTRA_CLIENT_ID,
      authority: ENTRA_AUTHORITY,
      redirectUri: ENTRA_REDIRECT_URI,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  return _msalInstance;
}

// ─── Auth helpers ──────────────────────────────────────────────────────────────
async function signInWithEntra() {
  const msal = getMsalInstance();
  if (!msal) throw new Error("MSAL not loaded");
  await msal.initialize();
  await msal.loginRedirect({
    scopes: ["openid", "profile", "email"],
  });
}

async function getAccessToken(forceRefresh = false) {
  const msal = getMsalInstance();
  if (!msal) return null;
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (!accounts.length) return null;
  try {
    const result = await msal.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account: accounts[0],
      forceRefresh, // mint a brand-new ID token, not the cached (possibly expired) one
    });
    // Send the ID token (audienced to our app, verifiable) — NOT the access
    // token (audienced to Microsoft Graph, which our API cannot verify).
    return result.idToken;
  } catch {
    return null;
  }
}

// Silently mint a fresh Entra ID token and persist it for API calls. No-op for
// non-MSAL (e.g. Google) sessions. Returns the fresh token, or null.
async function refreshStoredToken() {
  const t = await getAccessToken(true);
  if (t) sessionStorage.setItem("pvbi_token", t);
  return t;
}

async function signOut() {
  const msal = getMsalInstance();
  if (!msal) return;
  await msal.initialize();
  await msal.logoutPopup();
}

// ─── API calls ─────────────────────────────────────────────────────────────────
function downloadExcel(data, question) {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const escape = v => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = '﻿' + [headers.join(','), ...data.map(row => headers.map(h => escape(row[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(question || 'export').slice(0, 40).replace(/[^a-z0-9]/gi, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function extractKpiValue(answer) {
  if (!answer) return null;
  // Strip year-like numbers (1900-2199) so they don't shadow business values
  const cleaned = answer.replace(/\b(19|20|21)\d{2}\b/g, '');
  const m =
    cleaned.match(/[R$£€]\s*[\d,]+(\.\d+)?\s*[KMBkmb]?\b/) ||
    cleaned.match(/\b[\d,]+(\.\d+)?\s*[KMBkmb]\b/) ||
    cleaned.match(/\b[\d]{1,3}(,\d{3})+(\.\d+)?\b/) ||
    cleaned.match(/\b\d+\.\d+%?\b/) ||
    cleaned.match(/\b\d+%\b/) ||
    cleaned.match(/\b\d{4,}\b/) ||
    cleaned.match(/\b\d{1,3}\b/);
  return m ? m[0].trim() : null;
}

function authHeaders(token, impersonateTenantId) {
  const t = token || sessionStorage.getItem("pvbi_token");
  const h = { Authorization: `Bearer ${t}` };
  if (impersonateTenantId) h["X-Impersonate-Tenant"] = impersonateTenantId;
  return h;
}

async function fetchReports(token, impersonateTenantId) {
  const res = await fetch(`${API_BASE}/reports`, {
    headers: authHeaders(token, impersonateTenantId),
  });
  if (!res.ok) throw new Error("Failed to load reports");
  return res.json();
}

async function fetchEmbedToken(token, reportId, impersonateTenantId) {
  const res = await fetch(`${API_BASE}/embed-token`, {
    method: "POST",
    headers: { ...authHeaders(token, impersonateTenantId), "Content-Type": "application/json" },
    body: JSON.stringify({ reportId }),
  });
  if (!res.ok) throw new Error("Failed to get embed token");
  return res.json();
}

// ─── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg: "#0d1117", surface: "#161b22", surfaceHover: "#1f2937",
  border: "#21262d", borderHover: "#30363d",
  accent: "#c47a10", accentDark: "#a86209", accentLight: "#e8950f",
  accentGlow: "rgba(196,122,16,0.15)", accentGlow2: "rgba(196,122,16,0.07)",
  success: "#2ea043", successGlow: "rgba(46,160,67,0.15)",
  error: "#f85149", errorGlow: "rgba(248,81,73,0.12)",
  warn: "#d29922", textPrimary: "#e6edf3",
  textSecondary: "#8b949e", textMuted: "#484f58",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Plus Jakarta Sans',sans-serif;background:${T.bg};color:${T.textPrimary};min-height:100vh;overflow-x:hidden}
  .mono{font-family:'JetBrains Mono',monospace}
  .grid-bg{position:fixed;inset:0;pointer-events:none;z-index:0;
    background-image:linear-gradient(rgba(196,122,16,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(196,122,16,0.035) 1px,transparent 1px);
    background-size:48px 48px}
  .orb{position:fixed;border-radius:50%;pointer-events:none;z-index:0;filter:blur(90px)}
  @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes pulse2{0%,100%{opacity:1}50%{opacity:.45}}
  /* Home hero — "We Make Data Dance": bars that actually dance. Staggered per bar
     via inline animation-delay so the mark reads as live data, not decoration. */
  @keyframes dance{0%,100%{transform:scaleY(.28)}50%{transform:scaleY(1)}}
  @keyframes driftGlow{0%,100%{opacity:.5;transform:translate(0,0) scale(1)}50%{opacity:.85;transform:translate(14px,-10px) scale(1.12)}}
  @keyframes sweep{0%{background-position:-180% 0}100%{background-position:180% 0}}
  .dance-bar{transform-origin:bottom center;animation:dance 1.5s ease-in-out infinite}
  .hero-word{background:linear-gradient(90deg,#e6edf3 20%,#e8950f 45%,#e6edf3 70%);background-size:180% 100%;
    -webkit-background-clip:text;background-clip:text;color:transparent;animation:sweep 5.5s linear infinite}
  .launch-tile{transition:transform .2s cubic-bezier(.4,0,.2,1),border-color .2s,box-shadow .2s,background .2s}
  .launch-tile:hover{transform:translateY(-4px)}
  @keyframes waveDrift{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes ringGlow{0%,100%{filter:drop-shadow(0 0 3px rgba(196,122,16,.35))}50%{filter:drop-shadow(0 0 10px rgba(196,122,16,.6))}}
  .wave-strip{animation:waveDrift 26s linear infinite}
  .ring-live{animation:ringGlow 3.4s ease-in-out infinite}
  .pip-in{animation:fadeUp .5s ease both}
  @media(prefers-reduced-motion:reduce){
    .wave-strip,.ring-live,.pip-in{animation:none !important}
  }
  /* Respect users who ask the OS to stop motion — the hero is decorative. */
  @media(prefers-reduced-motion:reduce){
    .dance-bar,.hero-word,.orb-drift{animation:none !important}
    .hero-word{color:${T.textPrimary};-webkit-text-fill-color:${T.textPrimary}}
  }
  .fu{animation:fadeUp .45s ease both}
  .fu1{animation:fadeUp .45s .06s ease both}
  .fu2{animation:fadeUp .45s .13s ease both}
  .fu3{animation:fadeUp .45s .2s ease both}
  .spin{animation:spin .7s linear infinite}
  .field-group{display:flex;flex-direction:column;gap:6px}
  .field-label{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${T.textSecondary}}
  .field-input{background:rgba(255,255,255,.04);border:1px solid ${T.border};border-radius:8px;color:${T.textPrimary};font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;padding:11px 14px;outline:none;transition:border-color .2s,box-shadow .2s,background .2s;width:100%}
  .field-input:focus{border-color:${T.accent};box-shadow:0 0 0 3px ${T.accentGlow};background:rgba(196,122,16,.04)}
  .field-input::placeholder{color:${T.textMuted}}
  .btn-primary{background:${T.accent};border:none;border-radius:8px;color:#fff;cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:600;padding:12px 24px;transition:background .2s,transform .1s,box-shadow .2s;width:100%}
  .btn-primary:hover{background:${T.accentLight};box-shadow:0 0 28px ${T.accentGlow};transform:translateY(-1px)}
  .btn-primary:active{transform:translateY(0)}
  .btn-primary:disabled{opacity:.45;cursor:not-allowed;transform:none}
  .btn-secondary{background:transparent;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:9px 18px;transition:border-color .2s,color .2s,background .2s}
  .btn-secondary:hover{border-color:${T.borderHover};color:${T.textPrimary};background:rgba(255,255,255,.04)}
  .btn-ghost{background:none;border:none;color:${T.accent};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:4px 0;transition:opacity .2s}
  .btn-ghost:hover{opacity:.72}
  .card{background:${T.surface};border:1px solid ${T.border};border-radius:12px;padding:24px}
  .divider{border:none;border-top:1px solid ${T.border};margin:20px 0}
  .spinner{width:17px;height:17px;border:2px solid rgba(255,255,255,.2);border-top-color:#fff;border-radius:50%;display:inline-block}
  .alert{border-radius:8px;padding:12px 16px;font-size:13px;font-weight:500;display:flex;align-items:flex-start;gap:10px;animation:fadeUp .3s ease}
  .alert-error{background:${T.errorGlow};border:1px solid rgba(248,81,73,.25);color:#fca5a5}
  .alert-success{background:${T.successGlow};border:1px solid rgba(46,160,67,.25);color:#86efac}
  .alert-info{background:${T.accentGlow2};border:1px solid rgba(196,122,16,.2);color:#fcd34d}
  .badge{display:inline-flex;align-items:center;gap:5px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:.04em;padding:3px 10px}
  .badge-success{background:${T.successGlow};color:${T.success};border:1px solid rgba(46,160,67,.25)}
  .badge-info{background:${T.accentGlow};color:${T.accent};border:1px solid rgba(196,122,16,.25)}
  .nav-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:background .15s,color .15s;color:${T.textSecondary};border:none;background:none;width:100%;text-align:left;font-family:'Plus Jakarta Sans',sans-serif}
  .nav-item:hover{background:rgba(255,255,255,.05);color:${T.textPrimary}}
  .nav-item.active{background:${T.accentGlow};color:${T.accent}}
  .report-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:20px;cursor:pointer;transition:border-color .2s,transform .2s,box-shadow .2s;animation:fadeUp .4s ease both}
  .report-card:hover{border-color:${T.accent};transform:translateY(-2px);box-shadow:0 8px 32px rgba(196,122,16,.12)}
  .stat-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:18px 20px;animation:fadeUp .4s ease both}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
  @media print{
    body > * { display: none !important; }
    #report-print-frame { display: block !important; position: fixed; inset: 0; width: 100vw; height: 100vh; border: none; z-index: 9999; background: white; }
  }
  .hamburger-btn{display:none;align-items:center;justify-content:center;background:none;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;padding:8px;flex-shrink:0}
  .sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:199;cursor:pointer}
  @media(max-width:768px){
    .hamburger-btn{display:flex !important}
    .mob-sidebar{position:fixed !important;left:0;top:0;bottom:0;z-index:200;transform:translateX(-100%);transition:transform .3s ease;overflow-y:auto}
    .mob-sidebar.open{transform:translateX(0) !important;box-shadow:4px 0 32px rgba(0,0,0,.6)}
    .sidebar-overlay.open{display:block}
    .kpi-grid{grid-template-columns:1fr !important}
    .reports-grid{grid-template-columns:1fr !important}
    .mob-pad{padding:18px 14px !important}
    .mob-stats{grid-template-columns:1fr !important}
    .mob-header{flex-direction:column;align-items:flex-start !important;gap:12px}
  }
  @media(min-width:769px) and (max-width:1100px){
    .kpi-grid{grid-template-columns:repeat(2,1fr) !important}
    .reports-grid{grid-template-columns:repeat(2,1fr) !important}
  }
`;

const Icon = ({ n, size = 16, color = "currentColor" }) => {
  const icons = {
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    db: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
    chart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    grid: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    logout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    microsoft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><rect x="1" y="1" width="10.5" height="10.5" fill="#f25022"/><rect x="12.5" y="1" width="10.5" height="10.5" fill="#7fba00"/><rect x="1" y="12.5" width="10.5" height="10.5" fill="#00a4ef"/><rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#ffb900"/></svg>,
    zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    send: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    bell: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    starEmpty: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    messageSquare: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    menu: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    layers: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  };
  return icons[n] || null;
};

// ─── Home hero marks ──────────────────────────────────────────────────────────

// The Power Vision mark: five bars mid-dance, sized like a bar chart so it reads
// as data rather than ornament. Heights and delays are hand-picked so no two bars
// peak together — a uniform stagger looks mechanical.
function PvbiMark({ size = 46 }) {
  const bars = [
    { h: 0.50, d: "0s" },
    { h: 0.78, d: ".18s" },
    { h: 1.00, d: ".07s" },
    { h: 0.64, d: ".31s" },
    { h: 0.86, d: ".24s" },
  ];
  const w = size * 0.14, gap = size * 0.075;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap, height: size }} aria-hidden="true">
      {bars.map((b, i) => (
        <span key={i} className="dance-bar" style={{
          width: w, height: size * b.h, borderRadius: w / 2, animationDelay: b.d,
          background: `linear-gradient(180deg, ${T.accentLight}, ${T.accentDark})`,
          boxShadow: `0 0 14px ${T.accentGlow}`,
        }} />
      ))}
    </div>
  );
}

// The client's mark. A real logo when one is configured, otherwise a monogram from
// the tenant name — so this never renders as a broken image or an empty gap.
function ClientMark({ name, logoUrl, size = 46 }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || "?").replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/)
    .slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
  if (logoUrl && !failed) {
    return <img src={logoUrl} alt={name || "Client"} onError={() => setFailed(true)}
      style={{ height: size, maxWidth: size * 3.4, objectFit: "contain", display: "block" }} />;
  }
  return (
    <div title={name} style={{
      height: size, minWidth: size, padding: `0 ${size * 0.22}px`, borderRadius: size * 0.26,
      background: "rgba(255,255,255,.05)", border: `1px solid ${T.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, letterSpacing: ".02em", color: T.textPrimary,
    }}>{initials}</div>
  );
}

// Numbers that count up on mount. Eased, so it decelerates into the final value
// instead of arriving at a constant clip.
function CountUp({ value, duration = 950, suffix = "" }) {
  const [n, setN] = useState(0);
  const target = Number(value);
  useEffect(() => {
    if (!Number.isFinite(target)) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setN(target); return; }
    let raf, start;
    const step = ts => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));   // easeOutCubic
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  if (!Number.isFinite(target)) return <>{value}</>;
  return <>{n}{suffix}</>;
}

// Progress ring. Draws itself in by transitioning stroke-dashoffset after mount,
// so the figure reads as something measured rather than printed.
function HomeRing({ value = 0, size = 96, stroke = 8, color = T.accent, sub }) {
  const [shown, setShown] = useState(0);
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  useEffect(() => { const t = setTimeout(() => setShown(v), 120); return () => clearTimeout(t); }, [v]);
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth={stroke} />
        <circle className={v > 0 ? "ring-live" : undefined}
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * shown) / 100}
          style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: size * 0.26, fontWeight: 700, color: T.textPrimary, lineHeight: 1 }}><CountUp value={v} suffix="%" /></span>
        {sub && <span style={{ fontSize: size * 0.11, color: T.textMuted, marginTop: 3, letterSpacing: ".06em", textTransform: "uppercase" }}>{sub}</span>}
      </div>
    </div>
  );
}

// Portfolio health as one stacked bar. Segments grow from zero on mount.
function HealthBar({ onTrack = 0, atRisk = 0, offTrack = 0 }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => { const t = setTimeout(() => setGrown(true), 160); return () => clearTimeout(t); }, []);
  const total = onTrack + atRisk + offTrack;
  const segs = [
    { n: onTrack,  c: T.success, label: "On track" },
    { n: atRisk,   c: T.warn,    label: "At risk" },
    { n: offTrack, c: T.error,   label: "Off track" },
  ].filter(s => s.n > 0);
  if (!total) return null;
  return (
    <div style={{ flex: "1 1 220px", minWidth: 180 }}>
      <div style={{ display: "flex", height: 8, borderRadius: 8, overflow: "hidden", background: "rgba(255,255,255,.06)", gap: 2 }}>
        {segs.map(s => (
          <div key={s.label} title={`${s.label}: ${s.n}`} style={{
            width: grown ? `${(s.n / total) * 100}%` : "0%", background: s.c, borderRadius: 8,
            transition: "width 1s cubic-bezier(.4,0,.2,1)",
          }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
        {segs.map(s => (
          <span key={s.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textSecondary }}>
            <span style={{ width: 7, height: 7, borderRadius: 2, background: s.c }} />
            {s.label} <strong style={{ color: T.textPrimary, fontWeight: 600 }}>{s.n}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

// Decorative background motif — a slow-drifting layered waveform. Purely visual,
// so it carries no data and sits behind content at low opacity.
function WaveStrip({ height = 64, opacity = 0.5 }) {
  return (
    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height, overflow: "hidden", pointerEvents: "none", opacity }} aria-hidden="true">
      <svg className="wave-strip" width="200%" height={height} viewBox="0 0 1200 64" preserveAspectRatio="none" style={{ display: "block" }}>
        <defs>
          <linearGradient id="wv" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={T.accent} stopOpacity="0" />
            <stop offset="50%" stopColor={T.accent} stopOpacity=".55" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,44 C100,16 200,60 300,38 C400,16 500,54 600,34 C700,14 800,56 900,36 C1000,16 1100,52 1200,32 L1200,64 L0,64 Z"
          fill="none" stroke="url(#wv)" strokeWidth="1.5" />
        <path d="M0,54 C120,32 240,64 360,48 C480,32 600,62 720,46 C840,30 960,60 1080,44 L1200,38"
          fill="none" stroke="url(#wv)" strokeWidth="1" opacity=".6" />
      </svg>
    </div>
  );
}

const CAT_COLOR = { Finance: T.accent, Sales: T.success, Operations: "#a78bfa", Purchasing: T.warn };

// ─── Chart components ──────────────────────────────────────────────────────────
const CHART_COLORS = ["#c47a10", "#2ea043", "#3b82f6", "#a78bfa", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"];

// (Legacy hand-rolled SVG BarChart/LineChart/PieChart removed — all charts now
//  render through the premium ECharts ChartWidget below.)

// Premium ECharts renderer for AI-assistant chart results (bar / line / pie).
// Used by every client's chat, so this is the shared "all reports" chart surface.
function ChartWidget({ chart }) {
  if (!chart || !chart.data?.length) return null;
  const TH = chartTheme();
  const names = chart.data.map(d => d.name);
  const values = chart.data.map(d => Number(d.value) || 0);
  const fmtNum = v => { const n = Number(v) || 0; return n >= 1e6 ? `${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `${(n/1e3).toFixed(1)}K` : `${n}`; };

  let option;
  if (chart.type === "pie") {
    option = {
      animationDuration: 600,
      tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.name}: ${fmtNum(p.value)} (${p.percent}%)` },
      legend: { type: "scroll", orient: "vertical", right: 6, top: "center", textStyle: { color: T.textSecondary, fontSize: 11 }, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
      series: [{
        type: "pie", radius: ["42%", "70%"], center: ["36%", "52%"], avoidLabelOverlap: true, padAngle: 2,
        itemStyle: { borderRadius: 6, borderColor: T.bg, borderWidth: 2 },
        data: chart.data.map((d, i) => ({ name: d.name, value: Number(d.value) || 0, itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] } })),
        label: { color: T.textSecondary, fontSize: 11 }, labelLine: { lineStyle: { color: T.border } },
      }],
    };
  } else if (chart.type === "line") {
    option = {
      animationDuration: 700,
      grid: { top: 24, right: 18, bottom: names.length > 6 ? 56 : 38, left: 56 },
      tooltip: { ...TH.tooltip, valueFormatter: v => fmtNum(v) },
      xAxis: { type: "category", data: names, boundaryGap: false, axisLabel: { ...TH.axisLabel, interval: 0, rotate: names.length > 6 ? 30 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
      yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmtNum }, splitLine: TH.splitLine, axisLine: { show: false } },
      series: [{ type: "line", smooth: true, data: values, lineStyle: { color: T.accent, width: 3 }, itemStyle: { color: T.accent }, symbol: "circle", symbolSize: 7, areaStyle: { color: TH.grad("rgba(196,122,16,0.28)", "rgba(196,122,16,0.02)") } }],
    };
  } else {
    option = {
      animationDuration: 700,
      grid: { top: 24, right: 18, bottom: names.length > 5 ? 60 : 38, left: 56 },
      tooltip: { ...TH.tooltip, valueFormatter: v => fmtNum(v) },
      xAxis: { type: "category", data: names, axisLabel: { ...TH.axisLabel, interval: 0, rotate: names.length > 5 ? 30 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
      yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmtNum }, splitLine: TH.splitLine, axisLine: { show: false } },
      series: [{ type: "bar", barWidth: "55%", data: values.map((v, i) => ({ value: v, itemStyle: { color: TH.grad(CHART_COLORS[i % CHART_COLORS.length], CHART_COLORS[i % CHART_COLORS.length] + "88"), borderRadius: [5, 5, 0, 0] } })), label: { show: values.length <= 12, position: "top", formatter: p => fmtNum(p.value), color: T.textMuted, fontSize: 10 } }],
    };
  }

  return (
    <div style={{ marginTop: 12, padding: "14px 12px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
      {chart.title && <p style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary, textAlign: "center", marginBottom: 8 }}>{chart.title}</p>}
      <EChart option={option} height={300} />
    </div>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
// Now uses Microsoft Entra External ID via MSAL popup
function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const googleDivRef = useRef(null);

  const handleMicrosoftSignIn = async () => {
    setErr(""); setLoading(true);
    try {
      await signInWithEntra(); // redirects away — no return value
    } catch (e) {
      setErr("Sign-in failed. Please try again or contact Power Vision BI support.");
      setLoading(false);
    }
  };

  // Google Workspace sign-in via Google Identity Services (renders Google's button +
  // One Tap auto sign-in for returning users). Token is verified server-side by issuer.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let tries = 0;
    const iv = setInterval(() => {
      const gid = window.google?.accounts?.id;
      if (gid) {
        clearInterval(iv);
        gid.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: true,
          callback: (resp) => {
            const t = resp.credential;
            const p = decodeJwtPayload(t);
            sessionStorage.setItem("pvbi_provider", "google");
            onLogin({ email: p.email, name: p.name || (p.email || "").split("@")[0] }, t);
          },
        });
        if (googleDivRef.current) {
          gid.renderButton(googleDivRef.current, { theme: "outline", size: "large", width: 336, text: "continue_with", shape: "rectangular" });
        }
        gid.prompt(); // One Tap for returning users
      } else if (++tries > 40) {
        clearInterval(iv); // GIS script didn't load (~10s) — Microsoft sign-in still works
      }
    }, 250);
    return () => clearInterval(iv);
  }, [onLogin]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, position: "relative", zIndex: 1 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div className="fu" style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          <div style={{ marginTop: 10, height: 1, width: 200, background: `linear-gradient(90deg, transparent, ${T.accent}, transparent)` }} />
          <p style={{ marginTop: 10, fontSize: 12, color: T.textMuted, letterSpacing: "0.06em" }}>CLIENT ANALYTICS PORTAL</p>
        </div>

        <div className="card fu1" style={{ padding: 32 }}>
          <h2 style={{ fontSize: 19, fontWeight: 600, marginBottom: 4 }}>Welcome back</h2>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 28 }}>Sign in with your company account to view your reports</p>

          {err && <div className="alert alert-error" style={{ marginBottom: 18 }}><Icon n="alert" size={15} color={T.error} />{err}</div>}

          {/* Microsoft sign-in button */}
          <button
            className="btn-primary"
            onClick={handleMicrosoftSignIn}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#fff", color: "#1a1a1a", border: "1px solid #ddd" }}
          >
            {loading
              ? <><span className="spinner spin" style={{ borderTopColor: T.accent, borderColor: "rgba(196,122,16,.3)" }} />Signing in…</>
              : <><Icon n="microsoft" size={18} />Continue with Microsoft</>
            }
          </button>

          {/* Google Workspace sign-in (renders only when configured) */}
          {GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 12px" }}>
                <span style={{ flex: 1, height: 1, background: T.border }} />
                <span style={{ fontSize: 11, color: T.textMuted }}>or</span>
                <span style={{ flex: 1, height: 1, background: T.border }} />
              </div>
              <div ref={googleDivRef} style={{ display: "flex", justifyContent: "center" }} />
            </div>
          )}

          <hr className="divider" />
          <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center" }}>
            New customer? <button className="btn-ghost" style={{ fontSize: 12 }}>Contact Power Vision BI</button>
          </p>
        </div>

        <p className="fu2" style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: T.textMuted }}>
          <span style={{ color: "#ffffff", fontWeight: 600, letterSpacing: "0.04em" }}>We Make Data Dance!</span> · Secured with Microsoft Entra ID
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────────
// Now loads real reports from /api/reports and embeds via /api/embed-token
// ─── ECharts wrapper (premium, themed, responsive) ───────────────────────────
function EChart({ option, height = 320, onClick }) {
  const ref = useRef(null);
  const inst = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!inst.current) inst.current = echarts.init(ref.current, null, { renderer: "canvas" });
    inst.current.setOption(option, true);
    if (onClick) { inst.current.off("click"); inst.current.on("click", onClick); inst.current.getZr().setCursorStyle("pointer"); }
    const ro = new ResizeObserver(() => { if (inst.current) inst.current.resize(); });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [option, onClick]);
  useEffect(() => () => { if (inst.current) { inst.current.dispose(); inst.current = null; } }, []);
  return <div ref={ref} style={{ width: "100%", height }} />;
}

// Shared premium chart styling, parameterised by the portal theme tokens (T).
function chartTheme() {
  const grad = (c1, c2) => new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: c1 }, { offset: 1, color: c2 },
  ]);
  return {
    grad,
    axisLabel: { color: T.textMuted, fontSize: 10, fontFamily: "'Plus Jakarta Sans',sans-serif" },
    axisLine: { lineStyle: { color: T.border } },
    splitLine: { lineStyle: { color: T.border, type: "dashed", opacity: 0.4 } },
    tooltip: {
      trigger: "axis",
      backgroundColor: T.surface,
      borderColor: T.border,
      borderWidth: 1,
      padding: [8, 12],
      textStyle: { color: T.textPrimary, fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12 },
      axisPointer: { type: "shadow", shadowStyle: { color: "rgba(196,122,16,0.07)" } },
      extraCssText: "box-shadow:0 8px 24px rgba(0,0,0,0.35);border-radius:8px;",
    },
  };
}

// ── East End Welding · Job Cost Review (on-demand, date-ranged) ──────────────
function JobCostReview({ apiBase, T, Icon }) {
  // Local-date ISO (avoid toISOString() UTC shift that rolls midnight back a day in +UTC zones).
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const now = new Date();
  const [type, setType] = useState("invoiced");
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [view, setView] = useState("all");        // all | fully | wip
  const [mode, setMode] = useState("dashboard");  // dashboard | detail
  const [sort, setSort] = useState({ k: "customer", dir: 1 });
  const [drill, setDrill] = useState(null);       // { title, sub, rows } | null

  const run = (t = type, f = from, to2 = to) => {
    setLoading(true); setErr("");
    fetch(`${apiBase}/eew/jobcost?key=eew-jobcost-2026&type=${t}&from=${f}&to=${to2}`)
      .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setData(d); })
      .catch(e => setErr(e.message || "Failed to run")).finally(() => setLoading(false));
  };
  const preset = (label) => {
    const y = now.getFullYear(), m = now.getMonth();
    let f, t2;
    if (label === "This month") { f = new Date(y, m, 1); t2 = now; }
    else if (label === "Last month") { f = new Date(y, m - 1, 1); t2 = new Date(y, m, 0); }
    else if (label === "This quarter") { const q = Math.floor(m / 3) * 3; f = new Date(y, q, 1); t2 = now; }
    else if (label === "YTD") { f = new Date(y, 0, 1); t2 = now; }
    else { f = new Date(y, m, 1); t2 = now; }
    setFrom(iso(f)); setTo(iso(t2)); run(type, iso(f), iso(t2));
  };
  // Auto-run month-to-date on first open so the report lands on live MTD numbers.
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const money = (v) => { const n = Number(v || 0); return (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString(); };
  const num = (v, d = 1) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });
  const pct = (v) => v == null ? "—" : (Number(v) * 100).toFixed(1) + "%";
  const dt = (v) => v ? String(v).slice(0, 10) : "—";

  const COLS = [
    { k: "so", l: "SO", a: "left" }, { k: "customer", l: "Customer", a: "left" }, { k: "descr", l: "Description", a: "left" },
    { k: "order_date", l: "Order", a: "left", f: dt }, { k: "last_invoiced", l: "Last Inv.", a: "left", f: dt },
    { k: "revenue", l: "Revenue", a: "right", f: money }, { k: "budget_hours", l: "Bud Hrs", a: "right", f: v => num(v) },
    { k: "actual_hours", l: "Act Hrs", a: "right", f: v => num(v) }, { k: "actual_labor", l: "Act Labor", a: "right", f: money },
    { k: "parts", l: "Parts", a: "right", f: money }, { k: "other", l: "Other", a: "right", f: money },
    { k: "total_cost", l: "Total Cost", a: "right", f: money }, { k: "gain", l: "Gain/(Loss)", a: "right", f: money },
    { k: "margin", l: "Margin", a: "right", f: pct }, { k: "remaining_to_invoice", l: "Remaining", a: "right", f: money },
  ];

  const rows = useMemo(() => {
    let r = (data?.rows || []);
    if (view === "fully") r = r.filter(x => x.fully_invoiced);
    else if (view === "wip") r = r.filter(x => Number(x.remaining_to_invoice || 0) > 0.01);
    const { k, dir } = sort;
    return [...r].sort((a, b) => {
      let av = a[k], bv = b[k];
      if (["revenue", "budget_hours", "actual_hours", "actual_labor", "parts", "other", "total_cost", "gain", "margin", "remaining_to_invoice"].includes(k)) { av = Number(av || 0); bv = Number(bv || 0); return dir * (av - bv); }
      return dir * String(av || "").localeCompare(String(bv || ""));
    });
  }, [data, view, sort]);

  const s = data?.summary;
  const TH = chartTheme();
  const fmt0 = (v) => { const n = Number(v || 0); const a = Math.abs(n), sg = n < 0 ? "-" : ""; return a >= 1e6 ? `${sg}$${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${sg}$${(a / 1e3).toFixed(0)}K` : `${sg}$${Math.round(a)}`; };
  const bandOf = (x) => { const g = Number(x.gain || 0), m = x.margin == null ? null : Number(x.margin); if (m == null) return null; if (g < 0 || m < 0) return "Loss"; if (m < 0.15) return "0–15%"; if (m < 0.30) return "15–30%"; if (m < 0.50) return "30–50%"; return "50%+"; };
  const dash = useMemo(() => {
    const r = (data?.rows || []).filter(x => x.fully_invoiced);   // dashboard = fully-invoiced (completed) jobs only
    const byC = {};
    r.forEach(x => { const c = x.customer || "—"; const o = byC[c] || (byC[c] = { rev: 0, cost: 0, gain: 0, jobs: 0 }); o.rev += Number(x.revenue || 0); o.cost += Number(x.total_cost || 0); o.gain += Number(x.gain || 0); o.jobs++; });
    const cust = Object.entries(byC).map(([name, v]) => ({ name, ...v }));
    const topRev = [...cust].sort((a, b) => b.rev - a.rev).slice(0, 10);
    const gainByCust = [...cust].sort((a, b) => b.gain - a.gain).slice(0, 10);
    const bands = { "Loss": 0, "0–15%": 0, "15–30%": 0, "30–50%": 0, "50%+": 0 };
    r.forEach(x => { const b = bandOf(x); if (b) bands[b]++; });
    const problem = [...r].filter(x => Number(x.revenue || 0) !== 0).sort((a, b) => Number(a.margin ?? 9) - Number(b.margin ?? 9)).slice(0, 10);
    const trev = r.reduce((a, x) => a + Number(x.revenue || 0), 0), tcost = r.reduce((a, x) => a + Number(x.total_cost || 0), 0), tgain = r.reduce((a, x) => a + Number(x.gain || 0), 0);
    const fiSummary = { jobs: r.length, revenue: Math.round(trev), cost: Math.round(tcost), gain: Math.round(tgain), margin: trev ? tgain / trev : null, labor_over: r.filter(x => x.labor_over).length };
    // cost + margin over the selected period — bucketed by the run's own date basis (weekly if <=~9wk span, else monthly)
    const df = data?.from, dt2 = data?.to, invoiced = data?.type === "invoiced";
    const spanD = df && dt2 ? (new Date(dt2) - new Date(df)) / 864e5 : 0;
    const weekly = spanD > 0 && spanD <= 63;
    const basis = (x) => String((invoiced ? x.last_invoiced : x.order_date) || "").slice(0, 10);
    const bkt = (d) => { if (!d) return null; if (!weekly) return d.slice(0, 7); const t = new Date(d + "T00:00:00Z"); if (isNaN(t)) return null; t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7)); return t.toISOString().slice(0, 10); };
    const tb = {};
    r.forEach(x => { const b = bkt(basis(x)); if (!b) return; const o = tb[b] || (tb[b] = { rev: 0, cost: 0, gain: 0, rows: [] }); o.rev += Number(x.revenue || 0); o.cost += Number(x.total_cost || 0); o.gain += Number(x.gain || 0); o.rows.push(x); });
    const labels = Object.keys(tb).sort();
    const fmtLbl = (l) => weekly ? new Date(l + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : new Date(l + "-01T00:00:00Z").toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    const trend = { keys: labels, labels: labels.map(fmtLbl), cost: labels.map(l => Math.round(tb[l].cost)), margin: labels.map(l => tb[l].rev ? Math.round(tb[l].gain / tb[l].rev * 1000) / 10 : null), buckets: tb, weekly };
    return { topRev, gainByCust, bands, problem, trend, fiSummary };
  }, [data]);
  const hbar = (rowsIn, valFn, color, labelFn) => ({
    animationDuration: 600, grid: { top: 8, right: 74, bottom: 22, left: 150 },
    tooltip: { ...TH.tooltip, trigger: "item", valueFormatter: v => money(v) },
    xAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    yAxis: { type: "category", data: [...rowsIn].reverse().map(labelFn), axisLabel: { ...TH.axisLabel }, axisLine: TH.axisLine, axisTick: { show: false } },
    series: [{ type: "bar", barWidth: "62%", data: [...rowsIn].reverse().map(valFn), label: { show: true, position: "right", formatter: p => fmt0(p.value), color: T.textSecondary, fontSize: 10 }, itemStyle: typeof color === "string" ? { borderRadius: [0, 5, 5, 0], color } : undefined }],
  });
  const BAND_COLOR = { "Loss": T.error, "0–15%": "#d29922", "15–30%": "#e0a836", "30–50%": "#7bb662", "50%+": "#2ea043" };
  const bandOption = {
    animationDuration: 700, tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.name}: ${p.value} jobs (${p.percent}%)` },
    series: [{ type: "pie", radius: ["46%", "72%"], center: ["50%", "52%"], itemStyle: { borderColor: T.bg, borderWidth: 2 }, label: { color: T.textSecondary, fontSize: 11, formatter: "{b}\n{c}" }, data: Object.entries(dash.bands).map(([k, v]) => ({ name: k, value: v, itemStyle: { color: BAND_COLOR[k] } })) }],
  };
  const trendOption = {
    animationDuration: 700, grid: { top: 30, right: 58, bottom: dash.trend.labels.length > 8 ? 54 : 34, left: 62 },
    legend: { data: ["Total Cost", "Margin %"], textStyle: { color: T.textSecondary, fontSize: 11 }, top: 2, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
    tooltip: { ...TH.tooltip, formatter: (ps) => { const c = ps.find(p => p.seriesName === "Total Cost"), m = ps.find(p => p.seriesName === "Margin %"); return `<b>${ps[0].axisValue}</b><br/>Cost: ${money(c ? c.value : 0)}<br/>Margin: ${m && m.value != null ? m.value + "%" : "—"}`; } },
    xAxis: { type: "category", data: dash.trend.labels, axisLabel: { ...TH.axisLabel, rotate: dash.trend.labels.length > 8 ? 40 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: [
      { type: "value", name: "Cost", nameTextStyle: { color: T.textMuted, fontSize: 10 }, axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
      { type: "value", name: "Margin %", position: "right", axisLabel: { ...TH.axisLabel, formatter: "{value}%" }, splitLine: { show: false }, axisLine: { show: false } },
    ],
    series: [
      { name: "Total Cost", type: "bar", yAxisIndex: 0, barWidth: "46%", data: dash.trend.cost, itemStyle: { borderRadius: [5, 5, 0, 0], color: TH.grad("#8a8f98", "#5b6069") } },
      { name: "Margin %", type: "line", yAxisIndex: 1, smooth: true, data: dash.trend.margin, connectNulls: true, lineStyle: { color: "#2ea043", width: 3 }, itemStyle: { color: "#2ea043" }, symbol: "circle", symbolSize: 7, label: { show: true, position: "top", formatter: (p) => p.value == null ? "" : p.value + "%", color: T.textSecondary, fontSize: 10 } },
    ],
  };
  const openDrill = (title, sub, drows) => setDrill({ title, sub, rows: drows });
  const drillCust = (p) => { const c = p.name; openDrill(c, "Fully invoiced jobs for this customer", (data?.rows || []).filter(r => r.fully_invoiced && (r.customer || "—") === c)); };
  const drillBand = (p) => { const b = p.name; openDrill("Margin band · " + b, "Fully invoiced jobs in this margin band", (data?.rows || []).filter(r => r.fully_invoiced && bandOf(r) === b)); };
  const drillJob = (p) => { const so = String(p.name).split(" · ")[0].trim(); openDrill("Job " + so, "", (data?.rows || []).filter(r => r.so === so)); };
  const drillBucket = (p) => { const i = dash.trend.labels.indexOf(p.axisValue ?? p.name); const key = dash.trend.keys[i]; const b = key && dash.trend.buckets[key]; openDrill((dash.trend.weekly ? "Week of " : "") + (p.axisValue ?? p.name), b ? `${b.rows.length} jobs` : "", (b && b.rows) || []); };
  const chartCard = (title, sub, option, h = 300, onClick) => (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 2 }}>{title}</div>
        {onClick && <div style={{ fontSize: 10, color: T.textMuted, opacity: .8 }}>click to drill ›</div>}
      </div>
      {sub && <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 8 }}>{sub}</div>}
      <EChart option={option} height={h} onClick={onClick} />
    </div>
  );
  const exportCsv = () => {
    const esc = (v) => { v = v == null ? "" : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
    const head = COLS.map(c => c.l).concat(["Fully Invoiced", "Labor Over Budget"]);
    const lines = [head.join(",")].concat(rows.map(r => COLS.map(c => esc(c.f && (c.k === "order_date" || c.k === "last_invoiced") ? dt(r[c.k]) : r[c.k])).concat([r.fully_invoiced ? "Yes" : "No", r.labor_over ? "Yes" : "No"]).join(",")));
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `EEW_JobCostReview_${type}_${from}_to_${to}.csv`; document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 100);
  };
  const tile = (label, val, color) => (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 16px", minWidth: 120, flex: 1 }}>
      <div style={{ fontSize: 11, color: T.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 3, color: color || T.text, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
    </div>
  );
  const inputStyle = { background: T.bg, color: "#fff", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 11px", fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif", colorScheme: "dark" };
  const chip = (label, active, onClick) => (
    <div onClick={onClick} style={{ padding: "6px 13px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: `1px solid ${active ? T.accent : T.border}`, background: active ? T.accent : "transparent", color: active ? "#fff" : T.textSecondary }}>{label}</div>
  );

  return (
    <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
      {/* controls */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: T.textSecondary, fontWeight: 600 }}>Date basis
          <select value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, minWidth: 180 }}>
            <option value="invoiced">Invoiced date</option>
            <option value="order">Sales Order date</option>
          </select></label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: T.textSecondary, fontWeight: 600 }}>From
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: T.textSecondary, fontWeight: 600 }}>To
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></label>
        <button onClick={() => run()} disabled={loading} style={{ background: T.accent, color: "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 13.5, fontWeight: 700, cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", gap: 7, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          {loading ? <span className="spinner spin" style={{ width: 14, height: 14, borderTopColor: "#fff", borderColor: "rgba(255,255,255,.35)" }} /> : <Icon n="external" size={14} color="#fff" />}Run report</button>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", alignItems: "center" }}>
          {["This month", "Last month", "This quarter", "YTD"].map(p => chip(p, false, () => preset(p)))}
        </div>
      </div>

      {err && <div className="alert alert-error"><Icon n="alert" size={15} color={T.error} />{err}</div>}
      {loading && !data && <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.textSecondary, padding: 40 }}><span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: "rgba(196,122,16,.3)" }} />Running job cost review…</div>}
      {!loading && !data && !err && <div style={{ color: T.textSecondary, padding: 40, textAlign: "center", fontSize: 14 }}>Choose a date basis and range, then <b>Run report</b>.</div>}

      {data && (<>
        {mode === "dashboard" ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {tile("Jobs (invoiced)", num(dash.fiSummary.jobs, 0))}
            {tile("Revenue", money(dash.fiSummary.revenue))}
            {tile("Total Cost", money(dash.fiSummary.cost))}
            {tile("Gain/(Loss)", money(dash.fiSummary.gain), dash.fiSummary.gain < 0 ? T.error : "#2ea043")}
            {tile("Margin", pct(dash.fiSummary.margin), (dash.fiSummary.margin != null && dash.fiSummary.margin < 0.15) ? T.error : "#2ea043")}
            {tile("Labor > Budget", num(dash.fiSummary.labor_over, 0), dash.fiSummary.labor_over ? "#d29922" : T.textSecondary)}
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {tile("Jobs", num(s.jobs, 0))}
            {tile("Revenue", money(s.revenue))}
            {tile("Total Cost", money(s.cost))}
            {tile("Gain/(Loss)", money(s.gain), s.gain < 0 ? T.error : "#2ea043")}
            {tile("Margin", pct(s.margin), (s.margin != null && s.margin < 0.15) ? T.error : "#2ea043")}
            {tile("WIP (Remaining)", money(s.wip), "#d29922")}
            {tile("Fully Invoiced", num(s.fully_invoiced, 0), "#2ea043")}
            {tile("Labor > Budget", num(s.labor_over, 0), s.labor_over ? "#d29922" : T.textSecondary)}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {chip("📊 Dashboard", mode === "dashboard", () => setMode("dashboard"))}
          {chip("📋 Detail table", mode === "detail", () => setMode("detail"))}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: T.textSecondary }}>{data.rows.length} jobs · {type === "invoiced" ? "invoiced" : "ordered"} {from} → {to}</span>
            <button onClick={exportCsv} style={{ background: "rgba(46,160,67,.12)", color: "#2ea043", border: "1px solid #2ea04355", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Plus Jakarta Sans',sans-serif" }}><Icon n="external" size={13} color="#2ea043" />Export Excel</button>
          </div>
        </div>

        {mode === "dashboard" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 12, color: T.textSecondary, background: "rgba(46,160,67,.08)", border: "1px solid #2ea04333", borderRadius: 8, padding: "9px 13px" }}>
              📌 The dashboard reflects <b style={{ color: T.text }}>fully invoiced (completed) jobs only</b> — {dash.fiSummary.jobs} of {data.rows.length} jobs in range. Open WIP is excluded so margins reflect finalized numbers. Use the <b style={{ color: T.text }}>Detail table</b> for every job.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14 }}>
              {chartCard("Top Customers by Revenue", "Invoiced revenue · completed jobs", hbar(dash.topRev, c => Math.round(c.rev), TH.grad("#e0a836", T.accent), c => c.name), 300, drillCust)}
              {chartCard("Gain / (Loss) by Customer", "Profit on completed jobs", hbar(dash.gainByCust, c => ({ value: Math.round(c.gain), itemStyle: { borderRadius: [0, 5, 5, 0], color: c.gain < 0 ? T.error : "#2ea043" } }), null, c => c.name), 300, drillCust)}
              {chartCard("Jobs by Margin Band", "Completed jobs by gross margin", bandOption, 300, drillBand)}
              {chartCard("Lowest-Margin Jobs", "Watch list · thin or loss-making", hbar(dash.problem, r => ({ value: Math.round(Number(r.gain || 0)), itemStyle: { borderRadius: [0, 5, 5, 0], color: Number(r.gain) < 0 ? T.error : "#7bb662" } }), null, r => `${r.so} · ${(r.customer || "").slice(0, 14)}`), 300, drillJob)}
            </div>
            {chartCard(`Cost & Margin over the Period (${dash.trend.weekly ? "weekly" : "monthly"})`, `Completed jobs · actual cost vs gross margin % by ${data.type === "invoiced" ? "invoice" : "order"} date`, trendOption, 320, drillBucket)}
          </div>
        )}

        {mode === "detail" && (<>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {chip(`All (${data.rows.length})`, view === "all", () => setView("all"))}
          {chip(`Fully Invoiced · ready to close (${data.rows.filter(r => r.fully_invoiced).length})`, view === "fully", () => setView("fully"))}
          {chip(`Open WIP (${data.rows.filter(r => Number(r.remaining_to_invoice || 0) > 0.01).length})`, view === "wip", () => setView("wip"))}
        </div>

        <div style={{ overflow: "auto", border: `1px solid ${T.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, whiteSpace: "nowrap" }}>
            <thead><tr>
              {COLS.map(c => (
                <th key={c.k} onClick={() => setSort(p => ({ k: c.k, dir: p.k === c.k ? -p.dir : 1 }))}
                  style={{ position: "sticky", top: 0, background: T.bg, color: T.textSecondary, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em", padding: "9px 11px", textAlign: c.a, cursor: "pointer", borderBottom: `1px solid ${T.border}`, userSelect: "none" }}>
                  {c.l}{sort.k === c.k ? (sort.dir < 0 ? " ▾" : " ▴") : ""}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.so + i} style={{ background: r.labor_over && Number(r.remaining_to_invoice || 0) <= 0.01 ? "rgba(210,153,34,.05)" : "transparent" }}>
                  {COLS.map(c => {
                    const raw = r[c.k];
                    let color = T.text;
                    if (c.k === "gain") color = Number(raw) < 0 ? T.error : "#2ea043";
                    else if (c.k === "margin") color = raw == null ? T.textSecondary : (Number(raw) < 0.15 ? T.error : (Number(raw) < 0.3 ? "#d29922" : "#2ea043"));
                    else if (c.k === "remaining_to_invoice") color = Number(raw) > 0.01 ? "#d29922" : T.textSecondary;
                    else if (c.a === "right") color = T.text;
                    return <td key={c.k} style={{ padding: "8px 11px", textAlign: c.a, borderBottom: `1px solid ${T.border}55`, color, fontFamily: c.a === "right" ? "'JetBrains Mono',monospace" : "inherit", maxWidth: c.k === "descr" || c.k === "customer" ? 200 : "none", overflow: "hidden", textOverflow: "ellipsis" }}>{c.f ? c.f(raw) : (raw ?? "—")}</td>;
                  })}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={COLS.length} style={{ padding: 24, textAlign: "center", color: T.textSecondary }}>No jobs in this range / view.</td></tr>}
            </tbody>
          </table>
        </div>
        </>)}
        <div style={{ fontSize: 11, color: T.textSecondary, opacity: .7 }}>Data live from the Sage warehouse · generated {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : ""} · internal job 0000100 excluded</div>
      </>)}

      {drill && (() => {
        const dr = drill.rows || [];
        const sm = (k) => dr.reduce((a, r) => a + Number(r[k] || 0), 0);
        const drev = sm("revenue"), dcost = sm("total_cost"), dgain = sm("gain");
        const DC = [
          { k: "so", l: "SO", a: "left" }, { k: "customer", l: "Customer", a: "left" }, { k: "descr", l: "Description", a: "left" }, { k: "last_invoiced", l: "Invoiced", a: "left", f: dt },
          { k: "revenue", l: "Revenue", a: "right", f: money }, { k: "total_cost", l: "Total Cost", a: "right", f: money },
          { k: "gain", l: "Gain", a: "right", f: money }, { k: "margin", l: "Margin", a: "right", f: pct },
        ];
        return (
          <div onClick={() => setDrill(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 14, width: "min(1040px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 70px rgba(0,0,0,.5)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "16px 18px", borderBottom: `1px solid ${T.border}` }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{drill.title}</div>
                  {drill.sub && <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>{drill.sub}</div>}
                </div>
                <div onClick={() => setDrill(null)} style={{ cursor: "pointer", color: T.textSecondary, fontSize: 22, lineHeight: 1, padding: "0 4px" }}>×</div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "12px 18px", borderBottom: `1px solid ${T.border}55` }}>
                {tile("Jobs", num(dr.length, 0))}
                {tile("Revenue", money(drev))}
                {tile("Total Cost", money(dcost))}
                {tile("Gain/(Loss)", money(dgain), dgain < 0 ? T.error : "#2ea043")}
                {tile("Margin", pct(drev ? dgain / drev : null), (drev && dgain / drev < 0.15) ? T.error : "#2ea043")}
              </div>
              <div style={{ overflow: "auto", padding: "8px 18px 18px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, whiteSpace: "nowrap" }}>
                  <thead><tr>{DC.map(c => <th key={c.k} style={{ position: "sticky", top: 0, background: T.bg, color: T.textSecondary, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em", padding: "9px 11px", textAlign: c.a, borderBottom: `1px solid ${T.border}` }}>{c.l}</th>)}</tr></thead>
                  <tbody>
                    {[...dr].sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0)).map((r, i) => (
                      <tr key={r.so + i}>{DC.map(c => {
                        const raw = r[c.k]; let color = T.text;
                        if (c.k === "gain") color = Number(raw) < 0 ? T.error : "#2ea043";
                        else if (c.k === "margin") color = raw == null ? T.textSecondary : (Number(raw) < 0.15 ? T.error : (Number(raw) < 0.3 ? "#d29922" : "#2ea043"));
                        else if (c.k === "remaining_to_invoice") color = Number(raw) > 0.01 ? "#d29922" : T.textSecondary;
                        return <td key={c.k} style={{ padding: "8px 11px", textAlign: c.a, borderBottom: `1px solid ${T.border}55`, color, fontFamily: c.a === "right" ? "'JetBrains Mono',monospace" : "inherit", maxWidth: c.k === "descr" || c.k === "customer" ? 220 : "none", overflow: "hidden", textOverflow: "ellipsis" }}>{c.f ? c.f(raw) : (raw ?? "—")}</td>;
                      })}</tr>
                    ))}
                    {!dr.length && <tr><td colSpan={DC.length} style={{ padding: 24, textAlign: "center", color: T.textSecondary }}>No jobs.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function AnalyticsCharts({ data }) {
  const COLORS = ["#c47a10","#2ea043","#3b82f6","#a78bfa","#f59e0b","#ef4444","#06b6d4","#ec4899","#84cc16","#f97316","#8b5cf6","#14b8a6"];
  const safe = v => (v == null || isNaN(v)) ? 0 : Number(v);
  const fmt = v => { const n = safe(v); return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${n.toFixed(0)}`; };
  const money = v => `$${safe(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const cardStyle = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 20px 12px" };

  // ── Premium ECharts options (bundled, self-hosted) ──────────────────────────
  const TH = chartTheme();
  const fmt0 = v => { const n = safe(v); return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${Math.round(n)}`; };

  const monthlyRevOption = {
    animationDuration: 700, animationEasing: "cubicOut",
    grid: { top: 28, right: 18, bottom: 36, left: 64 },
    tooltip: { ...TH.tooltip, valueFormatter: v => money(v) },
    xAxis: { type: "category", data: data.monthlyRevenue.labels, axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      type: "bar", data: data.monthlyRevenue.revenue.map(safe), barWidth: "55%",
      itemStyle: { borderRadius: [6, 6, 0, 0], color: TH.grad("#e0a836", T.accent) },
      emphasis: { itemStyle: { color: TH.grad("#f2bb4d", "#d4870f") } },
      label: { show: true, position: "top", formatter: p => fmt0(p.value), color: T.textMuted, fontSize: 10, fontFamily: "'Plus Jakarta Sans',sans-serif" },
      animationDelay: i => i * 40,
    }],
  };

  const gpOption = {
    animationDuration: 800,
    grid: { top: 28, right: 18, bottom: 36, left: 48 },
    tooltip: { ...TH.tooltip, valueFormatter: v => v == null ? "—" : `${Number(v).toFixed(1)}%` },
    xAxis: { type: "category", data: data.monthlyRevenue.labels, boundaryGap: false, axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", min: 0, max: 100, axisLabel: { ...TH.axisLabel, formatter: "{value}%" }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      type: "line", smooth: true, connectNulls: true,
      data: data.monthlyRevenue.gp.map(v => v == null || isNaN(v) ? null : Number(v)),
      lineStyle: { color: "#a78bfa", width: 3 }, itemStyle: { color: "#a78bfa" },
      symbol: "circle", symbolSize: 7,
      areaStyle: { color: TH.grad("rgba(167,139,250,0.35)", "rgba(167,139,250,0.02)") },
      label: { show: true, position: "top", formatter: p => p.value == null ? "" : `${p.value}%`, color: T.textMuted, fontSize: 10 },
    }],
  };

  const topCustNames = [...data.topCustomers.names].reverse();
  const topCustRev = [...data.topCustomers.revenue].reverse();
  const topCustOption = {
    animationDuration: 700,
    grid: { top: 12, right: 64, bottom: 28, left: 132 },
    tooltip: { ...TH.tooltip, trigger: "item", valueFormatter: v => money(v) },
    xAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    yAxis: { type: "category", data: topCustNames, axisLabel: { ...TH.axisLabel, fontSize: 11 }, axisLine: TH.axisLine, axisTick: { show: false } },
    series: [{
      type: "bar", barWidth: "62%",
      data: topCustRev.map((v, i) => ({ value: safe(v), itemStyle: { color: COLORS[(topCustNames.length - 1 - i) % COLORS.length], borderRadius: [0, 5, 5, 0] } })),
      label: { show: true, position: "right", formatter: p => fmt0(p.value), color: T.textMuted, fontSize: 10 },
    }],
  };

  const salesOption = {
    animationDuration: 700,
    grid: { top: 28, right: 18, bottom: data.salesperson.names.length > 6 ? 56 : 38, left: 64 },
    tooltip: { ...TH.tooltip, valueFormatter: v => money(v) },
    xAxis: { type: "category", data: data.salesperson.names, axisLabel: { ...TH.axisLabel, interval: 0, rotate: data.salesperson.names.length > 6 ? 30 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      type: "bar", barWidth: "55%",
      data: data.salesperson.revenue.map((v, i) => ({ value: safe(v), itemStyle: { color: TH.grad(COLORS[i % COLORS.length], COLORS[i % COLORS.length] + "88"), borderRadius: [5, 5, 0, 0] } })),
      label: { show: true, position: "top", formatter: p => fmt0(p.value), color: T.textMuted, fontSize: 10 },
    }],
  };

  const yoyColors = [T.accent, "#2ea043", "#3b82f6"];
  const yoyOption = {
    animationDuration: 800,
    grid: { top: 32, right: 20, bottom: 34, left: 68 },
    tooltip: { ...TH.tooltip, valueFormatter: v => v == null ? "—" : money(v) },
    legend: { data: data.yoy.series.map(s => String(s.year)), top: 2, right: 4, textStyle: { color: T.textSecondary, fontSize: 11 }, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
    xAxis: { type: "category", data: data.yoy.months, boundaryGap: false, axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: data.yoy.series.map((s, i) => ({
      name: String(s.year), type: "line", smooth: true, connectNulls: true,
      data: s.values.map(v => v == null || isNaN(v) ? null : Number(v)),
      lineStyle: { color: yoyColors[i % yoyColors.length], width: 2.5 },
      itemStyle: { color: yoyColors[i % yoyColors.length] }, symbol: "circle", symbolSize: 5,
      ...(i === 0 ? { areaStyle: { color: TH.grad("rgba(196,122,16,0.18)", "rgba(196,122,16,0.01)") } } : {}),
    })),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Row 1: Monthly Revenue + GP% */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Monthly Revenue <span style={{ color: T.textMuted, fontWeight: 400 }}>— {new Date().getFullYear()}</span></p>
          <EChart option={monthlyRevOption} height={300} />
        </div>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Gross Profit % <span style={{ color: T.textMuted, fontWeight: 400 }}>— {new Date().getFullYear()}</span></p>
          <EChart option={gpOption} height={300} />
        </div>
      </div>

      {/* Row 2: Top 10 Customers + Salesperson */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Top 10 Customers <span style={{ color: T.textMuted, fontWeight: 400 }}>— YTD</span></p>
          <EChart option={topCustOption} height={320} />
        </div>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Revenue by Salesperson <span style={{ color: T.textMuted, fontWeight: 400 }}>— YTD</span></p>
          <EChart option={salesOption} height={320} />
        </div>
      </div>

      {/* Row 3: Year-over-Year */}
      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Year-over-Year Monthly Revenue</p>
        <EChart option={yoyOption} height={280} />
      </div>

      {/* Row 4: Commission Payout Table */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>
            Commission Payout — Paid Invoices <span style={{ color: T.textMuted, fontWeight: 400 }}>({data.commissions.length} records)</span>
          </p>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            Total comm: <strong style={{ color: T.accent }}>{money(data.commissions.reduce((s, r) => s + safe(r.commAmt), 0))}</strong>
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Invoice","Date","Customer","Salesperson","Sales Order","Invoice Amt","SO Value","% of SO","Comm %","Commission","Payment Date"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: h === "Invoice" || h === "Date" || h === "Customer" || h === "Salesperson" || h === "Sales Order" ? "left" : "right", fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.commissions.map((r, i) => {
                const pct = r.pctOfOrder;
                const pctColor = pct == null ? T.textMuted : pct >= 90 ? T.success : pct >= 50 ? T.warn : T.error;
                return (
                <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.invoiceNo}</td>
                  <td style={{ padding: "7px 12px", color: T.textMuted, whiteSpace: "nowrap" }}>{r.date}</td>
                  <td style={{ padding: "7px 12px", color: T.textPrimary, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.customer}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary }}>{r.salesperson}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.salesOrderNo}</td>
                  <td style={{ padding: "7px 12px", color: T.textPrimary, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{money(r.amount)}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.orderAmount > 0 ? money(r.orderAmount) : "—"}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, color: pctColor, whiteSpace: "nowrap" }}>{pct != null ? `${pct}%` : "—"}</td>
                  <td style={{ padding: "7px 12px", color: T.textMuted, textAlign: "right" }}>{safe(r.commRate)}%</td>
                  <td style={{ padding: "7px 12px", color: T.accent, textAlign: "right", fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{money(r.commAmt)}</td>
                  <td style={{ padding: "7px 12px", color: T.success, whiteSpace: "nowrap" }}>{r.paymentDate}</td>
                </tr>
                );
              })}
              {data.commissions.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: "center", color: T.textMuted }}>No paid commission records found for this year</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Sales Order Detail component ────────────────────────────────────────────
function SalesOrderDetail({ data }) {
  const { header, summary, lineItems, invoices, invoiceLines, videos, documents, listDocs } = data;
  const [activeVideo, setActiveVideo] = useState(null);
  const fmt = v => `$${(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US') : '—';
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 20px" };
  const th = { padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "7px 12px", fontSize: 12, borderBottom: `1px solid ${T.border}` };

  // ── Charts (ECharts, bundled — no CDN) ─────────────────────────────────────
  const TH = chartTheme();
  const numv = v => Number(v) || 0;
  const fmtK = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${Math.round(v)}`;

  const qtyLines = lineItems.filter(l => numv(l.qtyOrdered) > 0).slice(0, 15);
  const qtyOption = {
    animationDuration: 600,
    legend: { data: ["Ordered", "Shipped", "Backorder"], top: 0, textStyle: { color: T.textSecondary, fontSize: 11 }, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
    grid: { top: 32, right: 12, bottom: 72, left: 48 },
    tooltip: { ...TH.tooltip },
    xAxis: { type: "category", data: qtyLines.map(l => (l.itemCode || "").slice(0, 14)), axisLabel: { ...TH.axisLabel, interval: 0, rotate: 35 }, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: TH.axisLabel, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [
      { name: "Ordered", type: "bar", data: qtyLines.map(l => numv(l.qtyOrdered)), itemStyle: { color: "rgba(196,122,16,0.85)", borderRadius: [3, 3, 0, 0] } },
      { name: "Shipped", type: "bar", data: qtyLines.map(l => numv(l.qtyShipped)), itemStyle: { color: "rgba(46,160,67,0.9)", borderRadius: [3, 3, 0, 0] } },
      { name: "Backorder", type: "bar", data: qtyLines.map(l => numv(l.qtyBackordered)), itemStyle: { color: "rgba(248,81,73,0.85)", borderRadius: [3, 3, 0, 0] } },
    ],
  };

  const invVals = invoices.map(i => numv(i.value));
  const orderVal = numv(summary.totalOrdered);
  const invYMax = Math.max(...invVals, orderVal, 0) * 1.2 || 1000;
  const invOption = {
    animationDuration: 600,
    grid: { top: 24, right: 16, bottom: 40, left: 64 },
    tooltip: { ...TH.tooltip, valueFormatter: v => `$${numv(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}` },
    xAxis: { type: "category", data: invoices.map(i => i.invoiceNo), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", max: invYMax, axisLabel: { ...TH.axisLabel, formatter: fmtK }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      name: "Invoice Value", type: "bar", barWidth: "55%", data: invVals,
      itemStyle: { color: TH.grad("rgba(46,160,67,0.95)", "rgba(46,160,67,0.5)"), borderRadius: [5, 5, 0, 0] },
      label: { show: true, position: "top", formatter: p => p.value > 0 ? `$${(p.value / 1000).toFixed(1)}K` : "", color: T.textMuted, fontSize: 10 },
      markLine: {
        symbol: "none", silent: true,
        lineStyle: { color: T.accent, type: "dashed", width: 1.5 },
        label: { formatter: "Order value", position: "insideEndTop", color: T.accent, fontSize: 10 },
        data: [{ yAxis: orderVal }],
      },
    }],
  };

  const statusColor = header.orderStatus === 'O' ? T.success : header.orderStatus === 'C' ? T.textMuted : T.warn;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Header summary */}
      <div style={{ ...card, borderLeft: `4px solid ${T.accent}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: T.accent }}>SO {header.salesOrderNo}</h2>
              <span style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}44`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                {header.orderStatus === 'O' ? 'Open' : header.orderStatus === 'C' ? 'Completed' : header.orderStatus || 'Unknown'}
              </span>
              {header.orderType && header.orderType !== 'S' && (
                <span style={{ background: "rgba(168,85,247,.15)", color: "#a855f7", border: "1px solid rgba(168,85,247,.3)", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                  {header.orderType === 'Q' ? 'Quote' : header.orderType === 'M' ? 'Master Order' : header.orderType === 'B' ? 'Back Order' : header.orderType === 'R' ? 'Repeating' : header.orderType}
                </span>
              )}
              {data.fromHistory && <span style={{ background: T.accentGlow2, color: T.accent, border: `1px solid ${T.accent}44`, borderRadius: 20, padding: "2px 10px", fontSize: 10 }}>History</span>}
              {data.fromInvoicesOnly && <span style={{ background: "rgba(59,130,246,.15)", color: "#3b82f6", border: "1px solid rgba(59,130,246,.3)", borderRadius: 20, padding: "2px 10px", fontSize: 10 }}>From AR Invoices</span>}
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 4 }}>{header.billToName || header.customerName}</p>
            {header.projectName && <p style={{ fontSize: 13, color: T.textSecondary }}>Project: {header.projectName}</p>}
            {header.customerPONo && <p style={{ fontSize: 12, color: T.textMuted }}>Customer PO: {header.customerPONo}</p>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", textAlign: "right" }}>
            {[
              ["Order Date", fmtDate(header.orderDate)],
              ["Salesperson", header.salespersonNo || "—"],
              ["Order Value", fmt(header.orderValue)],
              ["Invoiced", fmt(summary.totalInvoiced)],
              ["GP%", `${summary.gpPct}%`],
              ["Fill Rate", `${summary.fillRate}%`],
            ].map(([label, value]) => (
              <div key={label}>
                <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, fontFamily: label.includes('Date') || label.includes('person') ? undefined : "'JetBrains Mono',monospace" }}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Line Items", value: summary.lineCount, color: T.accent },
          { label: "Invoices", value: summary.invoiceCount, color: T.success },
          { label: "Ordered Qty", value: summary.totalOrderedQty, color: T.warn },
          { label: "Shipped Qty", value: summary.totalShippedQty, color: "#3b82f6" },
        ].map(k => (
          <div key={k.label} style={{ ...card, textAlign: "center" }}>
            <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{k.label}</p>
            <p style={{ fontSize: 26, fontWeight: 700, color: k.color, fontFamily: "'JetBrains Mono',monospace" }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      {lineItems.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Qty: Ordered vs Shipped vs Backorder</p>
            <EChart option={qtyOption} height={280} />
          </div>
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Invoices vs Order Value</p>
            {invoices.length > 0
              ? <EChart option={invOption} height={280} />
              : <div style={{ height: 280, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textMuted, fontSize: 13, gap: 6 }}>
                  <span>{header.orderType === 'Q' ? 'Quote — not invoiced' : 'No invoices yet'}</span>
                  {header.orderType === 'Q' && <span style={{ fontSize: 11 }}>Must be converted to a Sales Order before invoicing</span>}
                </div>}
          </div>
        </div>
      )}

      {/* Line Items table */}
      {lineItems.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>Line Items ({lineItems.length})</p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                {["Item Code","Description","Qty Ordered","Qty Shipped","Backorder","Unit Price","Amount","Promise Date","Comment"].map(h => (
                  <th key={h} style={{ ...th, textAlign: h === "Description" || h === "Item Code" || h === "Comment" ? "left" : "right" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{lineItems.map((l, i) => (
                <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,.01)" : "transparent" }}>
                  <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", color: T.accent }}>{l.itemCode}</td>
                  <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.description}</td>
                  <td style={{ ...td, textAlign: "right" }}>{l.qtyOrdered}</td>
                  <td style={{ ...td, textAlign: "right", color: l.qtyShipped > 0 ? T.success : T.textMuted }}>{l.qtyShipped}</td>
                  <td style={{ ...td, textAlign: "right", color: l.qtyBackordered > 0 ? T.error : T.textMuted }}>{l.qtyBackordered || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(l.unitPrice)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{fmt(l.extAmount)}</td>
                  <td style={{ ...td, textAlign: "right", color: T.textMuted, whiteSpace: "nowrap" }}>{fmtDate(l.promiseDate)}</td>
                  <td style={{ ...td, color: T.textMuted, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.comment || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices table */}
      {invoices.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>Invoices ({invoices.length}) — Total {fmt(summary.totalInvoiced)}</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr>
              {["Invoice #","Date","Due Date","Value","Cost","Freight","Commission","Salesperson"].map(h => (
                <th key={h} style={{ ...th, textAlign: h==="Invoice #" || h==="Salesperson" ? "left" : "right" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{invoices.map((inv, i) => (
              <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,.01)" : "transparent" }}>
                <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", color: T.success }}>{inv.invoiceNo}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: T.textMuted }}>{fmtDate(inv.invoiceDate)}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: T.textMuted }}>{fmtDate(inv.dueDate)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{fmt(inv.value)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", color: T.textMuted }}>{fmt(inv.cost)}</td>
                <td style={{ ...td, textAlign: "right", color: T.textMuted }}>{fmt(inv.freight)}</td>
                <td style={{ ...td, textAlign: "right", color: T.accent }}>{fmt(inv.commission)}</td>
                <td style={{ ...td, color: T.textSecondary }}>{inv.salesperson || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* YouTube Videos — inline player */}
      {videos.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>
            <Icon n="external" size={14} color="#ef4444" /> YouTube Videos ({videos.length})
          </p>
          {/* Inline player */}
          {activeVideo && (
            <div style={{ marginBottom: 16, borderRadius: 10, overflow: "hidden", background: "#000", position: "relative" }}>
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
                style={{ width: "100%", height: 420, border: "none", display: "block" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title="YouTube Video" />
              <button onClick={() => setActiveVideo(null)}
                style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,.7)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "4px 10px", fontSize: 12 }}>✕ Close</button>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {videos.map((v, i) => (
              <div key={i} onClick={() => setActiveVideo(activeVideo === v.id ? null : v.id)}
                style={{ background: T.bg, border: `2px solid ${activeVideo === v.id ? T.accent : T.border}`, borderRadius: 10, overflow: "hidden", cursor: "pointer", transition: "border-color .2s, transform .15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = activeVideo === v.id ? T.accent : T.border; e.currentTarget.style.transform = "none"; }}>
                <div style={{ position: "relative" }}>
                  {v.thumbnail && <img src={v.thumbnail} alt={v.title} style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", display: "block" }} />}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.3)", opacity: 0, transition: "opacity .2s" }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "0"}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width={18} height={18} viewBox="0 0 24 24" fill="#fff"><polygon points="5,3 19,12 5,21"/></svg>
                    </div>
                  </div>
                </div>
                <div style={{ padding: "8px 10px" }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title}</p>
                  <p style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                    {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString('en-US') : ''}
                    {v.viewCount ? ` · ${parseInt(v.viewCount).toLocaleString()} views` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents — manufacturing + SharePoint list */}
      {(documents.length > 0 || (listDocs && listDocs.length > 0)) && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>
            <Icon n="db" size={14} /> Documents & Files
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...documents, ...(listDocs||[])].map((doc, i) => {
              const Tag = doc.url ? "a" : "div";
              const linkProps = doc.url ? { href: doc.url, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Tag key={i} {...linkProps}
                  style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", textDecoration: "none", display: "flex", alignItems: "flex-start", gap: 10, transition: "border-color .2s", cursor: doc.url ? "pointer" : "default", opacity: doc.url ? 1 : 0.85 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = doc.url ? T.accent : T.border}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>
                    {(doc.name||'').endsWith('.pdf') ? '📄' : (doc.name||'').match(/\.(dwg|dxf|stp|step|ipt|iam|idw)$/i) ? '📐' : (doc.name||'').match(/\.(xlsx|csv)$/i) ? '📊' : '📁'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, color: T.textPrimary, fontWeight: 500, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</p>
                    {doc.source && <span style={{ fontSize: 10, color: T.textMuted, background: T.border, borderRadius: 4, padding: "1px 6px" }}>{doc.source}</span>}
                    {doc.preview && <p style={{ fontSize: 10, color: T.textMuted, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.preview.slice(0, 80)}</p>}
                  </div>
                  {doc.url && <Icon n="external" size={13} color={T.textMuted} />}
                </Tag>
              );
            })}
          </div>
        </div>
      )}

      {videos.length === 0 && documents.length === 0 && (!listDocs || listDocs.length === 0) && (
        <div style={{ ...card, textAlign: "center", color: T.textMuted, padding: 24 }}>
          <Icon n="external" size={20} color={T.border} />
          <p style={{ fontSize: 12, marginTop: 10 }}>No related videos or documents found for this order</p>
        </div>
      )}
    </div>
  );
}

// ─── Alternative Reports config ───────────────────────────────────────────────
// tenantMatch: substring of the tenant name as stored in the DB (case-insensitive)
// status: "published" = visible to the tenant's users.
//         "draft"     = only the PVBI developer (see isDeveloper) can see/open it,
//                       shown with a DRAFT badge. Build & preview a report against
//                       real production data, then flip to "published" and redeploy
//                       to release it to the client. Default (missing) = published.
// department: optional. If a tenant has ANY departmented report, the Reports pane
//             groups by DEPARTMENTS (in this order); empty departments show as
//             "coming soon". Tenants with no departmented reports render flat.
const DEPARTMENTS = ["Executive", "Sales", "Finance", "Operations", "Planning", "Procurement", "Customer Service", "Shop Floor"];
const ALT_REPORTS = [
  { id: "rdp-financials-q1-2026", name: "RDP Financials Q1 2026", description: "Raindrop Products financial summary — Q1 2026", type: "html", file: "/reports/RDP_Financials_Q1_2026.html", tenant: "RDP", tenantMatch: "rain-drop", status: "published" },
  { id: "analytics-dashboard", name: "Analytics Dashboard", description: "Live charts — monthly revenue, GP%, top customers, salesperson & YoY, plus commission payout report", type: "analytics", tenant: "RDP", tenantMatch: "rain-drop", status: "published" },
  { id: "eew-executive-sales", department: "Executive", name: "Executive Sales Report", description: "East End Welding — monthly sales, YoY trends, top customers, open orders & salesperson breakdown", type: "html", file: "/reports/EEW_Executive_Sales_Report.html", tenant: "EEW", tenantMatch: "east end", status: "published" },
  { id: "eew-raw-materials", department: "Planning", name: "Raw Materials Report", description: "East End Welding — raw material inventory: stock on hand, committed to orders & on PO, shortages, product-line breakdown & open purchase orders", type: "html", file: "/reports/EEW_Raw_Materials_Report.html", tenant: "EEW", tenantMatch: "east end", status: "published" },
  { id: "eew-mrp", department: "Planning", name: "MRP / Resource Planning", description: "East End Welding — material requirements planning: net position (On Hand + On PO − On SO), shortage action list, buy plan by material family & full item browser", type: "html", file: "/reports/EEW_MRP_Report.html", tenant: "EEW", tenantMatch: "east end", status: "published" },
  { id: "eew-scheduler", department: "Planning", name: "Workshop Scheduler", description: "East End Welding — shop-floor capacity & job planning: work-center load board, demand-vs-capacity timeline, work-center queues, and job/delivery tracking from the Job Cost module", type: "html", file: "/reports/EEW_Workshop_Scheduler.html", tenant: "EEW", tenantMatch: "east end", status: "published" },
  { id: "eew-jobcost", department: "Finance", name: "Job Cost Review", description: "East End Welding — on-demand job cost & WIP review: pick a date basis (invoiced or order date) and range, then Run — Revenue vs cost, margin, budget-vs-actual hours, ready-to-close & WIP, with Excel export", type: "jobcost", tenant: "EEW", tenantMatch: "east end", status: "published" },
  { id: "bio-ecommerce-report", name: "eCommerce & Shipping Report", description: "Biolongevity Labs — order volume, revenue trends, carrier performance, geographic distribution & payment mix", type: "html", file: "/reports/BIO_Ecommerce_Report.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
  { id: "bio-inventory-report", name: "Inventory & Velocity Report", description: "Live inventory levels, 30-day sell-through velocity, days/months of stock available, and open purchase orders by product category", type: "html", file: "/reports/BIO_Inventory_Report.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
  { id: "bio-ghost-orders", name: "Ghost Orders Report", description: "ShipHero fulfilled orders with no tracking number or zero quantity shipped — identify fulfilment gaps before they become customer issues", type: "html", file: "/reports/BIO_Ghost_Orders.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
  { id: "bio-refunds-report", name: "Refunds & Chargebacks Report", description: "Refund value & count broken down by ShipHero reason tag and category (Shipping, Product, Customer Service…), with prior-period deltas, trend and detail — BioLongevity Labs", type: "html", file: "/reports/BIO_Refunds_Report.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
  // Sales demo. Lives on PVBI's own tenant so no client user can ever see it.
  // Synthetic data for a fictional fabricator — safe to screen-share to prospects.
  { id: "pvbi-sage100-demo", name: "Sage 100 MRP — Prospect Demo", description: "DEMO — Sage 100 material requirements planning for a fictional fabricator (Northgate Fabrication LLC). Synthetic data derived from a real Sage 100 shape; contains no client information. Use for prospect walkthroughs.", type: "html", file: "/reports/Northgate_MRP_Demo.html", tenant: "PVBI", tenantMatch: "power vision", status: "published" },
];

// Per-brand report selectors (BioLongevity). Each brand tab shows these as cards, so more
// brand-specific reports can be added later without touching the sidebar/render logic.
const BRAND_REPORTS = {
  nova: [
    { id: "nova-inventory", name: "Inventory & Velocity Report", description: "Live inventory levels, 30-day sell-through velocity, days/months of stock available, and open purchase orders by product category — Nova only", file: "/reports/BIO_Inventory_Report.html?brand=nova", status: "published" },
  ],
  lyv: [
    { id: "lyv-inventory", name: "Inventory & Velocity Report", description: "Live inventory levels, 30-day sell-through velocity, days/months of stock available, and open purchase orders by product category — LYV only", file: "/reports/BIO_Inventory_Report.html?brand=lyv", status: "published" },
  ],
};

// The PVBI operator who builds reports. Sees "draft" reports (even while using
// "View As" a client tenant); real client users never do. Matches the same
// identity used to gate the Admin panel.
const isDeveloper = (email) => (email || "").toLowerCase().includes("christiaan.horn");

function Dashboard({ user, token, onLogout }) {
  // Land on the branded home screen, not straight into Power BI.
  const [nav, setNav] = useState("home");
  const [altReport, setAltReport] = useState(null);
  const [deptTab, setDeptTab] = useState("All");   // active department tab on the Reports pane
  // Stable iframe src per opened report — computing the ?t= cache-buster once (not on every
  // render) so re-renders (e.g. toggling the sidebar) don't reload the report to its default tab.
  const altReportSrc = useMemo(() => (altReport?.file ? `${altReport.file}?t=${Date.now()}` : ""), [altReport?.file]);
  // Selected report inside a per-brand tab (Nova / LYV). Its file may already carry a query
  // string (?brand=…), so the cache-buster joins with & or ? accordingly.
  const [brandReport, setBrandReport] = useState(null);
  const brandReportSrc = useMemo(() => {
    if (!brandReport?.file) return "";
    const sep = brandReport.file.includes("?") ? "&" : "?";
    return `${brandReport.file}${sep}t=${Date.now()}`;
  }, [brandReport?.id]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [soQuery, setSoQuery] = useState("");
  const [soData, setSoData] = useState(null);
  const [soLoading, setSoLoading] = useState(false);
  const [soErr, setSoErr] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsErr, setAnalyticsErr] = useState("");
  const [reports, setReports] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [tenantLogo, setTenantLogo] = useState("");
  // Instant home-screen figures. Everything here comes from our own Postgres, so
  // it renders immediately — deliberately no AI/warehouse queries on this page.
  const [homeSummary, setHomeSummary] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsErr, setReportsErr] = useState("");
  const [activeReport, setActiveReport] = useState(null);
  const [embedData, setEmbedData] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [embedErr, setEmbedErr] = useState("");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
const [messages, setMessages] = useState([]);
const [chatHistory, setChatHistory] = useState([]);
const [savedQueries, setSavedQueries] = useState([]);
const [showHistory, setShowHistory] = useState(false);
const [alerts, setAlerts] = useState([]);
const [schedules, setSchedules] = useState([]);
const [auditLogs, setAuditLogs] = useState([]);
const [reportComments, setReportComments] = useState([]);
const [newComment, setNewComment] = useState("");
const [showAlertModal, setShowAlertModal] = useState(false);
const [showScheduleModal, setShowScheduleModal] = useState(false);
const [newAlert, setNewAlert] = useState({ name:"", question:"", condition:"gt", threshold:"", notifyEmail:"", frequency:"daily" });
const [newSchedule, setNewSchedule] = useState({ reportName:"", question:"", recipients:"", frequency:"weekly", dayOfWeek:1, hour:8 });
const [kpiCards, setKpiCards] = useState([]);
const [kpiLoading, setKpiLoading] = useState(false);
const [defaultKpiCards, setDefaultKpiCards] = useState([]);
const [defaultKpiLoading, setDefaultKpiLoading] = useState(false);
const [viewAs, setViewAs] = useState(null);
// Share a BIO inventory report: mint an expiring token link and copy it to the clipboard.
// (Declared after viewAs so its dependency array can reference it.)
const [shareMsg, setShareMsg] = useState("");
const [shareBusy, setShareBusy] = useState(false);
const mintShare = useCallback(async (brand, report) => {
  setShareBusy(true); setShareMsg("Creating link…");
  try {
    const res = await fetch(`${API_BASE}/bio/share`, {
      method: "POST",
      headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
      body: JSON.stringify({ brand, days: 30, report: report || "inventory" }),
    });
    if (!res.ok) throw new Error(String(res.status));
    const d = await res.json();
    try { await navigator.clipboard.writeText(d.url); } catch { /* clipboard blocked */ }
    const exp = new Date(d.expiresAt).toLocaleDateString();
    setShareMsg(`✓ Link copied — expires ${exp}`);
  } catch (e) {
    setShareMsg("Couldn't create link");
  } finally {
    setShareBusy(false);
    setTimeout(() => setShareMsg(""), 7000);
  }
}, [token, viewAs]);
const [exportingFormat, setExportingFormat] = useState(null);
const [showShareModal, setShowShareModal] = useState(false);
const [shareEmail, setShareEmail] = useState("");
const [shareNote, setShareNote] = useState("");
const [shareSending, setShareSending] = useState(false);
const pbiReportRef = useRef(null);
const [expandedTenant, setExpandedTenant] = useState(null);
const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("pvbi_sidebar_collapsed") === "1");
const toggleSidebar = () => setSidebarCollapsed(v => {
  const n = !v; localStorage.setItem("pvbi_sidebar_collapsed", n ? "1" : "0");
  // After the sidebar width animation, nudge embedded reports (iframes) to re-fit their
  // charts — the browser doesn't reliably fire a resize inside the iframe on an ancestor
  // layout change, so ECharts/Plotly reports would otherwise go blank until a re-render.
  [220, 400].forEach(ms => setTimeout(() => {
    document.querySelectorAll("iframe").forEach(f => { try { f.contentWindow?.dispatchEvent(new Event("resize")); } catch (e) {} });
  }, ms));
  return n;
});
const [showAddUser, setShowAddUser] = useState(false);
const [addUserTenantId, setAddUserTenantId] = useState("");
const [addUserTenantName, setAddUserTenantName] = useState("");
const [newUser, setNewUser] = useState({ name: "", email: "", role: "VIEWER" });
const [inviteLoading, setInviteLoading] = useState(false);
const [adminData, setAdminData] = useState(null);
const [showAddTenant, setShowAddTenant] = useState(false);
const inviteUser = async () => {
  if (!newUser.name || !newUser.email) return;
  setInviteLoading(true);
  try {
    const res = await fetch(`${API_BASE}/admin/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId: addUserTenantId, ...newUser }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setShowAddUser(false);
    setNewUser({ name: "", email: "", role: "VIEWER" });
    loadAdminData();
    alert(`✅ Invitation sent to ${newUser.email}!`);
  } catch (err) {
    alert(`❌ Error: ${err.message}`);
  } finally {
    setInviteLoading(false);
  }
};
const [newTenant, setNewTenant] = useState({
  name: "",
  slug: "",
  azureTenantId: "",
  pbiWorkspaceId: "",
  dbType: "client",
  dbConnectionString: "",
  dbSchema: "public"
});
const [input, setInput] = useState("");
const [aiLoading, setAiLoading] = useState(false);
const messagesEndRef = useRef(null);
useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, aiLoading]);

// Deep-link routing: portal.powervisionbi.com/#report/{id}
useEffect(() => {
  const hash = window.location.hash;
  if (!hash.startsWith('#report/')) return;
  const reportId = decodeURIComponent(hash.replace('#report/', ''));
  if (!reportId || !reports.length) return;
  const found = reports.find(r => r.pbiReportId === reportId || r.id === reportId);
  if (found) { setNav("reports"); openReport(found); window.location.hash = ''; }
}, [reports]);
const loadAdminData = async () => {
  try {
    const doFetch = (tk) => fetch(`${API_BASE}/admin/tenants`, { headers: { Authorization: `Bearer ${tk}` } });
    let res = await doFetch(token || sessionStorage.getItem("pvbi_token"));
    // Token expired mid-session → silently refresh and retry once (fixes "Retry does nothing").
    if (res.status === 401) {
      const fresh = await refreshStoredToken();
      if (fresh) res = await doFetch(fresh);
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error("Admin data error:", res.status, err);
      setAdminData({ _error: err.error || `HTTP ${res.status}` });
      return;
    }
    const data = await res.json();
    setAdminData(data);
  } catch (err) {
    console.error("Admin data error:", err);
    setAdminData({ _error: err.message || "Network error" });
  }
};

// ── Load helpers for new features ────────────────────────────────────────────
const loadChatHistory = async () => {
  try { const d = await (await fetch(`${API_BASE}/chat-history`, { headers: authHeaders(token, viewAs?.id) })).json(); setChatHistory(d.messages || []); } catch {}
};
const loadSavedQueries = async () => {
  try { const d = await (await fetch(`${API_BASE}/saved-queries`, { headers: authHeaders(token, viewAs?.id) })).json(); setSavedQueries(d.queries || []); } catch {}
};
const loadAlerts = async () => {
  try { const d = await (await fetch(`${API_BASE}/alerts`, { headers: authHeaders(token, viewAs?.id) })).json(); setAlerts(d.alerts || []); } catch {}
};
const loadSchedules = async () => {
  try { const d = await (await fetch(`${API_BASE}/scheduled-reports`, { headers: authHeaders(token, viewAs?.id) })).json(); setSchedules(d.schedules || []); } catch {}
};
const loadAuditLogs = async () => {
  try { const d = await (await fetch(`${API_BASE}/audit-log`, { headers: authHeaders(token) })).json(); setAuditLogs(d.logs || []); } catch {}
};
const loadComments = async (pbiReportId) => {
  try { const d = await (await fetch(`${API_BASE}/comments?reportId=${encodeURIComponent(pbiReportId)}`, { headers: authHeaders(token, viewAs?.id) })).json(); setReportComments(d.comments || []); } catch {}
};
// Home figures: projects + the requests waiting on this user. Cheap Postgres reads
// against our own DB, and a failure just leaves the tiles blank rather than
// blocking the page.
const loadHomeSummary = async () => {
  try {
    const [proj, reqs] = await Promise.all([
      projApi("/").catch(() => null),
      projApi("/requests?status=open").catch(() => null),
    ]);
    setHomeSummary({
      projects: proj?.projects || [],
      summary: proj?.summary || null,
      isAgency: !!proj?.isAgency,
      requests: reqs?.requests || [],
    });
  } catch { setHomeSummary({ projects: [], summary: null, isAgency: false, requests: [] }); }
};

const logAction = async (action, detail) => {
  try { await fetch(`${API_BASE}/audit-log`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ action, detail }) }); } catch {}
};
const saveMessage = async (role, content, sql, rowCount) => {
  try { await fetch(`${API_BASE}/chat-history`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ role, content, sql, rowCount }) }); } catch {}
};
const DEFAULT_KPIS = [
  { id: 'ytd-rev',    name: 'YTD Revenue',       question: 'What is the total Year-to-Date revenue or net sales amount?',                          color: '#c47a10', icon: 'chart' },
  { id: 'mtd-rev',    name: 'MTD Revenue',       question: 'What is the total Month-to-Date revenue or net sales amount for the current month?',   color: '#c47a10', icon: 'chart' },
  { id: 'ytd-bud',    name: 'YTD Budget',        question: 'What is the Year-to-Date budget or target sales amount?',                              color: '#2ea043', icon: 'zap'   },
  { id: 'mtd-bud',    name: 'MTD Budget',        question: 'What is the Month-to-Date budget or target sales amount for the current month?',       color: '#2ea043', icon: 'zap'   },
  { id: 'ytd-gp',     name: 'YTD GP%',           question: 'What is the Year-to-Date gross profit percentage?',                                    color: '#a78bfa', icon: 'db'    },
  { id: 'mtd-gp',     name: 'MTD GP%',           question: 'What is the Month-to-Date gross profit percentage for the current month?',             color: '#a78bfa', icon: 'db'    },
  { id: 'open-ord',   name: 'Open Orders',       question: 'What is the total COUNT (number) of open confirmed sales orders? Return only the count, not a dollar amount.',  color: '#f59e0b', icon: 'grid'  },
  { id: 'open-ord-val', name: 'Open Order Balance', question: 'What is the total value or dollar amount of all open sales orders?',               color: '#38bdf8', icon: 'send'  },
  { id: 'open-inv',   name: 'Open Invoices',     question: 'What is the total outstanding balance or dollar amount owed on all open unpaid customer invoices?', color: '#f87171', icon: 'alert' },
];

const loadDefaultKPIs = async (noCache = false) => {
  setDefaultKpiLoading(true);
  setDefaultKpiCards([]);
  const results = [];
  for (const kpi of DEFAULT_KPIS) {
    try {
      const res = await fetch(`${API_BASE}/ai-query`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ question: kpi.question, noCache }) });
      const d = await res.json();
      results.push({ ...kpi, answer: d.answer });
    } catch {
      results.push({ ...kpi, answer: null });
    }
    setDefaultKpiCards([...results]);
  }
  setDefaultKpiLoading(false);
};

const loadHomeKPIs = async () => {
  if (!savedQueries.length) return;
  setKpiLoading(true);
  const cards = [];
  for (const q of savedQueries.slice(0, 4)) {
    try {
      const res = await fetch(`${API_BASE}/ai-query`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ question: q.question }) });
      const d = await res.json();
      cards.push({ name: q.name, question: q.question, answer: d.answer, rowCount: d.rowCount });
    } catch {}
  }
  setKpiCards(cards);
  setKpiLoading(false);
};

const addTenant = async () => {
  try {
    await fetch(`${API_BASE}/admin/tenants`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify(newTenant)
    });
    setShowAddTenant(false);
    setNewTenant({ name: "", slug: "", azureTenantId: "", pbiWorkspaceId: "", dbType: "client", dbConnectionString: "", dbSchema: "public" });
    loadAdminData();
  } catch (err) {
    console.error("Add tenant error:", err);
  }
};
const sendMessage = async (override) => {
  const question = (override ?? input).trim();
  if (!question || aiLoading) return;
  if (!override) setInput("");
  const userMsg = { id: Date.now(), role: "user", content: question };
  const history = [...messages, userMsg];
  setMessages(history);
  saveMessage("user", question);
  sendAiRequest(question, history);
};

const sendAiRequest = async (question, history) => {
  setAiLoading(true);
  try {
    const res = await fetch(`${API_BASE}/ai-query`, {
      method: "POST",
      headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
      body: JSON.stringify({ question, history: history.map(m => ({ role: m.role, content: m.content })) }),
    });
    const data = await res.json();
    const assistantMsg = { id: Date.now(), role: "assistant", content: data.answer || "Sorry, I could not answer that.", chart: data.chart || null, exportData: data.exportData || null, question };
    setMessages(prev => [...prev, assistantMsg]);
    saveMessage("assistant", assistantMsg.content, data.sql, data.rowCount);
    logAction("ai_query", question);
  } catch {
    setMessages(prev => [...prev, { id: Date.now(), role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
  } finally {
    setAiLoading(false);
  }
};
 useEffect(() => {
  let cancelled = false;
  async function load() {
    setLoadingReports(true); setReportsErr("");
    try {
      const data = await fetchReports(token, viewAs?.id);
      if (!cancelled) {
        setReports(data.reports || []);
        setTenantName(data.tenant?.name || "");
        setTenantLogo(data.tenant?.logoUrl || "");
      }
    } catch (e) {
      if (!cancelled) setReportsErr("Failed to load reports. Please refresh or contact support.");
    } finally {
      if (!cancelled) setLoadingReports(false);
    }
    // Load admin data if admin user
    if (user.email?.toLowerCase().includes("christiaan.horn")) {
      loadAdminData();
    }
  }
  load();
  return () => { cancelled = true; };
}, [token, viewAs]);

// Load feature data on mount
useEffect(() => {
  if (!token) return;
  loadSavedQueries();
  loadChatHistory();
  loadAlerts();
  loadSchedules();
}, [token, viewAs]);

// Mirror the impersonation target into sessionStorage so the module-level
// crmApi() helper can scope CRM calls to the tenant being viewed.
useEffect(() => {
  if (viewAs?.id) sessionStorage.setItem("pvbi_impersonate", viewAs.id);
  else sessionStorage.removeItem("pvbi_impersonate");
}, [viewAs]);

// Load comments when report opens
useEffect(() => {
  if (activeReport) { loadComments(activeReport.pbiReportId || activeReport.id); logAction('view_report', activeReport.name); }
}, [activeReport]);

// Home used to fire DEFAULT_KPIS on open — eight SEQUENTIAL /api/ai-query calls
// (an LLM round-trip each) against the client's database. Slow, costly, and the
// generic questions often didn't map to a given schema, so cards sat on "—".
// The home screen no longer shows them, so nothing is loaded here. The saved-query
// KPIs still live on the AI Agent page.
useEffect(() => {
  if (nav === 'home') loadHomeSummary();
}, [nav]); // eslint-disable-line
useEffect(() => { if (nav === 'audit') loadAuditLogs(); }, [nav]);
useEffect(() => { if (nav === 'alerts') loadAlerts(); }, [nav]);
useEffect(() => { if (nav === 'schedule') loadSchedules(); }, [nav]);
useEffect(() => { if (nav === 'admin') loadAdminData(); }, [nav]);

  // Open a report — fetch embed token from backend
  const openReport = useCallback(async (report) => {
    setActiveReport(report);
    setEmbedData(null);
    setEmbedErr("");
    setLoadingEmbed(true);
    try {
      const data = await fetchEmbedToken(token, report.id, viewAs?.id);
      setEmbedData(data);
    } catch (e) {
      setEmbedErr("Could not load this report. Please try again.");
    } finally {
      setLoadingEmbed(false);
    }
  }, [token, viewAs]);

  const activeTenantName = (viewAs?.name || tenantName || "").toLowerCase();
  const canSeeDrafts = isDeveloper(user.email);
  const altReportsForTenant = ALT_REPORTS.filter(r =>
    activeTenantName.includes(r.tenantMatch) &&
    (r.status !== "draft" || canSeeDrafts)
  );

  const cats = ["All", ...Array.from(new Set(reports.map(r => r.cat || "Other")))];
  const filtered = reports.filter(r => {
    const ms = r.name.toLowerCase().includes(search.toLowerCase()) || (r.description || "").toLowerCase().includes(search.toLowerCase());
    return ms && (cat === "All" || r.cat === cat);
  });
  const initials = (user.name || "U").slice(0, 2).toUpperCase();

  return (
    <div style={{ display: "flex", minHeight: "100vh", position: "relative", zIndex: 1 }}>
      {/* Mobile overlay */}
      <div className={`sidebar-overlay${mobileSidebarOpen ? " open" : ""}`} onClick={() => setMobileSidebarOpen(false)} />
      {/* Fixed mobile hamburger — always visible on small screens */}
      {!mobileSidebarOpen && (
        <button className="hamburger-btn" onClick={() => setMobileSidebarOpen(true)}
          style={{ position: "fixed", bottom: 20, right: 20, zIndex: 300, width: 48, height: 48, borderRadius: "50%", background: T.accent, border: "none", color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,.4)" }}>
          <Icon n="menu" size={20} color="#fff" />
        </button>
      )}
      {/* Sidebar */}
      <div className={`mob-sidebar${mobileSidebarOpen ? " open" : ""}`} style={{ width: sidebarCollapsed ? 66 : 224, background: T.surface, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", padding: sidebarCollapsed ? "18px 8px" : "18px 12px", flexShrink: 0, transition: "width .2s ease" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: sidebarCollapsed ? "center" : "space-between", padding: sidebarCollapsed ? "4px 0 8px" : "4px 8px 8px 8px", marginBottom: 16 }}>
          {!sidebarCollapsed && (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
                Power<span style={{ color: T.accent }}>Vision</span> BI
              </div>
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>We Make Data Dance</p>
            </div>
          )}
          <button onClick={toggleSidebar} title={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
            style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 7, cursor: "pointer", color: T.textSecondary, display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, flexShrink: 0, fontSize: 15, lineHeight: 1 }}>
            {sidebarCollapsed ? "»" : "«"}
          </button>
        </div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${T.accent}60, transparent)`, marginBottom: 20, marginLeft: sidebarCollapsed ? 2 : 8, marginRight: sidebarCollapsed ? 2 : 8 }} />

        <nav style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          {[
{ id: "home", icon: "home", label: "Home" },
...(activeTenantName.includes("rain-drop") || isDeveloper(user.email) ? [{ id: "reports", icon: "grid", label: "Power BI" }] : []),
...(altReportsForTenant.length > 0 ? [{ id: "alt-reports", icon: "external", label: "Reports" }] : []),
...(activeTenantName.includes("biolongevity") ? [
  { id: "bio-nova", icon: "chart", label: "Nova" },
  { id: "bio-lyv", icon: "chart", label: "LYV" },
] : []),
...(activeTenantName.includes("rain-drop") ? [{ id: "order-lookup", icon: "db", label: "Order Lookup" }] : []),
{ id: "ai", icon: "zap", label: "AI Agent" },
{ id: "projects", icon: "layers", label: "Projects" },
{ id: "crm", icon: "users", label: "CRM" },
...(activeTenantName.includes("rain-drop") ? [{ id: "hub", icon: "check", label: "Hub" }] : []),
{ id: "alerts", icon: "alert", label: "Alerts" },
{ id: "schedule", icon: "refresh", label: "Scheduled" },
{ id: "audit", icon: "eye", label: "Audit Log" },
{ id: "settings", icon: "settings", label: "Settings" },
...(isDeveloper(user.email) ? [{ id: "admin", icon: "shield", label: "Admin" }] : []),
].map(item => (
            <button key={item.id} title={sidebarCollapsed ? item.label : undefined} className={`nav-item ${nav === item.id ? "active" : ""}`} style={sidebarCollapsed ? { justifyContent: "center", padding: "10px 0" } : undefined} onClick={() => { setNav(item.id); setActiveReport(null); setAltReport(null); setBrandReport(null); setMobileSidebarOpen(false); }}>
              <Icon n={item.icon} size={15} />
              {!sidebarCollapsed && item.label}
            </button>
          ))}
        </nav>

        {/* User */}
        <div style={{ display: "flex", flexDirection: sidebarCollapsed ? "column" : "row", alignItems: "center", gap: 9, padding: "10px 8px", borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
          <div title={sidebarCollapsed ? `${user.name}\n${user.email}` : undefined} style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
          {!sidebarCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</p>
              <p style={{ fontSize: 10, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</p>
            </div>
          )}
          <button onClick={onLogout} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex", padding: 4 }} title="Sign out">
            <Icon n="logout" size={14} />
          </button>
        </div>
      </div>

      {/* Impersonation banner */}
      {viewAs && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 999, background: `linear-gradient(90deg, ${T.accent}, ${T.accentDark})`, padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
            👁 Viewing as: <strong>{viewAs.name}</strong>
          </span>
          <button
            onClick={() => { setViewAs(null); setMessages([]); setActiveReport(null); setNav("reports"); }}
            style={{ background: "rgba(255,255,255,.2)", border: "none", borderRadius: 6, padding: "4px 14px", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            ✕ Exit client view
          </button>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", marginTop: viewAs ? 36 : 0 }}>

        {/* ── Home Dashboard ── */}
        {/* ── Home ──
             A branded landing rather than a metrics dump: the animated Power Vision
             mark alongside the signed-in client's own logo, two big quick-launch
             tiles (Reports / Projects), and a band of figures that come straight
             from our own Postgres so the page is instant. The old AI-generated KPI
             grid is gone — see the note on the nav === 'home' effect. */}
        {nav === "home" && (() => {
          const hs = homeSummary;
          const clientName = viewAs?.name || tenantName || "";
          const openProjects = (hs?.projects || []).filter(p => PRJ_OPEN_STATUS.includes(p.status));
          const avgProgress = openProjects.length
            ? Math.round(openProjects.reduce((s, p) => s + (p.progress || 0), 0) / openProjects.length) : 0;
          // "Waiting on you" means different things either side: PVBI triages what
          // clients asked; a client answers what PVBI asked of them.
          const waiting = (hs?.requests || []).filter(r => hs?.isAgency ? !r.fromAgency : r.fromAgency);
          const totalReports = reports.length + altReportsForTenant.length;
          const hour = new Date().getHours();
          // One shared surface for every block below. Cards that each carried their
          // own accent bar and colour made the page read as five competing things;
          // a single quiet panel lets the content be the thing you notice.
          // Softer than a card: a faint fill and a barely-there edge, so the page
          // reads as a few grouped sections rather than a stack of boxes.
          const panel = {
            background: "rgba(255,255,255,.015)",
            border: "1px solid rgba(255,255,255,.045)",
            borderRadius: 16,
          };

          return (
            <div style={{ flex: 1, overflow: "auto", padding: "28px 36px 40px" }} className="mob-pad">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <button className="hamburger-btn" onClick={() => setMobileSidebarOpen(true)}><Icon n="menu" size={18} /></button>
              </div>

              {/* ── Hero: the two marks, side by side ── */}
              <div className="fu" style={{
                position: "relative", overflow: "hidden", borderRadius: 20, marginBottom: 26,
                // No card here — the hero sits directly on the page so the marks
                // read as branding, not as another panel among the tiles below.
                background: "transparent", border: "none", padding: "56px 24px 46px", textAlign: "center",
              }}>
                <div className="orb-drift" style={{
                  position: "absolute", top: -90, right: -60, width: 320, height: 320, borderRadius: "50%",
                  background: `radial-gradient(circle, ${T.accentGlow} 0%, transparent 68%)`,
                  filter: "blur(46px)", pointerEvents: "none", animation: "driftGlow 11s ease-in-out infinite",
                }} />
                {/* No grid overlay here — the page already renders a fixed
                    .grid-bg, and a second one made this band visibly denser. */}

                {/* The two marks, centred and given room to breathe. Wraps to a
                    stack on narrow screens rather than crushing the divider. */}
                <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 38, flexWrap: "wrap", marginBottom: 38 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                    <PvbiMark size={72} />
                    <div style={{ textAlign: "left" }}>
                      <div className="hero-word" style={{ fontSize: "clamp(27px, 5.2vw, 42px)", fontWeight: 700, letterSpacing: "-.03em", lineHeight: 1.05 }}>
                        PowerVision BI
                      </div>
                      <p style={{ fontSize: 12.5, color: T.textMuted, letterSpacing: ".26em", textTransform: "uppercase", marginTop: 7 }}>
                        We Make Data Dance
                      </p>
                    </div>
                  </div>

                  {clientName && (
                    <>
                      <div style={{ width: 1, height: 74, background: `linear-gradient(180deg, transparent, ${T.borderHover}, transparent)` }} />
                      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                        {/* tenantLogo already tracks "View as": fetchReports sends the
                            impersonation header, so the tenant payload (name AND logo)
                            is the client being viewed. */}
                        <ClientMark name={clientName} logoUrl={tenantLogo} size={64} />
                        <div style={{ textAlign: "left" }}>
                          <p style={{ fontSize: "clamp(16px, 2.4vw, 21px)", fontWeight: 600, lineHeight: 1.2 }}>{clientName}</p>
                          <p style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Your data workspace</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ position: "relative" }}>
                  <h1 style={{ fontSize: "clamp(22px, 3.4vw, 31px)", fontWeight: 600, letterSpacing: "-.025em", marginBottom: 9 }}>
                    Good {hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening"}, {user.name?.split(" ")[0] || user.name}
                  </h1>
                  <p style={{ fontSize: 14.5, color: T.textSecondary }}>
                    {waiting.length > 0
                      ? `${waiting.length} ${waiting.length === 1 ? "request needs" : "requests need"} your attention.`
                      : "Everything's up to date. Pick up where you left off."}
                  </p>
                </div>
              </div>

              {/* ── Needs you ── only when it does */}
              {waiting.length > 0 && (
                <div className="fu1" style={{
                  ...panel, padding: "15px 18px", marginBottom: 18,
                  display: "flex", alignItems: "center", gap: 13, flexWrap: "wrap",
                }}>
                  {/* A small amber dot carries the "attention" signal — a tinted
                      panel and a solid button shouted over everything else. */}
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.accent, flexShrink: 0, boxShadow: `0 0 8px ${T.accent}` }} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                      {waiting.length} {waiting.length === 1 ? "request" : "requests"} waiting on you
                    </p>
                    <p style={{ fontSize: 11.5, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {waiting.slice(0, 2).map(r => r.title).join(" · ")}{waiting.length > 2 ? ` · +${waiting.length - 2} more` : ""}
                    </p>
                  </div>
                  <button className="btn-secondary" style={{ fontSize: 12.5, padding: "7px 16px" }}
                    onClick={() => setNav("projects")}>Review</button>
                </div>
              )}

              {/* ── Quick launch ── */}
              <div className="reports-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14, marginBottom: 18 }}>
                {[
                  {
                    id: "alt-reports", nav: altReportsForTenant.length > 0 ? "alt-reports" : "reports",
                    icon: "chart", title: "Reports",
                    line: totalReports ? `${totalReports} available` : "Nothing published yet",
                    sub: "Open your dashboards and analysis",
                  },
                  {
                    id: "projects", nav: "projects", icon: "layers", title: "Projects",
                    line: openProjects.length ? `${openProjects.length} in progress · ${avgProgress}% avg` : (hs ? "Nothing active" : "Loading…"),
                    sub: hs?.isAgency ? "Track delivery across every client" : "See what we're building for you",
                  },
                ].map((t, i) => (
                  <div key={t.id} className="launch-tile fu1" onClick={() => setNav(t.nav)}
                    style={{ ...panel, padding: "22px 22px 20px", cursor: "pointer", animationDelay: `${i * 0.07}s`, position: "relative", overflow: "hidden" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(196,122,16,.45)"; e.currentTarget.style.background = "rgba(255,255,255,.035)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = panel.border.split(" ").slice(2).join(" "); e.currentTarget.style.background = panel.background; }}>
                    <WaveStrip height={46} opacity={0.28} />
                    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, marginBottom: 13 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "rgba(255,255,255,.04)", border: `1px solid ${T.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}><Icon n={t.icon} size={17} color={T.accent} /></div>
                      <div>
                        <p style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>{t.title}</p>
                        <p style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{t.sub}</p>
                      </div>
                      <span style={{ marginLeft: "auto", color: T.textMuted, fontSize: 17 }}>→</span>
                    </div>
                    <p style={{ position: "relative", fontSize: 12.5, color: T.accent, fontWeight: 600 }}>{t.line}</p>
                  </div>
                ))}
              </div>

              {/* ── Delivery at a glance ── real progress bars, not a KPI guess */}
              {openProjects.length > 0 && (
                <div className="fu2" style={{ ...panel, padding: "22px 24px 24px", marginBottom: 0, position: "relative", overflow: "hidden" }}>
                  <WaveStrip height={70} opacity={0.35} />
                  <div style={{ position: "relative" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: T.textSecondary }}>Delivery at a glance</p>
                      <button className="btn-ghost" style={{ fontSize: 12, color: T.textSecondary }} onClick={() => setNav("projects")}>View all →</button>
                    </div>

                    {/* Portfolio summary: one ring for overall completion, one bar for
                        how the work is actually tracking. Both are real figures. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap", paddingBottom: 20, marginBottom: 6, borderBottom: `1px solid ${T.border}` }}>
                      <HomeRing value={avgProgress} size={98} stroke={8} sub="complete" />
                      <HealthBar
                        onTrack={openProjects.filter(p => p.health === "on_track").length}
                        atRisk={openProjects.filter(p => p.health === "at_risk").length}
                        offTrack={openProjects.filter(p => p.health === "off_track").length}
                      />
                      <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
                        {[
                          { k: "Active", v: openProjects.length },
                          { k: "Milestones done", v: openProjects.reduce((s, p) => s + (p.milestonesDone || 0), 0) },
                          { k: "Due in 30d", v: hs?.summary?.dueSoon ?? 0 },
                        ].map(x => (
                          <div key={x.k}>
                            <p style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}><CountUp value={x.v} /></p>
                            <p style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", marginTop: 6 }}>{x.k}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {openProjects.slice(0, 4).map((p, idx) => {
                      const hl = PRJ_HEALTH.find(h => h.id === p.health) || { color: T.textMuted };
                      return (
                        <div key={p.id} onClick={() => setNav("projects")} className="pip-in"
                          style={{ display: "flex", alignItems: "center", gap: 16, padding: "11px 0", cursor: "pointer", borderBottom: idx < Math.min(openProjects.length, 4) - 1 ? `1px solid ${T.border}` : "none", animationDelay: `${idx * 0.06}s` }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: hl.color, flexShrink: 0 }} title={hl.label} />
                          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                            <p style={{ fontSize: 12.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {hs?.isAgency && <span style={{ color: T.textMuted }}>{p.clientName} · </span>}{p.name}
                            </p>
                            <p style={{ fontSize: 10.5, color: T.textMuted, marginTop: 3 }}>
                              {p.nextMilestone ? `next: ${p.nextMilestone.title}` : "No milestones yet"}
                            </p>
                          </div>
                          {/* Milestone pips: one per milestone, filled as they complete.
                              Shows the shape of the work, not just a percentage. */}
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            {Array.from({ length: Math.min(p.milestonesTotal || 0, 10) }).map((_, i) => (
                              <span key={i} style={{
                                width: 7, height: 7, borderRadius: 2, transition: "background .4s ease",
                                transitionDelay: `${i * 60}ms`,
                                background: i < p.milestonesDone ? T.accent : "rgba(255,255,255,.1)",
                              }} />
                            ))}
                          </div>
                          <div style={{ flex: "0 1 150px", minWidth: 90 }}>
                            <PrjProgress value={p.progress} color={p.progress === 100 ? T.success : T.accent} height={5} showLabel />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Figures ── all from our own DB, instant */}
              {/* The four stat tiles that used to sit here were removed: every one of
                  them repeated a figure already on this page (reports count = the
                  Reports tile, active + avg progress = the ring and its counts,
                  waiting on you = the banner). Four extra boxes, no new information. */}
            </div>
          );
        })()}

        {nav === "reports" && !activeReport && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Your reports</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Live Power BI reports — click any report to open it</p>
            </div>

            {/* Stats */}
            <div className="fu1 mob-stats" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 13, marginBottom: 28 }}>
              {[
                { label: "Reports available", value: loadingReports ? "…" : reports.length, icon: "grid", color: T.accent },
                { label: "Tenant", value: viewAs?.name || tenantName || "Loading…", icon: "db", color: T.success },
                { label: "Session status", value: "Active", icon: "refresh", color: T.warn },
              ].map((s, i) => (
                <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</p>
                      <p style={{ fontSize: 20, fontWeight: 600, color: s.color }}>{s.value}</p>
                    </div>
                    <div style={{ color: s.color, opacity: 0.7 }}><Icon n={s.icon} size={18} /></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error state */}
            {reportsErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{reportsErr}</div>}

            {/* Loading state */}
            {loadingReports && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 32, color: T.textSecondary }}>
                <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                Loading your reports…
              </div>
            )}

            {!loadingReports && !reportsErr && (
              <>
                {/* Filters */}
                <div className="fu2" style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
                  <input className="field-input" style={{ maxWidth: 240, padding: "9px 14px" }} placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
                  <div style={{ display: "flex", gap: 6 }}>
                    {cats.map(c => (
                      <button key={c} onClick={() => setCat(c)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${cat === c ? T.accent : T.border}`, background: cat === c ? T.accentGlow : "transparent", color: cat === c ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "all .15s" }}>{c}</button>
                    ))}
                  </div>
                </div>

                {/* Report grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                  {filtered.map((r, i) => (
                    <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => openReport(r)}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                        {r.cat && <span className="badge" style={{ background: `${CAT_COLOR[r.cat] || T.accent}18`, color: CAT_COLOR[r.cat] || T.accent, border: `1px solid ${CAT_COLOR[r.cat] || T.accent}30`, fontSize: 10 }}>{r.cat}</span>}
                        {r.updated && <span style={{ fontSize: 11, color: T.textMuted }}>{r.updated}</span>}
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
                      <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
                      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
                        <Icon n="external" size={13} /> Open report
                      </div>
                    </div>
                  ))}
                  {filtered.length === 0 && !loadingReports && (
                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 48, color: T.textMuted }}>
                      <Icon n="chart" size={32} color={T.border} />
                      <p style={{ marginTop: 12, fontSize: 14 }}>No reports found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Power BI Report Viewer ── */}
        {nav === "reports" && activeReport && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setActiveReport(null); setEmbedData(null); }}>← Back</button>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>{activeReport.name}</h2>
                <p style={{ fontSize: 12, color: T.textSecondary }}>{activeReport.description}</p>
              </div>
              <span className="badge badge-success"><span style={{ width: 5, height: 5, borderRadius: "50%", background: T.success }} />Live data</span>
              <button
                onClick={async () => {
                  if (pbiReportRef.current?.print) {
                    try { await pbiReportRef.current.print(); } catch { window.print(); }
                  } else { window.print(); }
                }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                ↓ PDF
              </button>

              {/* Share by email */}
              <button
                onClick={() => { setShowShareModal(true); setShareEmail(""); setShareNote(""); }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                ✉ Share
              </button>
            </div>

            {/* Share modal */}
            {showShareModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 440, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Share report</h2>
                  <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 20 }}>{activeReport?.name}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div className="field-group">
                      <label className="field-label">Recipient email</label>
                      <input className="field-input" type="email" placeholder="colleague@company.com" value={shareEmail} onChange={e => setShareEmail(e.target.value)} />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Personal note (optional)</label>
                      <textarea className="field-input" rows={3} placeholder="Here's the report you asked about..." value={shareNote} onChange={e => setShareNote(e.target.value)} style={{ resize: "vertical", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button
                      className="btn-primary"
                      disabled={shareSending || !shareEmail.trim()}
                      onClick={async () => {
                        setShareSending(true);
                        try {
                          const res = await fetch(`${API_BASE}/email-report`, {
                            method: "POST",
                            headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
                            body: JSON.stringify({
                              reportId: activeReport?.pbiReportId || activeReport?.id,
                              reportName: activeReport?.name,
                              reportDescription: activeReport?.description,
                              recipientEmail: shareEmail.trim(),
                              note: shareNote.trim(),
                            }),
                          });
                          if (res.ok) {
                            setShowShareModal(false);
                            alert(`Report shared with ${shareEmail} ✓`);
                          } else {
                            const e = await res.json();
                            alert("Failed: " + (e.error || "Unknown error"));
                          }
                        } catch { alert("Send failed. Please try again."); }
                        finally { setShareSending(false); }
                      }}
                      style={{ flex: 1 }}>
                      {shareSending ? "Sending…" : "Send email"}
                    </button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowShareModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 500 }}>

              {loadingEmbed && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: T.textSecondary }}>
                  <span className="spinner spin" style={{ width: 28, height: 28, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                  <p style={{ fontSize: 14 }}>Loading report…</p>
                </div>
              )}

              {embedErr && (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <div className="alert alert-error" style={{ display: "inline-flex" }}><Icon n="alert" size={15} color={T.error} />{embedErr}</div>
                  <br /><br />
                  <button className="btn-secondary" onClick={() => openReport(activeReport)}>Retry</button>
                </div>
              )}

              {/* Power BI Embedded iframe — uses the token from /api/embed-token */}
             {embedData && !loadingEmbed && (
  <div
    id="report-print-frame"
    ref={el => {
      if (el && embedData && window.powerbi) {
        window.powerbi.reset(el);
        pbiReportRef.current = window.powerbi.embed(el, {
          type: 'report',
          id: embedData.reportId,
          embedUrl: embedData.embedUrl,
          accessToken: embedData.token,
          tokenType: window.models?.TokenType?.Embed ?? 1,
          settings: {
            navContentPaneEnabled: true,
            filterPaneEnabled: true,
            exportDataEnabled: true,
          }
        });
      }
    }}
    style={{ width: "100%", height: "100%", minHeight: 600 }}
  />
)}
            </div>

            {/* Comments panel */}
            <div style={{ borderTop: `1px solid ${T.border}`, padding: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 10 }}>Comments ({reportComments.length})</p>
              <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                {reportComments.map(c => (
                  <div key={c.id} style={{ background: T.bg, borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontWeight: 600, color: T.accent }}>{c.user?.name || c.user?.email}</span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ color: T.textMuted, fontSize: 11 }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                        <button onClick={async () => { await fetch(`${API_BASE}/comments`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: c.id }) }); loadComments(activeReport.pbiReportId || activeReport.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 0 }}><Icon n="x" size={11} /></button>
                      </div>
                    </div>
                    <p style={{ color: T.textPrimary, lineHeight: 1.5 }}>{c.content}</p>
                  </div>
                ))}
                {reportComments.length === 0 && <p style={{ fontSize: 11, color: T.textMuted }}>No comments yet — be the first to add one</p>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="field-input" style={{ fontSize: 12 }} placeholder="Add a comment…" value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={async e => { if (e.key === "Enter" && newComment.trim()) { await fetch(`${API_BASE}/comments`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ pbiReportId: activeReport.pbiReportId || activeReport.id, reportName: activeReport.name, content: newComment.trim() }) }); setNewComment(""); loadComments(activeReport.pbiReportId || activeReport.id); }}} />
                <button className="btn-secondary" style={{ whiteSpace: "nowrap", padding: "8px 14px" }} onClick={async () => { if (!newComment.trim()) return; await fetch(`${API_BASE}/comments`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ pbiReportId: activeReport.pbiReportId || activeReport.id, reportName: activeReport.name, content: newComment.trim() }) }); setNewComment(""); loadComments(activeReport.pbiReportId || activeReport.id); }}>Post</button>
              </div>
            </div>
          </div>
        )}
{/* ── Alternative Reports ── */}
{nav === "alt-reports" && !altReport && (() => {
  const openAlt = (r) => { setAltReport(r); if (r.type === "analytics") { setAnalyticsData(null); setAnalyticsLoading(true); setAnalyticsErr(""); fetch(`${API_BASE}/analytics`, { headers: authHeaders(token, viewAs?.id) }).then(res=>res.json()).then(d=>{ if(d.error) throw new Error(d.error); setAnalyticsData(d); }).catch(e=>setAnalyticsErr(e.message)).finally(()=>setAnalyticsLoading(false)); } };
  const card = (r, i) => (
    <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => openAlt(r)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <span className="badge badge-info">{r.tenant}</span>
        {r.status === "draft" && (
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".04em", color: "#b45309", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "2px 8px" }}>DRAFT</span>
        )}
      </div>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
      <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
        <Icon n="external" size={13} /> Open report
      </div>
    </div>
  );
  const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 };
  const usesDepts = altReportsForTenant.some(r => r.department);
  const extraDepts = [...new Set(altReportsForTenant.filter(r => r.department && !DEPARTMENTS.includes(r.department)).map(r => r.department))];
  const tabs = ["All", ...DEPARTMENTS, ...extraDepts];
  const activeTab = tabs.includes(deptTab) ? deptTab : "All";
  const tabItems = activeTab === "All" ? altReportsForTenant : altReportsForTenant.filter(r => r.department === activeTab);
  return (
    <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
      <div className="fu" style={{ marginBottom: usesDepts ? 18 : 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Reports</h1>
        <p style={{ fontSize: 13, color: T.textSecondary }}>Interactive reports{usesDepts ? " by department — pick a tab" : ""} — click any report to open it</p>
      </div>
      {!usesDepts ? (
        <div style={gridStyle}>{altReportsForTenant.map(card)}</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${T.border}`, marginBottom: 24, overflowX: "auto" }}>
            {tabs.map(t => {
              const cnt = t === "All" ? altReportsForTenant.length : altReportsForTenant.filter(r => r.department === t).length;
              const active = activeTab === t;
              return (
                <div key={t} onClick={() => setDeptTab(t)} style={{
                  padding: "9px 15px", cursor: "pointer", whiteSpace: "nowrap", fontSize: 13, fontWeight: 600,
                  color: active ? T.accent : T.textSecondary, opacity: (cnt > 0 || t === "All") ? 1 : .55,
                  borderBottom: active ? `2px solid ${T.accent}` : "2px solid transparent", marginBottom: -1,
                }}>
                  {t}{cnt > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, opacity: .8 }}>{cnt}</span>}
                </div>
              );
            })}
          </div>
          {tabItems.length > 0
            ? <div key={activeTab} className="fu" style={gridStyle}>{tabItems.map(card)}</div>
            : <p style={{ fontSize: 13, color: T.textSecondary, opacity: .7, marginTop: 8 }}>No reports in <b>{activeTab}</b> yet — coming soon.</p>}
        </>
      )}
    </div>
  );
})()}
{nav === "alt-reports" && altReport && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16, overflow: (altReport.type === "analytics" || altReport.type === "jobcost") ? "auto" : "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
      <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setAltReport(null); setAnalyticsData(null); }}>← Back</button>
      <div style={{ flex: 1 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>{altReport.name}</h2>
        <p style={{ fontSize: 12, color: T.textSecondary }}>{altReport.description}</p>
      </div>
      {(altReport.id === "bio-inventory-report" || altReport.id === "bio-refunds-report") && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {shareMsg && <span style={{ fontSize: 12, color: T.textSecondary }}>{shareMsg}</span>}
          <button onClick={() => mintShare("all", altReport.id === "bio-refunds-report" ? "refunds" : "inventory")} disabled={shareBusy} title="Create a shareable link (expires in 30 days)"
            style={{ display: "flex", alignItems: "center", gap: 6, background: T.accentGlow, color: T.accent, border: `1px solid ${T.accent}55`, borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: shareBusy ? "default" : "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <Icon n="external" size={14} color={T.accent} /> Share
          </button>
        </div>
      )}
      <span className="badge badge-info">{altReport.tenant}</span>
      {altReport.status === "draft" && (
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".04em", color: "#b45309", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "2px 8px" }}>DRAFT — not visible to client</span>
      )}
      {altReport.type === "analytics" && (
        <button onClick={() => { setAnalyticsData(null); setAnalyticsLoading(true); setAnalyticsErr(""); fetch(`${API_BASE}/analytics`, { headers: authHeaders(token, viewAs?.id) }).then(r=>r.json()).then(d=>{ if(d.error) throw new Error(d.error); setAnalyticsData(d); }).catch(e=>setAnalyticsErr(e.message)).finally(()=>setAnalyticsLoading(false)); }}
          style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 14px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
          <Icon n="refresh" size={13} />Refresh
        </button>
      )}
    </div>
    {altReport.type === "html" && altReport.devOnly && !canSeeDrafts && (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 46 }}>🚧</div>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>This report is under development</h2>
        <p style={{ fontSize: 14, color: T.textSecondary, maxWidth: 460, lineHeight: 1.6 }}>The Refunds &amp; Chargebacks report isn’t quite ready yet. You’ll be notified as soon as access is available.</p>
      </div>
    )}
    {altReport.type === "html" && !(altReport.devOnly && !canSeeDrafts) && (
      <iframe src={altReportSrc} style={{ flex: 1, border: "none", borderRadius: 8, background: "#fff", height: "calc(100vh - 120px)", width: "100%" }} title={altReport.name} />
    )}
    {altReport.type === "analytics" && analyticsLoading && (
      <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.textSecondary, padding: 48 }}>
        <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />Loading analytics…
      </div>
    )}
    {altReport.type === "analytics" && analyticsErr && <div className="alert alert-error"><Icon n="alert" size={15} color={T.error} />{analyticsErr}</div>}
    {altReport.type === "analytics" && analyticsData && <AnalyticsCharts data={analyticsData} />}
    {altReport.type === "jobcost" && <JobCostReview apiBase={API_BASE} T={T} Icon={Icon} />}
  </div>
)}
{/* ── BioLongevity: per-brand report selector (Nova / LYV) ── */}
{(nav === "bio-nova" || nav === "bio-lyv") && (() => {
  const brandKey = nav === "bio-nova" ? "nova" : "lyv";
  const brandLabel = brandKey === "nova" ? "Nova" : "LYV";
  const list = BRAND_REPORTS[brandKey] || [];
  if (!brandReport) return (
    <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
      <div className="fu" style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>{brandLabel} Reports</h1>
        <p style={{ fontSize: 13, color: T.textSecondary }}>Interactive reports for {brandLabel} — click any report to open it</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
        {list.map((r, i) => (
          <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => setBrandReport(r)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span className="badge badge-info">{brandLabel}</span>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
            <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5, marginBottom: 14 }}>{r.description}</p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.accent, fontSize: 13, fontWeight: 500 }}>
              <Icon n="external" size={14} color={T.accent} />Open report
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setBrandReport(null)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 7, cursor: "pointer", color: T.textSecondary, padding: "7px 12px", fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>← {brandLabel} Reports</button>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600 }}>{brandReport.name}</h2>
            <p style={{ fontSize: 12, color: T.textSecondary }}>{brandReport.description}</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {shareMsg && <span style={{ fontSize: 12, color: T.textSecondary }}>{shareMsg}</span>}
          <button onClick={() => mintShare(brandKey)} disabled={shareBusy} title="Create a shareable link (expires in 30 days)"
            style={{ display: "flex", alignItems: "center", gap: 6, background: T.accentGlow, color: T.accent, border: `1px solid ${T.accent}55`, borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: shareBusy ? "default" : "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <Icon n="external" size={14} color={T.accent} /> Share
          </button>
          <span className="badge badge-info">{brandLabel}</span>
        </div>
      </div>
      <iframe src={brandReportSrc} style={{ flex: 1, border: "none", borderRadius: 8, background: "#fff", height: "calc(100vh - 120px)", width: "100%" }} title={`${brandLabel} — ${brandReport.name}`} />
    </div>
  );
})()}
{/* ── Order Lookup ── */}
{nav === "order-lookup" && (
  <div style={{ flex: 1, overflow: "auto", padding: "32px 36px" }}>
    <div className="fu" style={{ marginBottom: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Order Lookup</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Enter a sales order number to see everything related — items, invoices, shipments, videos & documents</p>
    </div>

    {/* Search */}
    <div style={{ display: "flex", gap: 10, marginBottom: 28, maxWidth: 500 }}>
      <input className="field-input" placeholder="e.g. 1968514" value={soQuery}
        onChange={e => setSoQuery(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") {
          setSoData(null); setSoErr(""); setSoLoading(true);
          fetch(`${API_BASE}/order-lookup?soNo=${encodeURIComponent(soQuery.trim())}`, { headers: authHeaders(token, viewAs?.id) })
            .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setSoData(d); })
            .catch(e => setSoErr(e.message)).finally(() => setSoLoading(false));
        }}}
        style={{ flex: 1, fontSize: 16, fontFamily: "'JetBrains Mono',monospace" }} />
      <button className="btn-primary" style={{ width: "auto", padding: "11px 24px" }}
        disabled={soLoading || !soQuery.trim()}
        onClick={() => {
          setSoData(null); setSoErr(""); setSoLoading(true);
          fetch(`${API_BASE}/order-lookup?soNo=${encodeURIComponent(soQuery.trim())}`, { headers: authHeaders(token, viewAs?.id) })
            .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setSoData(d); })
            .catch(e => setSoErr(e.message)).finally(() => setSoLoading(false));
        }}>
        {soLoading ? <><span className="spinner spin" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,.3)" }} /> Searching…</> : "Search"}
      </button>
    </div>

    {soErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{soErr}</div>}

    {soData && <SalesOrderDetail data={soData} />}

    {!soData && !soLoading && !soErr && (
      <div style={{ textAlign: "center", padding: "64px 0", color: T.textMuted }}>
        <Icon n="db" size={40} color={T.border} />
        <p style={{ marginTop: 16, fontSize: 14 }}>Enter a sales order number above</p>
        <p style={{ fontSize: 12, marginTop: 6 }}>Shows items, invoices, shipment status, YouTube videos & documents</p>
      </div>
    )}
  </div>
)}
{/* ── AI Agent ── */}
{nav === "ai" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div className="fu" style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>AI Data Agent</h1>
        <p style={{ fontSize: 13, color: T.textSecondary }}>Ask any question about your business data in plain English</p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => { setShowHistory(!showHistory); }} style={{ background: showHistory ? T.accentGlow2 : "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: showHistory ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
          <Icon n="clock" size={13} /> History
        </button>
        <button onClick={async () => { await fetch(`${API_BASE}/chat-history`, { method: "DELETE", headers: authHeaders(token, viewAs?.id) }); setMessages([]); setChatHistory([]); }} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textMuted, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Clear
        </button>
      </div>
    </div>

    <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12 }} id="chat-messages">
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: T.textMuted }}>
            <Icon n="zap" size={32} color={T.border} />
            <p style={{ marginTop: 12, fontSize: 14 }}>Ask me anything about your business data</p>
            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
              {["What were my top 5 customers last month?", "Show me overdue invoices", "How is my revenue trending?"].map(q => (
                <button key={q} onClick={() => sendMessage(q)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 8, padding: "8px 16px", color: T.accent, cursor: "pointer", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: m.chart ? "98%" : "75%", width: m.chart ? "98%" : undefined, padding: "12px 16px", borderRadius: 12, background: m.role === "user" ? T.accent : T.surface, border: `1px solid ${m.role === "user" ? T.accent : T.border}`, color: m.role === "user" ? "#fff" : T.textPrimary, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {m.content}
              {m.chart && <ChartWidget chart={m.chart} />}
              {m.exportData && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => downloadExcel(m.exportData, m.question)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.25)", borderRadius: 6, padding: "5px 12px", color: "#22c55e", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    ↓ Export to Excel ({m.exportData.length.toLocaleString()} rows)
                  </button>
                  {m.role === "assistant" && m.question && (
                    <button onClick={async () => {
                      const name = prompt("Save as KPI card — enter a short name:", m.question.slice(0,40));
                      if (!name) return;
                      await fetch(`${API_BASE}/saved-queries`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ name, question: m.question }) });
                      loadSavedQueries();
                      alert("Saved to Home dashboard ✓");
                    }} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.accentGlow2, border: `1px solid rgba(196,122,16,.25)`, borderRadius: 6, padding: "5px 12px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      ☆ Save as KPI
                    </button>
                  )}
                </div>
              )}
              {m.role === "assistant" && m.question && !m.exportData && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={async () => {
                    const name = prompt("Save as KPI card:", m.question.slice(0,40));
                    if (!name) return;
                    await fetch(`${API_BASE}/saved-queries`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ name, question: m.question }) });
                    loadSavedQueries(); alert("Saved to Home dashboard ✓");
                  }} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                    ☆ Save as KPI
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {aiLoading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "12px 16px", borderRadius: 12, background: T.surface, border: `1px solid ${T.border}` }}>
              <span className="spinner spin" style={{ width: 16, height: 16, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Saved queries quick-access */}
      {savedQueries.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 16px", display: "flex", gap: 6, overflowX: "auto" }}>
          {savedQueries.slice(0,5).map(q => (
            <button key={q.id} onClick={() => sendMessage(q.question)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 20, padding: "4px 12px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>
              {q.name}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: 16, display: "flex", gap: 10 }}>
        <input className="field-input" placeholder="Ask anything about your data..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} disabled={aiLoading} />
        <button type="button" className="btn-primary" style={{ width: "auto", padding: "11px 20px", whiteSpace: "nowrap" }} onClick={() => sendMessage()} disabled={aiLoading || !input.trim()}>Send</button>
      </div>
    </div>

    {/* History sidebar */}
    {showHistory && (
      <div style={{ width: 280, background: T.surface, borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column", marginTop: -60 }}>
        <div style={{ padding: "16px", borderBottom: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 13, fontWeight: 600 }}>Chat history</p>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {chatHistory.filter(m => m.role === "user").map(m => (
            <div key={m.id} onClick={() => sendMessage(m.content)} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4, fontSize: 12, color: T.textSecondary, background: T.bg, border: `1px solid ${T.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceHover}
              onMouseLeave={e => e.currentTarget.style.background = T.bg}>
              {m.content.slice(0, 60)}{m.content.length > 60 ? "…" : ""}
            </div>
          ))}
          {chatHistory.filter(m => m.role === "user").length === 0 && <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", padding: 20 }}>No history yet</p>}
        </div>
      </div>
    )}
  </div>
)}
        {/* ── Projects ── */}
        {nav === "projects" && <ProjectsApp user={user} />}

        {/* ── CRM ── */}
        {nav === "crm" && <CrmApp user={user} />}

        {/* ── Hub ── For Rain Drop this is their embedded RainBrain page
             (served same-origin via the /rdp-books proxy); every other tenant
             gets the built-in task-workflow Hub. ── */}
        {nav === "hub" && (activeTenantName.includes("rain-drop") ? (
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <iframe
              title="Hub"
              src="https://books.portal.powervisionbi.com/"
              style={{ flex: 1, width: "100%", border: "none", background: "#fff" }}
              allow="clipboard-read; clipboard-write"
            />
          </div>
        ) : <HubApp user={user} />)}

        {/* ── Alerts ── */}
        {nav === "alerts" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div><h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>KPI Alerts</h1><p style={{ fontSize: 13, color: T.textSecondary }}>Get emailed when a metric crosses a threshold</p></div>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAlertModal(true)}>+ New Alert</button>
            </div>
            {alerts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 64, color: T.textMuted }}><Icon n="bell" size={32} color={T.border} /><p style={{ marginTop: 12 }}>No alerts set up yet</p></div>
            ) : alerts.map(a => (
              <div key={a.id} className="card" style={{ padding: 16, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{a.name}</p>
                  <p style={{ fontSize: 12, color: T.textSecondary }}>{a.question}</p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Threshold: {a.condition === "gt" ? ">" : a.condition === "lt" ? "<" : "="} {a.threshold} · {a.frequency} · → {a.notifyEmail}{a.lastValue != null ? ` · Last: ${a.lastValue}` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "PATCH", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: a.id, isActive: !a.isActive }) }); loadAlerts(); }} style={{ background: a.isActive ? "rgba(34,197,94,.12)" : T.bg, border: `1px solid ${a.isActive ? "rgba(34,197,94,.3)" : T.border}`, borderRadius: 6, padding: "4px 10px", color: a.isActive ? "#22c55e" : T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{a.isActive ? "Active" : "Paused"}</button>
                  <button onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: a.id }) }); loadAlerts(); }} style={{ background: "none", border: `1px solid rgba(239,68,68,.3)`, borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Delete</button>
                </div>
              </div>
            ))}
            {showAlertModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 480, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>New KPI Alert</h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="field-input" placeholder="Alert name (e.g. High open orders)" value={newAlert.name} onChange={e => setNewAlert(p => ({ ...p, name: e.target.value }))} />
                    <input className="field-input" placeholder="AI question (e.g. how many open sales orders)" value={newAlert.question} onChange={e => setNewAlert(p => ({ ...p, question: e.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="field-input" value={newAlert.condition} onChange={e => setNewAlert(p => ({ ...p, condition: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                        <option value="gt">Greater than (&gt;)</option><option value="lt">Less than (&lt;)</option><option value="eq">Equal to (=)</option>
                      </select>
                      <input className="field-input" placeholder="Threshold (e.g. 500)" type="number" value={newAlert.threshold} onChange={e => setNewAlert(p => ({ ...p, threshold: e.target.value }))} />
                    </div>
                    <input className="field-input" placeholder="Notify email" type="email" value={newAlert.notifyEmail} onChange={e => setNewAlert(p => ({ ...p, notifyEmail: e.target.value }))} />
                    <select className="field-input" value={newAlert.frequency} onChange={e => setNewAlert(p => ({ ...p, frequency: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                      <option value="hourly">Check hourly</option><option value="daily">Check daily</option><option value="weekly">Check weekly</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ ...newAlert, threshold: Number(newAlert.threshold) }) }); setShowAlertModal(false); setNewAlert({ name:"", question:"", condition:"gt", threshold:"", notifyEmail:"", frequency:"daily" }); loadAlerts(); }}>Create Alert</button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAlertModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Scheduled Reports ── */}
        {nav === "schedule" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div><h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Scheduled Reports</h1><p style={{ fontSize: 13, color: T.textSecondary }}>Automatically email reports or AI summaries on a schedule</p></div>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowScheduleModal(true)}>+ New Schedule</button>
            </div>
            {schedules.length === 0 ? (
              <div style={{ textAlign: "center", padding: 64, color: T.textMuted }}><Icon n="clock" size={32} color={T.border} /><p style={{ marginTop: 12 }}>No scheduled reports yet</p></div>
            ) : schedules.map(s => (
              <div key={s.id} className="card" style={{ padding: 16, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{s.reportName}</p>
                  <p style={{ fontSize: 12, color: T.textSecondary }}>{s.question || "Power BI report"}</p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{s.frequency} · {s.recipients} {s.nextRun ? `· Next: ${new Date(s.nextRun).toLocaleDateString()}` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "PATCH", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: s.id, isActive: !s.isActive }) }); loadSchedules(); }} style={{ background: s.isActive ? "rgba(34,197,94,.12)" : T.bg, border: `1px solid ${s.isActive ? "rgba(34,197,94,.3)" : T.border}`, borderRadius: 6, padding: "4px 10px", color: s.isActive ? "#22c55e" : T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.isActive ? "Active" : "Paused"}</button>
                  <button onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: s.id }) }); loadSchedules(); }} style={{ background: "none", border: `1px solid rgba(239,68,68,.3)`, borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Delete</button>
                </div>
              </div>
            ))}
            {showScheduleModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 480, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>New Scheduled Report</h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="field-input" placeholder="Report name" value={newSchedule.reportName} onChange={e => setNewSchedule(p => ({ ...p, reportName: e.target.value }))} />
                    <input className="field-input" placeholder="AI question for summary (optional)" value={newSchedule.question} onChange={e => setNewSchedule(p => ({ ...p, question: e.target.value }))} />
                    <input className="field-input" placeholder="Recipients (comma-separated emails)" value={newSchedule.recipients} onChange={e => setNewSchedule(p => ({ ...p, recipients: e.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="field-input" value={newSchedule.frequency} onChange={e => setNewSchedule(p => ({ ...p, frequency: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                        <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                      </select>
                      <select className="field-input" value={newSchedule.hour} onChange={e => setNewSchedule(p => ({ ...p, hour: Number(e.target.value) }))} style={{ background: T.surface, color: T.textPrimary }}>
                        {[6,7,8,9,10,11,12,13,14,15,16,17,18].map(h => <option key={h} value={h}>{h}:00</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify(newSchedule) }); setShowScheduleModal(false); setNewSchedule({ reportName:"", question:"", recipients:"", frequency:"weekly", dayOfWeek:1, hour:8 }); loadSchedules(); }}>Create Schedule</button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowScheduleModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Audit Log ── */}
        {nav === "audit" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Audit Log</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Track who viewed what and when</p>
            </div>
            <div className="card" style={{ padding: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {["User","Action","Detail","When"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(l => (
                    <tr key={l.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: "10px 16px", fontSize: 13 }}>{l.user?.name || l.user?.email}</td>
                      <td style={{ padding: "10px 16px", fontSize: 12 }}><span style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, color: T.accent, padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>{l.action}</span></td>
                      <td style={{ padding: "10px 16px", fontSize: 12, color: T.textSecondary, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.detail}</td>
                      <td style={{ padding: "10px 16px", fontSize: 11, color: T.textMuted, whiteSpace: "nowrap" }}>{new Date(l.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && <tr><td colSpan={4} style={{ padding: 32, textAlign: "center", color: T.textMuted, fontSize: 13 }}>No activity logged yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

{/* ── Admin Panel ── */}
{nav === "admin" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Admin Panel</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Manage clients, reports and users</p>
    </div>

    {/* Stats */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
      {[
        { label: "Total Clients", value: adminData?._error ? "—" : (adminData?.tenants?.length ?? "…"), icon: "users" },
        { label: "Total Reports", value: adminData?._error ? "—" : (adminData?.reports?.length ?? "…"), icon: "grid" },
        { label: "Platform Status", value: "Live", icon: "zap" },
      ].map(s => (
        <div key={s.label} className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Icon n={s.icon} size={16} color={T.accent} />
            <span style={{ fontSize: 12, color: T.textSecondary }}>{s.label}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
        </div>
      ))}
    </div>

    {/* Error banner */}
    {adminData?._error && (
      <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#f87171" }}>⚠ Failed to load admin data: {adminData._error}</span>
        <button onClick={loadAdminData} style={{ background: "none", border: "1px solid rgba(239,68,68,.4)", borderRadius: 6, padding: "4px 12px", color: "#f87171", cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Retry</button>
      </div>
    )}

{/* Clients list */}
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Clients</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={loadAdminData} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>⟳ Refresh</button>
          <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAddTenant(true)}>
            + Add Client
          </button>
        </div>
      </div>
      {!adminData && <div style={{ color: T.textSecondary, fontSize: 13, padding: "12px 0" }}>Loading…</div>}
      {adminData && !adminData._error && adminData.tenants?.length === 0 && <div style={{ color: T.textSecondary, fontSize: 13, padding: "12px 0" }}>No clients yet. Add one above.</div>}
      {adminData?.tenants?.map(t => (
        <div key={t.id} style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</p>
              <p style={{ fontSize: 12, color: T.textSecondary }}>{t.slug} · {t.dbType === "client" ? "🔗 Client DB" : "🏠 Hosted DB"}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: t.isActive ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)", color: t.isActive ? "#22c55e" : "#ef4444" }}>
                {t.isActive ? "Active" : "Inactive"}
              </span>
              <button onClick={() => setExpandedTenant(expandedTenant === t.id ? null : t.id)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", color: T.textSecondary, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {expandedTenant === t.id ? "Hide" : "Manage"}
              </button>
              <button
                onClick={() => { setViewAs({ id: t.id, name: t.name }); setNav("reports"); setMessages([]); setActiveReport(null); }}
                style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                👁 View as
              </button>
              <button
                onClick={async () => {
                  const next = !t.isActive;
                  await fetch(`${API_BASE}/admin/tenants/${t.id}`, { method: "PATCH", headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" }, body: JSON.stringify({ isActive: next }) });
                  loadAdminData();
                }}
                style={{ background: "none", border: `1px solid ${t.isActive ? "rgba(239,68,68,.3)" : "rgba(34,197,94,.3)"}`, borderRadius: 6, padding: "4px 10px", color: t.isActive ? "#ef4444" : "#22c55e", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {t.isActive ? "Disable" : "Enable"}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Permanently delete "${t.name}" and all their users? This cannot be undone.`)) return;
                  await fetch(`${API_BASE}/admin/tenants/${t.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}` } });
                  loadAdminData();
                }}
                style={{ background: "none", border: "1px solid rgba(239,68,68,.3)", borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Delete
              </button>
            </div>
          </div>

          {expandedTenant === t.id && (
            <div style={{ background: T.bg, borderRadius: 8, padding: 16, marginTop: 8 }}>

              {/* AI Context Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>AI Agent — Application & Context</p>

                {/* App type selector */}
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <select
                    id={`app-type-${t.id}`}
                    defaultValue={t.appType || "unknown"}
                    className="field-input"
                    style={{ maxWidth: 220, background: T.surface, color: T.textPrimary }}>
                    <option value="unknown">Unknown / Auto-detect</option>
                    <option value="sage100">Sage 100 (MAS 90/200)</option>
                    <option value="sage50">Sage 50</option>
                    <option value="sapb1">SAP Business One</option>
                    <option value="dynamics365">Microsoft Dynamics 365 / BC</option>
                    <option value="quickbooks">QuickBooks</option>
                    <option value="netsuite">NetSuite</option>
                    <option value="odoo">Odoo</option>
                    <option value="syspro">SYSPRO</option>
                    <option value="custom">Custom / Other</option>
                  </select>
                  <button
                    onClick={async (e) => {
                      const btn = e.target;
                      btn.textContent = "Learning…";
                      btn.disabled = true;
                      try {
                        const appType = document.getElementById(`app-type-${t.id}`).value;
                        const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/learn-schema`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ appType })
                        });
                        const data = await res.json();
                        if (!res.ok) { alert("Failed: " + data.error); return; }
                        document.getElementById(`ai-context-${t.id}`).value = data.aiContext;
                        loadAdminData();
                        alert(`✓ Schema learned! Analysed ${data.tableCount} tables. AI Context updated automatically.`);
                      } catch(err) {
                        alert("Error: " + err.message);
                      } finally {
                        btn.textContent = "🧠 Auto-learn schema";
                        btn.disabled = false;
                      }
                    }}
                    style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "6px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                    🧠 Auto-learn schema
                  </button>
                </div>

                <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Auto-learn connects to the client's database, analyses the schema and generates the context below. You can edit it afterwards.</p>
                <textarea
                  className="field-input"
                  rows={5}
                  placeholder="Click Auto-learn to generate this automatically, or type manually..."
                  defaultValue={t.aiContext || ""}
                  id={`ai-context-${t.id}`}
                  style={{ width: "100%", resize: "vertical", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12 }}
                />
                <button
                  onClick={async () => {
                    const val = document.getElementById(`ai-context-${t.id}`).value;
                    const appType = document.getElementById(`app-type-${t.id}`).value;
                    await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ aiContext: val, appType })
                    });
                    loadAdminData();
                    alert("Saved ✓");
                  }}
                  style={{ marginTop: 8, background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Client logo — shown beside the Power Vision mark on their home
                  screen. Blank falls back to a monogram, so this is optional. */}
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Client logo</p>
                <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
                  Public image URL (SVG or PNG, transparent background works best). Leave blank to show their initials instead.
                </p>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input className="field-input" id={`logo-url-${t.id}`} defaultValue={t.logoUrl || ""}
                    placeholder="https://client.com/logo.svg" style={{ flex: "1 1 260px", fontSize: 12 }} />
                  <ClientMark name={t.name} logoUrl={t.logoUrl} size={32} />
                  <button
                    onClick={async () => {
                      const val = document.getElementById(`logo-url-${t.id}`).value.trim();
                      await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                        method: "PATCH",
                        headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                        body: JSON.stringify({ logoUrl: val })
                      });
                      loadAdminData();
                      alert("Saved ✓");
                    }}
                    style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "7px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Save
                  </button>
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* KPI SQL Editor */}
              {(() => {
                const KPI_LABELS = [
                  { id: 'ytd-rev',      label: 'YTD Revenue',        hint: 'SUM of revenue for current year' },
                  { id: 'mtd-rev',      label: 'MTD Revenue',        hint: 'SUM of revenue for current month' },
                  { id: 'ytd-bud',      label: 'YTD Budget',         hint: 'Budget/target for current year (or SELECT \'No budget data\' AS result)' },
                  { id: 'mtd-bud',      label: 'MTD Budget',         hint: 'Budget/target for current month' },
                  { id: 'ytd-gp',       label: 'YTD GP%',            hint: 'Gross profit % for current year (e.g. 34.9)' },
                  { id: 'mtd-gp',       label: 'MTD GP%',            hint: 'Gross profit % for current month' },
                  { id: 'open-ord',     label: 'Open Orders (count)',  hint: 'COUNT of open confirmed orders' },
                  { id: 'open-ord-val', label: 'Open Order Balance',  hint: 'SUM value of open confirmed orders' },
                  { id: 'open-inv',     label: 'Open Invoices',       hint: 'SUM of outstanding invoice balances' },
                ];
                const kpiSql = t.kpiSql || {};
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>KPI SQL Queries</p>
                      <p style={{ fontSize: 11, color: T.textMuted }}>Write one SELECT query per KPI — must return a single value</p>
                    </div>
                    {KPI_LABELS.map(kpi => (
                      <div key={kpi.id} style={{ marginBottom: 12, background: T.bg, borderRadius: 8, padding: 12, border: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{kpi.label}</p>
                          <button
                            onClick={async () => {
                              const sql = document.getElementById(`kpi-sql-${t.id}-${kpi.id}`).value;
                              if (!sql.trim()) return;
                              const btn = document.getElementById(`kpi-test-${t.id}-${kpi.id}`);
                              btn.textContent = 'Testing…';
                              try {
                                const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/test-sql`, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ sql }),
                                });
                                const d = await res.json();
                                if (d.error) { btn.textContent = '✗ Error'; alert('SQL Error: ' + d.error); }
                                else { btn.textContent = '✓ OK: ' + JSON.stringify(Object.values(d.rows[0] || {})[0]); }
                              } catch(e) { btn.textContent = '✗ Failed'; }
                              setTimeout(() => { btn.textContent = 'Test'; }, 4000);
                            }}
                            id={`kpi-test-${t.id}-${kpi.id}`}
                            style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 5, padding: '3px 10px', color: T.textSecondary, cursor: 'pointer', fontSize: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Test
                          </button>
                        </div>
                        <p style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>{kpi.hint}</p>
                        <textarea
                          className="field-input"
                          id={`kpi-sql-${t.id}-${kpi.id}`}
                          rows={2}
                          defaultValue={kpiSql[kpi.id] || ''}
                          placeholder={`SELECT ... -- ${kpi.hint}`}
                          style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                        />
                      </div>
                    ))}
                    <button
                      onClick={async () => {
                        const newKpiSql = {};
                        KPI_LABELS.forEach(kpi => {
                          const val = document.getElementById(`kpi-sql-${t.id}-${kpi.id}`).value.trim();
                          if (val) newKpiSql[kpi.id] = val;
                        });
                        const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                          method: 'PATCH',
                          headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ kpiSql: newKpiSql }),
                        });
                        if (res.ok) { loadAdminData(); alert('KPI SQL saved ✓ — dashboard will use these queries immediately'); }
                        else alert('Save failed');
                      }}
                      style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: '6px 16px', color: T.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      Save All KPI SQL
                    </button>
                  </div>
                );
              })()}

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Power BI Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Power BI</p>
                <input
                  className="field-input"
                  placeholder="Workspace ID (GUID)"
                  defaultValue={t.pbiWorkspaceId || ""}
                  id={`pbi-ws-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  placeholder="Master user email (e.g. user@client.com)"
                  defaultValue={t.pbiMasterUser || ""}
                  id={`pbi-user-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  type="password"
                  placeholder="Master user password"
                  defaultValue={t.pbiMasterPassword || ""}
                  id={`pbi-pass-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  placeholder="Azure Tenant ID (GUID)"
                  defaultValue={t.azureTenantId || ""}
                  id={`pbi-az-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <button
                  onClick={async () => {
                    const pbiWorkspaceId = document.getElementById(`pbi-ws-${t.id}`).value;
                    const pbiMasterUser = document.getElementById(`pbi-user-${t.id}`).value;
                    const pbiMasterPassword = document.getElementById(`pbi-pass-${t.id}`).value;
                    const azureTenantId = document.getElementById(`pbi-az-${t.id}`).value;
                    const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ pbiWorkspaceId, pbiMasterUser, pbiMasterPassword, azureTenantId }),
                    });
                    if (res.ok) { loadAdminData(); alert("Saved ✓"); }
                    else alert("Save failed");
                  }}
                  style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save Power BI
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Database Connection Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Database Connection</p>
                <input
                  className="field-input"
                  placeholder="Connection string (postgresql://...)"
                  defaultValue={t.dbConnectionString || ""}
                  id={`db-conn-${t.id}`}
                  style={{ width: "100%", marginBottom: 8, fontFamily: "monospace", fontSize: 11 }}
                />
                <input
                  className="field-input"
                  placeholder="Schema (e.g. raindrop_snapshot)"
                  defaultValue={t.dbSchema || ""}
                  id={`db-schema-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <button
                  onClick={async () => {
                    const dbConnectionString = document.getElementById(`db-conn-${t.id}`).value;
                    const dbSchema = document.getElementById(`db-schema-${t.id}`).value;
                    const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ dbConnectionString, dbSchema }),
                    });
                    if (res.ok) { loadAdminData(); alert("Saved ✓"); }
                    else alert("Save failed");
                  }}
                  style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save Connection
                </button>
                <TestConnButton connId={`db-conn-${t.id}`} schemaId={`db-schema-${t.id}`} />
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Data Sync Section */}
              <div style={{ marginBottom: 20 }}>
                <SyncStatusSection tenantId={t.id} />
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Reports Section */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Reports</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {adminData?.reports?.map(r => {
                    const assigned = t.reports?.some(tr => tr.report?.id === r.id || tr.reportId === r.id)
                    return (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 6, background: assigned ? "rgba(196,122,16,.08)" : "rgba(255,255,255,.03)", border: `1px solid ${assigned ? T.accent : T.border}` }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</p>
                          <p style={{ fontSize: 11, color: T.textSecondary }}>{r.description || "No description"}</p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/reports`, {
                                method: assigned ? "DELETE" : "POST",
                                headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ reportId: r.id }),
                              })
                              if (res.ok) loadAdminData()
                              else alert("Failed to update report assignment")
                            } catch (err) {
                              alert("Error: " + err.message)
                            }
                          }}
                          style={{ background: assigned ? "rgba(239,68,68,.15)" : "rgba(196,122,16,.15)", border: "none", borderRadius: 6, padding: "4px 12px", color: assigned ? "#ef4444" : T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                        >
                          {assigned ? "Remove" : "Assign"}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Users Section */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Users</p>
                <button onClick={() => { setShowAddUser(true); setAddUserTenantId(t.id); setAddUserTenantName(t.name); setNewUser({ name: "", email: "", role: "VIEWER" }); }} style={{ background: "none", border: `1px solid ${T.accent}`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  + Invite User
                </button>
              </div>
              {!t.users || t.users.length === 0 ? (
                <p style={{ fontSize: 12, color: T.textMuted }}>No users yet — invite your first user above.</p>
              ) : (
                t.users.map(u => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</p>
                      <p style={{ fontSize: 11, color: T.textSecondary }}>{u.email}</p>
                    </div>
                    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(196,122,16,.15)", color: T.accent }}>{u.role}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>

    {/* Invite user modal — top level so it's never trapped by overflow/transform */}
    {showAddUser && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div className="card" style={{ width: 420, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Invite User</h2>
          <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 20 }}>Adding to: {addUserTenantName}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Email address" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
            <select className="field-input" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
              <option value="VIEWER">Viewer</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={inviteUser} disabled={inviteLoading}>
              {inviteLoading ? "Sending invite..." : "Send Invitation"}
            </button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddUser(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Add tenant modal */}
    {showAddTenant && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
        <div className="card" style={{ width: 440, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Add New Client</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Company name (e.g. Acme Corp)" value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Slug (e.g. acme-corp)" value={newTenant.slug} onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value }))} />
            <div>
              <input className="field-input" placeholder="Azure Tenant ID (from Azure Portal → Overview)" value={newTenant.azureTenantId} onChange={e => setNewTenant(p => ({ ...p, azureTenantId: e.target.value }))} />
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Azure Portal → Azure Active Directory → Overview → Tenant ID</p>
            </div>
            <input className="field-input" placeholder="Power BI Workspace ID (optional)" value={newTenant.pbiWorkspaceId} onChange={e => setNewTenant(p => ({ ...p, pbiWorkspaceId: e.target.value }))} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "hosted" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "hosted" ? T.accent : T.border}`, background: newTenant.dbType === "hosted" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "hosted" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🏠 We host (our Supabase)
              </button>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "client" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "client" ? T.accent : T.border}`, background: newTenant.dbType === "client" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "client" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🔗 Client's own DB
              </button>
            </div>
            {newTenant.dbType === "client" && (
              <>
                <input className="field-input" placeholder="Connection string (postgresql://...)" value={newTenant.dbConnectionString} onChange={e => setNewTenant(p => ({ ...p, dbConnectionString: e.target.value }))} />
                <input className="field-input" placeholder="Schema name (e.g. public)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
                <TestConnButton connString={newTenant.dbConnectionString} schema={newTenant.dbSchema} />
              </>
            )}
            {newTenant.dbType === "hosted" && (
              <input className="field-input" placeholder="Schema name (e.g. acme_corp)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={addTenant}>Create Client</button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddTenant(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

  </div>
)}
        {/* ── Settings ── */}
        {nav === "settings" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Settings</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Manage your account preferences</p>
            </div>
            <div className="card fu1" style={{ maxWidth: 520, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Account</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[["Display name", user.name], ["Email", user.email], ["Tenant", tenantName]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.textSecondary }}>{k}</span>
                    <span style={{ fontSize: 13, color: T.textPrimary }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card fu2" style={{ maxWidth: 520 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Session</h3>
              <button className="btn-secondary" style={{ color: T.error, borderColor: "rgba(248,81,73,.3)" }} onClick={onLogout}>Sign out</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin: Test connection button ───────────────────────────────────────────
// Works both with uncontrolled inputs (pass connId/schemaId element IDs) and
// controlled values (pass connString/schema). Gives instant onboarding feedback.
function TestConnButton({ connId, schemaId, connString, schema }) {
  const [status, setStatus] = useState(null); // { ok, msg }
  const [loading, setLoading] = useState(false);
  const run = async () => {
    const dbConnectionString = (connId ? document.getElementById(connId)?.value : connString || "").trim();
    const dbSchema = (schemaId ? document.getElementById(schemaId)?.value : schema || "").trim();
    if (!dbConnectionString) { setStatus({ ok: false, msg: "Enter a connection string first" }); return; }
    setLoading(true); setStatus(null);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/test-connection`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ dbConnectionString, dbSchema }),
      });
      const d = await res.json();
      if (d.ok) setStatus({ ok: true, msg: `Connected — ${d.tableCount} tables, ${Number(d.totalRows).toLocaleString()} rows in ${d.schemaChecked}` });
      else setStatus({ ok: false, msg: d.error || "Connection failed" });
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
      <button onClick={run} disabled={loading}
        style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 14px", color: T.textSecondary, cursor: loading ? "default" : "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {loading ? "Testing…" : "⚡ Test connection"}
      </button>
      {status && <span style={{ fontSize: 11, color: status.ok ? "#22c55e" : "#ef4444" }}>{status.ok ? "✓ " : "✗ "}{status.msg}</span>}
    </div>
  );
}

// ─── Admin: Data Sync freshness panel ─────────────────────────────────────────
function SyncStatusSection({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/sync-status`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}` },
      });
      const d = await res.json();
      if (d.ok) setData(d); else setErr(d.error || "Failed to read sync status");
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  const fmtAge = (h) => h == null ? "—" : h < 1 ? `${Math.round(h * 60)} min ago` : h < 48 ? `${h.toFixed(1)} h ago` : `${Math.round(h / 24)} d ago`;
  const fmtTs = (ts) => ts ? new Date(ts).toLocaleString() : "—";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Data Sync</p>
        <button onClick={load} disabled={loading}
          style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 12px", color: T.textSecondary, cursor: loading ? "default" : "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {loading ? "Checking…" : "↻ Check status"}
        </button>
      </div>
      {err && <p style={{ fontSize: 11, color: "#ef4444" }}>✗ {err}</p>}
      {data && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            {data.hasWatermarks ? (
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: data.stale ? "rgba(239,68,68,.15)" : "rgba(34,197,94,.15)", color: data.stale ? "#ef4444" : "#22c55e", fontWeight: 600 }}>
                {data.stale ? "⚠ Stale" : "✓ Fresh"} · data as of {fmtAge(data.ageHours)}
              </span>
            ) : (
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(148,163,184,.15)", color: T.textMuted }}>
                No sync watermark (not a ClientSync tenant)
              </span>
            )}
            <span style={{ fontSize: 11, color: T.textMuted }}>{data.tableCount} tables · {Number(data.totalRows).toLocaleString()} rows</span>
          </div>
          <div style={{ maxHeight: 220, overflow: "auto", border: `1px solid ${T.border}`, borderRadius: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ color: T.textMuted, textAlign: "left" }}>
                  <th style={{ padding: "6px 10px", fontWeight: 500 }}>Table</th>
                  <th style={{ padding: "6px 10px", fontWeight: 500, textAlign: "right" }}>Rows</th>
                  <th style={{ padding: "6px 10px", fontWeight: 500 }}>Watermark</th>
                  <th style={{ padding: "6px 10px", fontWeight: 500 }}>Last sync</th>
                </tr>
              </thead>
              <tbody>
                {data.tables.map((t) => (
                  <tr key={t.name} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ padding: "6px 10px", color: T.textPrimary, fontFamily: "monospace" }}>{t.name}</td>
                    <td style={{ padding: "6px 10px", color: T.textSecondary, textAlign: "right" }}>{Number(t.rows).toLocaleString()}</td>
                    <td style={{ padding: "6px 10px", color: t.lastValue ? T.textSecondary : T.textMuted }}>{t.lastValue || "—"}</td>
                    <td style={{ padding: "6px 10px", color: T.textMuted }}>{fmtTs(t.updatedAt)}</td>
                  </tr>
                ))}
                {data.tables.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>No tables in schema “{data.schema}”.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Projects (delivery tracker) ──────────────────────────────────────────────
// Agency→client work tracking. Power Vision BI staff manage projects across every
// client tenant; a client sees only their own projects — everything on them
// (milestones, tasks, timeline) is visible, they can comment, and they raise work
// via a request the agency triages. See pvbi-backend/src/app/lib/projects.ts.
//
// NOTE for admin use: "View as <client>" sends X-Impersonate-Tenant, which puts
// this section into the client's read-only view. That's the client preview; to
// manage projects, exit View As first.

const PRJ_STATUS = [
  { id: "planning",    label: "Planning",    color: "#8b949e" },
  { id: "in_progress", label: "In Progress", color: "#3b82f6" },
  { id: "review",      label: "In Review",   color: "#a78bfa" },
  { id: "blocked",     label: "Blocked",     color: "#ef4444" },
  { id: "on_hold",     label: "On Hold",     color: "#d29922" },
  { id: "completed",   label: "Completed",   color: "#2ea043" },
  { id: "cancelled",   label: "Cancelled",   color: "#484f58" },
];
const PRJ_OPEN_STATUS = ["planning", "in_progress", "review", "blocked", "on_hold"];
const PRJ_HEALTH = [
  { id: "on_track",  label: "On track",  color: "#2ea043" },
  { id: "at_risk",   label: "At risk",   color: "#d29922" },
  { id: "off_track", label: "Off track", color: "#ef4444" },
];
const PRJ_PRIORITY = [
  { id: "low",      label: "Low",      color: "#3b82f6" },
  { id: "normal",   label: "Normal",   color: "#8b949e" },
  { id: "high",     label: "High",     color: "#f59e0b" },
  { id: "critical", label: "Critical", color: "#ef4444" },
];
const PRJ_TASK_STATUS = [
  { id: "todo",        label: "To do",       color: "#8b949e" },
  { id: "in_progress", label: "In progress", color: "#3b82f6" },
  { id: "review",      label: "Review",      color: "#a78bfa" },
  // Ours vs theirs: "Review" is our internal check, "With client" is back with them.
  { id: "client_review", label: "With client", color: "#c47a10" },
  { id: "blocked",     label: "Blocked",     color: "#ef4444" },
  { id: "done",        label: "Done",        color: "#2ea043" },
];
const PRJ_MILESTONE_STATUS = [
  { id: "pending",     label: "Pending",     color: "#8b949e" },
  { id: "in_progress", label: "In progress", color: "#3b82f6" },
  { id: "done",        label: "Done",        color: "#2ea043" },
  { id: "blocked",     label: "Blocked",     color: "#ef4444" },
];
const PRJ_REQ_STATUS = [
  { id: "new",       label: "New",        color: "#3b82f6" },
  { id: "reviewing", label: "Reviewing",  color: "#a78bfa" },
  // "Reviewing" = PVBI is looking at it. "Ready for your review" = we're done and
  // it's back with the client. Very different messages; they need separate states.
  { id: "client_review", label: "Ready for your review", color: "#c47a10" },
  { id: "accepted",  label: "Accepted",   color: "#2ea043" },
  { id: "declined",  label: "Declined",   color: "#8b949e" },
  { id: "completed", label: "Completed",  color: "#2ea043" },
];
const PRJ_REQ_PRIORITY = [
  { id: "low",    label: "Low",    color: "#3b82f6" },
  { id: "normal", label: "Normal", color: "#8b949e" },
  { id: "high",   label: "High",   color: "#f59e0b" },
  { id: "urgent", label: "Urgent", color: "#ef4444" },
];
// Card accent palette offered when creating a project.
const PRJ_COLORS = ["#c47a10", "#3b82f6", "#2ea043", "#a78bfa", "#ec4899", "#06b6d4", "#f59e0b", "#ef4444"];

const prjMeta = (list, id) => list.find(x => x.id === id) || { id, label: id, color: "#8b949e" };

function projApi(path, opts = {}) {
  const imp = sessionStorage.getItem("pvbi_impersonate");
  return fetch(`${API_BASE}/projects${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}`,
      "Content-Type": "application/json",
      ...(imp ? { "X-Impersonate-Tenant": imp } : {}),
      ...(opts.headers || {}),
    },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || ("HTTP " + r.status)); return d; });
}

const prjDate = d => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const prjDateShort = d => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
const prjInput = d => d ? new Date(d).toISOString().slice(0, 10) : "";
// Whole days from now until `d` (negative = overdue). Compared at UTC midnight so
// the answer doesn't flip with the time of day.
const prjDaysLeft = d => {
  if (!d) return null;
  const day = 864e5;
  return Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / day);
};
const prjRelative = d => {
  if (!d) return "";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return prjDate(d);
};
const prjInitials = n => (n || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();

// ── Small shared pieces ───────────────────────────────────────────────────────

function PrjPill({ meta, size = 11 }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 20, padding: "3px 10px",
      fontSize: size, fontWeight: 600, letterSpacing: ".02em",
      background: `${meta.color}1f`, color: meta.color, border: `1px solid ${meta.color}40`, whiteSpace: "nowrap",
    }}>{meta.label}</span>
  );
}

function PrjDot({ color, size = 8, pulse = false }) {
  return <span style={{
    width: size, height: size, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block",
    boxShadow: `0 0 8px ${color}80`, animation: pulse ? "pulse2 2s ease-in-out infinite" : undefined,
  }} />;
}

function PrjProgress({ value, color = T.accent, height = 8, showLabel = false }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
      <div style={{ flex: 1, height, borderRadius: height, background: "rgba(255,255,255,.07)", overflow: "hidden" }}>
        <div style={{
          width: `${v}%`, height: "100%", borderRadius: height,
          background: `linear-gradient(90deg, ${color}, ${color}bb)`,
          boxShadow: v > 0 ? `0 0 12px ${color}70` : "none",
          transition: "width .5s cubic-bezier(.4,0,.2,1)",
        }} />
      </div>
      {showLabel && <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: v === 100 ? T.success : T.textSecondary, minWidth: 36, textAlign: "right" }}>{v}%</span>}
    </div>
  );
}

function PrjAvatar({ name, size = 24 }) {
  if (!name) return null;
  return (
    <div title={name} style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, color: "#fff",
    }}>{prjInitials(name)}</div>
  );
}

function PrjKpi({ label, value, sub, color, icon }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 18px",
      position: "relative", overflow: "hidden", animation: "fadeUp .4s ease both",
    }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: color }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {icon && <span style={{ fontSize: 13 }}>{icon}</span>}
        <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: T.textSecondary }}>{label}</p>
      </div>
      <p style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>{sub}</p>}
    </div>
  );
}

// Empty-state block used by every tab.
function PrjEmpty({ icon = "📋", title, hint, action }) {
  return (
    <div style={{ textAlign: "center", padding: "56px 24px", color: T.textMuted }}>
      <div style={{ fontSize: 34, marginBottom: 12, opacity: .7 }}>{icon}</div>
      <p style={{ fontSize: 14, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>{title}</p>
      {hint && <p style={{ fontSize: 12.5, maxWidth: 400, margin: "0 auto 16px" }}>{hint}</p>}
      {action}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

function ProjectsApp({ user }) {
  const [meta, setMeta] = useState(null);
  const [projects, setProjects] = useState(null);
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState(null);

  const [search, setSearch] = useState("");
  const [fClient, setFClient] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [layout, setLayout] = useState("grid");     // grid | board | table
  const [showNew, setShowNew] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showInbox, setShowInbox] = useState(false);

  const isAgency = !!meta?.isAgency;

  const loadProjects = () => projApi(`/${showArchived ? "?archived=1" : ""}`)
    .then(d => { setProjects(d.projects); setSummary(d.summary); })
    .catch(e => setErr(e.message));
  const loadRequests = () => projApi("/requests?status=open").then(d => setRequests(d.requests || [])).catch(() => {});

  useEffect(() => { projApi("/meta").then(setMeta).catch(e => setErr(e.message)); }, []);
  useEffect(() => { loadProjects(); loadRequests(); }, [showArchived]); // eslint-disable-line

  const refresh = () => { loadProjects(); loadRequests(); };

  const view = (projects || []).filter(p => {
    if (fClient && p.tenantId !== fClient) return false;
    if (fStatus === "open" ? !PRJ_OPEN_STATUS.includes(p.status) : fStatus && p.status !== fStatus) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (![p.name, p.code, p.clientName, p.description].some(v => (v || "").toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const openProject = view.find(p => p.id === openId) || (projects || []).find(p => p.id === openId);

  if (err && !projects) {
    return <div style={{ flex: 1, padding: 32 }}><div className="alert alert-error">{err}</div></div>;
  }
  if (!projects || !meta) {
    return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="spinner spin" style={{ width: 24, height: 24, borderTopColor: T.accent }} />
    </div>;
  }

  if (openId && openProject) {
    return <ProjectDetail
      projectId={openId} meta={meta} isAgency={isAgency}
      onBack={() => { setOpenId(null); refresh(); }}
      onChanged={refresh}
    />;
  }

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }} className="mob-pad">
      {/* Header */}
      <div className="mob-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Projects</h1>
          <p style={{ fontSize: 13, color: T.textSecondary }}>
            {isAgency ? "Everything Power Vision BI is building, across every client" : "What Power Vision BI is building for you"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {isAgency && requests.length > 0 && (
            <button className="btn-secondary" onClick={() => setShowInbox(true)} style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span>📥 Requests</span>
              <span style={{ background: T.accent, color: "#fff", borderRadius: 10, fontSize: 10.5, fontWeight: 700, padding: "1px 7px" }}>{requests.length}</span>
            </button>
          )}
          <button className="btn-secondary" onClick={() => setShowRequest(true)}>
            {isAgency ? "＋ Log request" : "＋ Request work"}
          </button>
          {isAgency && (
            <button className="btn-primary" style={{ width: "auto", padding: "9px 18px", fontSize: 13 }} onClick={() => setShowNew(true)}>
              ＋ New Project
            </button>
          )}
        </div>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: 16 }}>{err}<button className="btn-ghost" style={{ marginLeft: "auto" }} onClick={() => setErr(null)}>Dismiss</button></div>}

      {/* Client-side request inbox summary */}
      {!isAgency && requests.length > 0 && (
        <div className="alert alert-info" style={{ marginBottom: 18 }}>
          <span>You have {requests.length} open {requests.length === 1 ? "request" : "requests"} with Power Vision BI.</span>
          <button className="btn-ghost" style={{ marginLeft: "auto" }} onClick={() => setShowInbox(true)}>View</button>
        </div>
      )}

      {/* KPIs */}
      {summary && (
        <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 22 }}>
          <PrjKpi label="Active" value={summary.active} sub={`${summary.completed} completed`} color={T.accent} icon="🚀" />
          <PrjKpi label="On track" value={summary.onTrack} sub={`of ${summary.active} active`} color="#2ea043" icon="✅" />
          <PrjKpi label="Needs attention" value={summary.atRisk + summary.offTrack} sub={`${summary.atRisk} at risk · ${summary.offTrack} off track`} color={summary.atRisk + summary.offTrack ? "#d29922" : T.textSecondary} icon="⚠️" />
          <PrjKpi label="Due in 30 days" value={summary.dueSoon} sub={summary.overdue ? `${summary.overdue} overdue` : "none overdue"} color={summary.overdue ? "#ef4444" : "#3b82f6"} icon="📅" />
          <PrjKpi label="Avg progress" value={`${summary.avgProgress}%`} sub={`${summary.openTasks} open tasks`} color="#a78bfa" icon="📊" />
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
        <input className="field-input" placeholder="Search projects…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: 220, padding: "8px 12px", fontSize: 13 }} />
        {isAgency && (
          <select className="field-input" value={fClient} onChange={e => setFClient(e.target.value)} style={{ width: "auto", padding: "8px 12px", fontSize: 13, background: T.surface }}>
            <option value="">All clients</option>
            {meta.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select className="field-input" value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ width: "auto", padding: "8px 12px", fontSize: 13, background: T.surface }}>
          <option value="">All statuses</option>
          <option value="open">Active only</option>
          {PRJ_STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div style={{ display: "flex", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 2 }}>
          {[["grid", "▦ Grid"], ["board", "▤ Board"], ["table", "☰ List"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setLayout(id)} style={{
              background: layout === id ? T.accentGlow : "transparent", border: "none", cursor: "pointer",
              color: layout === id ? T.accent : T.textSecondary, fontSize: 12, fontWeight: 600,
              padding: "6px 12px", borderRadius: 6, fontFamily: "'Plus Jakarta Sans',sans-serif",
            }}>{lbl}</button>
          ))}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSecondary, cursor: "pointer" }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.textMuted }}>{view.length} {view.length === 1 ? "project" : "projects"}</span>
      </div>

      {/* Body */}
      {view.length === 0 ? (
        <PrjEmpty
          icon="🗂️"
          title={projects.length ? "No projects match those filters" : (isAgency ? "No projects yet" : "No projects to show yet")}
          hint={projects.length ? "Try clearing the search or filters." : (isAgency ? "Create your first project and pick the client it's for." : "Power Vision BI will publish work here as it kicks off.")}
          action={isAgency && !projects.length ? <button className="btn-primary" style={{ width: "auto", padding: "9px 20px", fontSize: 13 }} onClick={() => setShowNew(true)}>＋ New Project</button> : null}
        />
      ) : layout === "board" ? (
        <ProjectBoard projects={view} isAgency={isAgency} onOpen={setOpenId} onMove={async (id, status) => {
          setProjects(prev => prev.map(p => p.id === id ? { ...p, status } : p));
          try { await projApi(`/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }); refresh(); }
          catch (e) { setErr(e.message); loadProjects(); }
        }} />
      ) : layout === "table" ? (
        <ProjectTable projects={view} isAgency={isAgency} onOpen={setOpenId} />
      ) : (
        <div className="reports-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 16 }}>
          {view.map((p, i) => <ProjectCard key={p.id} p={p} isAgency={isAgency} idx={i} onOpen={() => setOpenId(p.id)} />)}
        </div>
      )}

      {showNew && <NewProjectModal meta={meta} onClose={() => setShowNew(false)} onSaved={id => { setShowNew(false); refresh(); setOpenId(id); }} />}
      {showRequest && <NewRequestModal meta={meta} projects={projects} onClose={() => setShowRequest(false)} onSaved={() => { setShowRequest(false); refresh(); }} />}
      {showInbox && <RequestInbox meta={meta} isAgency={isAgency} projects={projects} onClose={() => { setShowInbox(false); refresh(); }} onOpenProject={id => { setShowInbox(false); setOpenId(id); }} />}
    </div>
  );
}

// ── List presentations ────────────────────────────────────────────────────────

function ProjectCard({ p, isAgency, idx, onOpen }) {
  const st = prjMeta(PRJ_STATUS, p.status);
  const hl = prjMeta(PRJ_HEALTH, p.health);
  const accent = p.color || st.color;
  const days = prjDaysLeft(p.targetDate);
  const overdue = days !== null && days < 0 && p.status !== "completed";
  const [hover, setHover] = useState(false);

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: T.surface, border: `1px solid ${hover ? accent : T.border}`, borderRadius: 14,
        padding: 0, cursor: "pointer", overflow: "hidden", position: "relative",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? `0 12px 32px ${accent}22` : "none",
        transition: "transform .2s, box-shadow .2s, border-color .2s",
        animation: "fadeUp .4s ease both", animationDelay: `${Math.min(idx, 8) * 0.04}s`,
        opacity: p.archived ? 0.6 : 1,
      }}
    >
      <div style={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}30)` }} />
      <div style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
          <div style={{ minWidth: 0 }}>
            {isAgency && <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: accent, marginBottom: 5 }}>{p.clientName}</p>}
            <p style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.name}</p>
            {p.code && <p className="mono" style={{ fontSize: 10.5, color: T.textMuted, marginTop: 4 }}>{p.code}</p>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
            <PrjPill meta={st} />
            {PRJ_OPEN_STATUS.includes(p.status) && (
              <span title={hl.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, color: hl.color, fontWeight: 600 }}>
                <PrjDot color={hl.color} size={7} pulse={p.health !== "on_track"} />{hl.label}
              </span>
            )}
          </div>
        </div>

        {p.description && (
          <p style={{ fontSize: 12.5, color: T.textSecondary, lineHeight: 1.5, marginBottom: 14, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.description}</p>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 500 }}>
              {/* Milestones drive the bar, so that's what the caption reports — a
                  task count next to a milestone-derived % would just mislead. */}
              {p.milestonesTotal > 0 ? `${p.milestonesDone} of ${p.milestonesTotal} milestones` : "No milestones yet"}
            </span>
            <span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: p.progress === 100 ? T.success : accent }}>{p.progress}%</span>
          </div>
          <PrjProgress value={p.progress} color={p.progress === 100 ? T.success : accent} />
        </div>

        {p.nextMilestone && p.status !== "completed" && (
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, padding: "7px 10px", background: "rgba(255,255,255,.03)", borderRadius: 7, border: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 11 }}>🎯</span>
            <span style={{ fontSize: 11.5, color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{p.nextMilestone.title}</span>
            {p.nextMilestone.dueDate && <span className="mono" style={{ fontSize: 10.5, color: T.textMuted, flexShrink: 0 }}>{prjDateShort(p.nextMilestone.dueDate)}</span>}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11, color: T.textMuted, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
          {p.ownerName && <PrjAvatar name={p.ownerName} size={22} />}
          {p.targetDate && (
            <span style={{ display: "flex", alignItems: "center", gap: 4, color: overdue ? T.error : T.textMuted, fontWeight: overdue ? 600 : 400 }}>
              📅 {overdue ? `${Math.abs(days)}d overdue` : days === null ? prjDateShort(p.targetDate) : days === 0 ? "Due today" : `${days}d left`}
            </span>
          )}
          <span style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            {p.commentCount > 0 && <span title="Comments">💬 {p.commentCount}</span>}
            {p.openRequests > 0 && <span title="Open requests" style={{ color: T.accent, fontWeight: 600 }}>📥 {p.openRequests}</span>}
          </span>
        </div>
      </div>
    </div>
  );
}

function ProjectBoard({ projects, isAgency, onOpen, onMove }) {
  const [dragId, setDragId] = useState(null);
  const cols = PRJ_STATUS.filter(s => s.id !== "cancelled" || projects.some(p => p.status === "cancelled"));
  return (
    <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 12, alignItems: "flex-start" }}>
      {cols.map(col => {
        const items = projects.filter(p => p.status === col.id);
        return (
          <div key={col.id}
            onDragOver={e => { if (isAgency) e.preventDefault(); }}
            onDrop={() => { if (isAgency && dragId) { onMove(dragId, col.id); setDragId(null); } }}
            style={{ minWidth: 270, width: 270, flexShrink: 0, background: "rgba(255,255,255,.02)", border: `1px solid ${T.border}`, borderRadius: 12, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px 10px" }}>
              <PrjDot color={col.color} size={7} />
              <span style={{ fontSize: 12, fontWeight: 600 }}>{col.label}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: T.textMuted, background: "rgba(255,255,255,.05)", borderRadius: 10, padding: "1px 7px" }}>{items.length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 40 }}>
              {items.map(p => (
                <div key={p.id} draggable={isAgency} onDragStart={() => setDragId(p.id)} onClick={() => onOpen(p.id)}
                  style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: 11, cursor: "pointer", opacity: dragId === p.id ? .45 : 1 }}>
                  {isAgency && <p style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: p.color || col.color, marginBottom: 4 }}>{p.clientName}</p>}
                  <p style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, marginBottom: 8 }}>{p.name}</p>
                  <PrjProgress value={p.progress} color={p.color || col.color} height={5} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9, fontSize: 10.5, color: T.textMuted }}>
                    <span className="mono">{p.progress}%</span>
                    {p.targetDate && <span>· {prjDateShort(p.targetDate)}</span>}
                    {p.ownerName && <span style={{ marginLeft: "auto" }}><PrjAvatar name={p.ownerName} size={18} /></span>}
                  </div>
                </div>
              ))}
              {!items.length && <p style={{ fontSize: 11, color: T.textMuted, textAlign: "center", padding: "14px 0" }}>—</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProjectTable({ projects, isAgency, onOpen }) {
  const th = { textAlign: "left", fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.textSecondary, padding: "10px 12px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "12px", borderBottom: `1px solid ${T.border}`, fontSize: 12.5, verticalAlign: "middle" };
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <thead><tr>
          <th style={th}>Project</th>
          {isAgency && <th style={th}>Client</th>}
          <th style={th}>Status</th>
          <th style={th}>Health</th>
          <th style={{ ...th, width: 170 }}>Progress</th>
          <th style={th}>Target</th>
          <th style={th}>Lead</th>
        </tr></thead>
        <tbody>
          {projects.map(p => {
            const st = prjMeta(PRJ_STATUS, p.status), hl = prjMeta(PRJ_HEALTH, p.health);
            const days = prjDaysLeft(p.targetDate);
            const overdue = days !== null && days < 0 && p.status !== "completed";
            return (
              <tr key={p.id} onClick={() => onOpen(p.id)} style={{ cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,.03)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={td}>
                  <p style={{ fontWeight: 600 }}>{p.name}</p>
                  {p.code && <p className="mono" style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>{p.code}</p>}
                </td>
                {isAgency && <td style={{ ...td, color: T.textSecondary }}>{p.clientName}</td>}
                <td style={td}><PrjPill meta={st} /></td>
                <td style={td}><span style={{ display: "flex", alignItems: "center", gap: 6, color: hl.color, fontSize: 12 }}><PrjDot color={hl.color} size={7} />{hl.label}</span></td>
                <td style={td}><PrjProgress value={p.progress} color={p.color || st.color} height={6} showLabel /></td>
                <td style={{ ...td, color: overdue ? T.error : T.textSecondary, fontWeight: overdue ? 600 : 400, whiteSpace: "nowrap" }}>{prjDateShort(p.targetDate)}</td>
                <td style={td}>{p.ownerName ? <span style={{ display: "flex", alignItems: "center", gap: 7 }}><PrjAvatar name={p.ownerName} size={20} /><span style={{ color: T.textSecondary }}>{p.ownerName}</span></span> : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────────

function ProjectDetail({ projectId, meta, isAgency, onBack, onChanged }) {
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () => projApi(`/${projectId}`).then(d => setP(d.project)).catch(e => setErr(e.message));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const patch = async (body) => {
    try { await projApi(`/${projectId}`, { method: "PATCH", body: JSON.stringify(body) }); await load(); onChanged?.(); }
    catch (e) { setErr(e.message); }
  };

  if (!p) {
    return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {err ? <div className="alert alert-error">{err}</div> : <span className="spinner spin" style={{ width: 24, height: 24, borderTopColor: T.accent }} />}
    </div>;
  }

  const st = prjMeta(PRJ_STATUS, p.status);
  const hl = prjMeta(PRJ_HEALTH, p.health);
  const accent = p.color || st.color;
  const days = prjDaysLeft(p.targetDate);
  const overdue = days !== null && days < 0 && p.status !== "completed";
  const openRequests = (p.requests || []).filter(r => r.status === "new" || r.status === "reviewing").length;
  const milestoneCount = (p.milestones || []).length;
  const milestonesDone = (p.milestones || []).filter(m => m.status === "done").length;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "tasks", label: `Tasks${p.tasks?.length ? ` (${p.tasks.filter(t => t.status !== "done").length})` : ""}` },
    { id: "discussion", label: `Discussion${p.comments?.length ? ` (${p.comments.length})` : ""}` },
    { id: "files", label: `Files${p.files?.length ? ` (${p.files.length})` : ""}` },
    { id: "requests", label: `Requests${openRequests ? ` (${openRequests})` : ""}` },
    { id: "activity", label: "Activity" },
  ];

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 32 }} className="mob-pad">
      <button className="btn-ghost" onClick={onBack} style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>← All projects</button>

      {err && <div className="alert alert-error" style={{ marginBottom: 16 }}>{err}<button className="btn-ghost" style={{ marginLeft: "auto" }} onClick={() => setErr(null)}>Dismiss</button></div>}

      {/* Hero */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${accent}, ${accent}25)` }} />
        <div style={{ padding: 24 }}>
          <div className="mob-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: accent }}>{p.clientName}</span>
                {p.code && <span className="mono" style={{ fontSize: 11, color: T.textMuted }}>{p.code}</span>}
                {p.archived && <PrjPill meta={{ label: "Archived", color: T.textMuted }} />}
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.25, marginBottom: 10 }}>{p.name}</h1>
              {p.description && <p style={{ fontSize: 13.5, color: T.textSecondary, lineHeight: 1.6, maxWidth: 680 }}>{p.description}</p>}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
              {isAgency && <button className="btn-secondary" onClick={() => setEditing(true)}>✎ Edit</button>}
            </div>
          </div>

          {/* Status controls */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
            {isAgency ? (
              <>
                <select className="field-input" value={p.status} onChange={e => patch({ status: e.target.value })}
                  style={{ width: "auto", padding: "7px 12px", fontSize: 12.5, background: T.surface, color: st.color, fontWeight: 600, borderColor: `${st.color}55` }}>
                  {PRJ_STATUS.map(s => <option key={s.id} value={s.id} style={{ color: T.textPrimary }}>{s.label}</option>)}
                </select>
                <select className="field-input" value={p.health} onChange={e => patch({ health: e.target.value })}
                  style={{ width: "auto", padding: "7px 12px", fontSize: 12.5, background: T.surface, color: hl.color, fontWeight: 600, borderColor: `${hl.color}55` }}>
                  {PRJ_HEALTH.map(s => <option key={s.id} value={s.id} style={{ color: T.textPrimary }}>{s.label}</option>)}
                </select>
              </>
            ) : (
              <>
                <PrjPill meta={st} size={12} />
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: hl.color, fontWeight: 600 }}><PrjDot color={hl.color} size={8} />{hl.label}</span>
              </>
            )}
            <PrjPill meta={prjMeta(PRJ_PRIORITY, p.priority)} />
          </div>

          {/* Progress — derived from completed milestones, nothing else. */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: T.textSecondary }}>Progress</span>
              <span className="mono" style={{ fontSize: 20, fontWeight: 700, color: p.progress === 100 ? T.success : accent }}>{p.progress}%</span>
            </div>
            <PrjProgress value={p.progress} color={p.progress === 100 ? T.success : accent} height={10} />
            <p style={{ fontSize: 11.5, color: T.textMuted, marginTop: 8 }}>
              {milestoneCount === 0
                ? (isAgency ? "Add milestones on the Overview tab — completing them moves this bar." : "No milestones published yet.")
                : `${milestonesDone} of ${milestoneCount} milestones complete`}
            </p>
          </div>

          {/* Facts */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 14, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
            <Fact label="Start" value={prjDate(p.startDate)} />
            <Fact label="Target" value={prjDate(p.targetDate)} color={overdue ? T.error : undefined}
              sub={days !== null && p.status !== "completed" ? (overdue ? `${Math.abs(days)} days overdue` : days === 0 ? "due today" : `${days} days left`) : null} />
            <Fact label="PVBI lead" value={p.ownerName || "—"} />
            <Fact label="Client contact" value={p.clientLead || "—"} />
            <Fact label="Milestones" value={`${(p.milestones || []).filter(m => m.status === "done").length} / ${(p.milestones || []).length}`} />
            <Fact label="Tasks" value={`${(p.tasks || []).filter(t => t.status === "done").length} / ${(p.tasks || []).length}`} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 20, overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: "none", border: "none", borderBottom: `2px solid ${tab === t.id ? T.accent : "transparent"}`,
            color: tab === t.id ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 13, fontWeight: 600,
            padding: "10px 16px", fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: "nowrap", marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "overview" && <MilestoneTimeline p={p} isAgency={isAgency} accent={accent} onChanged={() => { load(); onChanged?.(); }} setErr={setErr} />}
      {tab === "tasks" && <TaskSection p={p} meta={meta} isAgency={isAgency} onChanged={() => { load(); onChanged?.(); }} setErr={setErr} />}
      {tab === "discussion" && <Discussion p={p} onChanged={() => { load(); onChanged?.(); }} setErr={setErr} />}
      {tab === "files" && <ProjectFiles p={p} isAgency={isAgency} meId={meta?.me?.id} onChanged={() => { load(); onChanged?.(); }} setErr={setErr} />}
      {tab === "requests" && <ProjectRequests p={p} meta={meta} isAgency={isAgency} onChanged={() => { load(); onChanged?.(); }} setErr={setErr} />}
      {tab === "activity" && <ActivityFeed items={p.activities || []} />}

      {editing && <EditProjectModal p={p} meta={meta} onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); load(); onChanged?.(); }}
        onDeleted={() => { setEditing(false); onBack(); }} />}
    </div>
  );
}

function Fact({ label, value, sub, color }) {
  return (
    <div>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.textMuted, marginBottom: 5 }}>{label}</p>
      <p style={{ fontSize: 13, fontWeight: 500, color: color || T.textPrimary }}>{value}</p>
      {sub && <p style={{ fontSize: 11, color: color || T.textMuted, marginTop: 2 }}>{sub}</p>}
    </div>
  );
}

// ── Overview: milestone timeline ──────────────────────────────────────────────

function MilestoneTimeline({ p, isAgency, accent, onChanged, setErr }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  const ms = p.milestones || [];

  const add = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await projApi("/milestones", { method: "POST", body: JSON.stringify({ projectId: p.id, title, dueDate: due || null }) });
      setTitle(""); setDue(""); setAdding(false); onChanged();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  const setStatus = async (m, status) => {
    try { await projApi(`/milestones?id=${m.id}`, { method: "PATCH", body: JSON.stringify({ status }) }); onChanged(); }
    catch (e) { setErr(e.message); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete milestone "${m.title}"?`)) return;
    try { await projApi(`/milestones?id=${m.id}`, { method: "DELETE" }); onChanged(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 16 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ms.length ? 20 : 0 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Milestones</h3>
          {isAgency && !adding && <button className="btn-ghost" onClick={() => setAdding(true)}>＋ Add milestone</button>}
        </div>

        {!ms.length && !adding && (
          <PrjEmpty icon="🎯" title="No milestones yet"
            hint={isAgency ? "Milestones drive the progress bar — add the key stages of this project." : "Power Vision BI hasn't published milestones for this project yet."}
            action={isAgency ? <button className="btn-primary" style={{ width: "auto", padding: "9px 20px", fontSize: 13 }} onClick={() => setAdding(true)}>＋ Add milestone</button> : null} />
        )}

        {ms.length > 0 && (
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", left: 11, top: 10, bottom: 10, width: 2, background: T.border }} />
            {ms.map((m, i) => {
              const st = prjMeta(PRJ_MILESTONE_STATUS, m.status);
              const days = prjDaysLeft(m.dueDate);
              const late = days !== null && days < 0 && m.status !== "done";
              return (
                <div key={m.id} style={{ display: "flex", gap: 14, position: "relative", paddingBottom: i === ms.length - 1 ? 0 : 18 }}>
                  <button
                    onClick={() => isAgency && setStatus(m, m.status === "done" ? "pending" : "done")}
                    disabled={!isAgency}
                    title={isAgency ? (m.status === "done" ? "Mark not done" : "Mark done") : st.label}
                    style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0, zIndex: 1,
                      background: m.status === "done" ? st.color : T.surface,
                      border: `2px solid ${st.color}`, cursor: isAgency ? "pointer" : "default",
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                    }}>
                    {m.status === "done" && <Icon n="check" size={12} color="#fff" />}
                    {m.status === "in_progress" && <span style={{ width: 8, height: 8, borderRadius: "50%", background: st.color }} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, textDecoration: m.status === "done" ? "line-through" : "none", color: m.status === "done" ? T.textMuted : T.textPrimary }}>{m.title}</span>
                      <PrjPill meta={st} size={10} />
                      {m.dueDate && <span className="mono" style={{ fontSize: 11, color: late ? T.error : T.textMuted, fontWeight: late ? 600 : 400 }}>{prjDate(m.dueDate)}{late ? ` · ${Math.abs(days)}d late` : ""}</span>}
                      {isAgency && (
                        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                          <select value={m.status} onChange={e => setStatus(m, e.target.value)}
                            style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.textSecondary, fontSize: 11, padding: "3px 6px", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                            {PRJ_MILESTONE_STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                          </select>
                          <button onClick={() => remove(m)} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2 }}>
                            <Icon n="trash" size={13} />
                          </button>
                        </span>
                      )}
                    </div>
                    {m.description && <p style={{ fontSize: 12, color: T.textSecondary, marginTop: 4, lineHeight: 1.5 }}>{m.description}</p>}
                    {(p.tasks || []).filter(t => t.milestoneId === m.id).length > 0 && (
                      <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                        {(p.tasks || []).filter(t => t.milestoneId === m.id && t.status === "done").length} of {(p.tasks || []).filter(t => t.milestoneId === m.id).length} tasks done
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {adding && (
          <div style={{ marginTop: 18, padding: 14, background: "rgba(255,255,255,.03)", border: `1px solid ${T.border}`, borderRadius: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input className="field-input" autoFocus placeholder="Milestone title" value={title} onChange={e => setTitle(e.target.value)}
              onKeyDown={e => e.key === "Enter" && add()} style={{ flex: "1 1 220px", padding: "8px 12px", fontSize: 13 }} />
            <input className="field-input" type="date" value={due} onChange={e => setDue(e.target.value)} style={{ width: 160, padding: "8px 12px", fontSize: 13 }} />
            <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 13 }} disabled={saving || !title.trim()} onClick={add}>Add</button>
            <button className="btn-secondary" onClick={() => { setAdding(false); setTitle(""); setDue(""); }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

function TaskSection({ p, meta, isAgency, onChanged, setErr }) {
  const [layout, setLayout] = useState("board");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", milestoneId: "", priority: "normal", assigneeId: "", dueDate: "" });
  const [dragId, setDragId] = useState(null);
  const [msgTask, setMsgTask] = useState(null);
  const tasks = p.tasks || [];

  const add = async () => {
    if (!form.title.trim()) return;
    const assignee = (meta.roster || []).find(u => u.id === form.assigneeId);
    try {
      await projApi("/tasks", { method: "POST", body: JSON.stringify({
        projectId: p.id, title: form.title, milestoneId: form.milestoneId || null,
        priority: form.priority, assigneeId: form.assigneeId || null,
        assigneeName: assignee ? (assignee.name || assignee.email) : null,
        dueDate: form.dueDate || null,
      }) });
      setForm({ title: "", milestoneId: "", priority: "normal", assigneeId: "", dueDate: "" });
      setAdding(false); onChanged();
    } catch (e) { setErr(e.message); }
  };
  const move = async (id, status) => {
    try { await projApi(`/tasks?id=${id}`, { method: "PATCH", body: JSON.stringify({ status }) }); onChanged(); }
    catch (e) { setErr(e.message); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try { await projApi(`/tasks?id=${t.id}`, { method: "DELETE" }); onChanged(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 2 }}>
          {[["board", "▤ Board"], ["list", "☰ List"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setLayout(id)} style={{
              background: layout === id ? T.accentGlow : "transparent", border: "none", cursor: "pointer",
              color: layout === id ? T.accent : T.textSecondary, fontSize: 12, fontWeight: 600,
              padding: "6px 12px", borderRadius: 6, fontFamily: "'Plus Jakarta Sans',sans-serif",
            }}>{lbl}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: T.textMuted }}>{tasks.filter(t => t.status === "done").length} of {tasks.length} done</span>
        {isAgency && <button className="btn-secondary" style={{ marginLeft: "auto" }} onClick={() => setAdding(a => !a)}>＋ Add task</button>}
      </div>

      {adding && isAgency && (
        <div style={{ marginBottom: 16, padding: 16, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input className="field-input" autoFocus placeholder="What needs doing?" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })} onKeyDown={e => e.key === "Enter" && add()}
            style={{ flex: "1 1 240px", padding: "8px 12px", fontSize: 13 }} />
          <select className="field-input" value={form.milestoneId} onChange={e => setForm({ ...form, milestoneId: e.target.value })} style={{ width: "auto", padding: "8px 12px", fontSize: 13, background: T.bg }}>
            <option value="">No milestone</option>
            {(p.milestones || []).map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
          <select className="field-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ width: "auto", padding: "8px 12px", fontSize: 13, background: T.bg }}>
            {PRJ_PRIORITY.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select className="field-input" value={form.assigneeId} onChange={e => setForm({ ...form, assigneeId: e.target.value })} style={{ width: "auto", padding: "8px 12px", fontSize: 13, background: T.bg }}>
            <option value="">Unassigned</option>
            {(meta.roster || []).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
          <input className="field-input" type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} style={{ width: 155, padding: "8px 12px", fontSize: 13 }} />
          <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 13 }} disabled={!form.title.trim()} onClick={add}>Add</button>
        </div>
      )}

      {!tasks.length ? (
        <PrjEmpty icon="✅" title="No tasks yet"
          hint={isAgency ? "Break the work down so the client can see exactly what's moving." : "No task-level detail has been published yet."} />
      ) : layout === "board" ? (
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 12, alignItems: "flex-start" }}>
          {PRJ_TASK_STATUS.map(col => {
            const items = tasks.filter(t => t.status === col.id);
            return (
              <div key={col.id}
                onDragOver={e => { if (isAgency) e.preventDefault(); }}
                onDrop={() => { if (isAgency && dragId) { move(dragId, col.id); setDragId(null); } }}
                style={{ minWidth: 240, width: 240, flexShrink: 0, background: "rgba(255,255,255,.02)", border: `1px solid ${T.border}`, borderRadius: 12, padding: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px 10px" }}>
                  <PrjDot color={col.color} size={7} />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{col.label}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: T.textMuted }}>{items.length}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 30 }}>
                  {items.map(t => <TaskCard key={t.id} t={t} p={p} isAgency={isAgency} onDragStart={() => setDragId(t.id)} dragging={dragId === t.id} onRemove={() => remove(t)} onMessage={() => setMsgTask(t)} />)}
                  {!items.length && <p style={{ fontSize: 11, color: T.textMuted, textAlign: "center", padding: "10px 0" }}>—</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
          {tasks.map((t, i) => {
            const st = prjMeta(PRJ_TASK_STATUS, t.status);
            const pr = prjMeta(PRJ_PRIORITY, t.priority);
            const days = prjDaysLeft(t.dueDate);
            const late = days !== null && days < 0 && t.status !== "done";
            return (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: i === tasks.length - 1 ? "none" : `1px solid ${T.border}` }}>
                <button onClick={() => isAgency && move(t.id, t.status === "done" ? "todo" : "done")} disabled={!isAgency}
                  style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, background: t.status === "done" ? T.success : "transparent", border: `1.5px solid ${t.status === "done" ? T.success : T.borderHover}`, cursor: isAgency ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                  {t.status === "done" && <Icon n="check" size={11} color="#fff" />}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, textDecoration: t.status === "done" ? "line-through" : "none", color: t.status === "done" ? T.textMuted : T.textPrimary }}>{t.title}</p>
                  {t.milestoneId && <p style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>🎯 {(p.milestones || []).find(m => m.id === t.milestoneId)?.title}</p>}
                </div>
                <PrjDot color={pr.color} size={7} />
                {isAgency ? (
                  <select value={t.status} onChange={e => move(t.id, e.target.value)}
                    style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: st.color, fontSize: 11, fontWeight: 600, padding: "3px 6px", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    {PRJ_TASK_STATUS.map(s => <option key={s.id} value={s.id} style={{ color: T.textPrimary }}>{s.label}</option>)}
                  </select>
                ) : <PrjPill meta={st} size={10} />}
                {t.dueDate && <span className="mono" style={{ fontSize: 11, color: late ? T.error : T.textMuted, minWidth: 58, textAlign: "right" }}>{prjDateShort(t.dueDate)}</span>}
                {t.assigneeName && <PrjAvatar name={t.assigneeName} size={22} />}
                {isAgency && <button onClick={() => setMsgTask(t)} title="Send an update to the client" style={{ background: "none", border: "none", cursor: "pointer", color: T.accent, padding: 2, fontSize: 13 }}>✉</button>}
                {isAgency && <button onClick={() => remove(t)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2 }}><Icon n="trash" size={13} /></button>}
              </div>
            );
          })}
        </div>
      )}

      {msgTask && <TaskClientUpdate t={msgTask} p={p} setErr={setErr}
        onClose={() => setMsgTask(null)}
        onSent={() => { setMsgTask(null); onChanged(); }} />}
    </div>
  );
}

// Hand one task back to the client with a note. Writes a comment tied to THIS task
// and emails the client in a single call, so the mail names the piece of work
// rather than reading as a generic "someone commented".
function TaskClientUpdate({ t, p, onClose, onSent, setErr }) {
  const [msg, setMsg] = useState("");
  const [toReview, setToReview] = useState(t.status !== "client_review");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await projApi(`/tasks?id=${t.id}`, { method: "PATCH", body: JSON.stringify({
        clientMessage: msg,
        ...(toReview ? { status: "client_review" } : {}),
      }) });
      onSent();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <PrjModal title="Send an update to the client" subtitle={t.title} onClose={onClose} width={560}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ width: "auto", padding: "10px 22px", fontSize: 13.5 }}
          disabled={busy || !msg.trim()} onClick={send}>{busy ? "Sending…" : "Send update"}</button>
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="field-group">
          <label className="field-label">Your message</label>
          <textarea className="field-input" rows={5} autoFocus value={msg} onChange={e => setMsg(e.target.value)}
            placeholder="What you've done, and what you'd like them to check…" style={{ resize: "vertical" }} />
          <p style={{ fontSize: 11, color: T.textMuted }}>
            Emailed to everyone at {p.clientName} involved in this project, and saved to the discussion.
          </p>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 13, color: T.textSecondary, cursor: "pointer" }}>
          <input type="checkbox" checked={toReview} onChange={e => setToReview(e.target.checked)} style={{ marginTop: 2 }} />
          <span>Move this task to <strong style={{ color: T.accent }}>With client</strong> so they can see it's waiting on them</span>
        </label>
      </div>
    </PrjModal>
  );
}

function TaskCard({ t, p, isAgency, onDragStart, dragging, onRemove, onMessage }) {
  const pr = prjMeta(PRJ_PRIORITY, t.priority);
  const days = prjDaysLeft(t.dueDate);
  const late = days !== null && days < 0 && t.status !== "done";
  return (
    <div draggable={isAgency} onDragStart={onDragStart}
      style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: 11, opacity: dragging ? .45 : 1, cursor: isAgency ? "grab" : "default" }}>
      <div style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
        <PrjDot color={pr.color} size={7} />
        <p style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.4, flex: 1 }}>{t.title}</p>
        {isAgency && <button onClick={onMessage} title="Send an update to the client" style={{ background: "none", border: "none", cursor: "pointer", color: T.accent, padding: 0, flexShrink: 0, fontSize: 12 }}>✉</button>}
        {isAgency && <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 0, flexShrink: 0 }}><Icon n="trash" size={12} /></button>}
      </div>
      {t.milestoneId && <p style={{ fontSize: 10, color: T.textMuted, marginTop: 6 }}>🎯 {(p.milestones || []).find(m => m.id === t.milestoneId)?.title}</p>}
      {(t.dueDate || t.assigneeName) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9, fontSize: 10.5, color: late ? T.error : T.textMuted }}>
          {t.dueDate && <span className="mono">{prjDateShort(t.dueDate)}</span>}
          {t.assigneeName && <span style={{ marginLeft: "auto" }}><PrjAvatar name={t.assigneeName} size={18} /></span>}
        </div>
      )}
    </div>
  );
}

// ── Discussion ────────────────────────────────────────────────────────────────

function Discussion({ p, onChanged, setErr }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const comments = p.comments || [];
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [comments.length]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try { await projApi("/comments", { method: "POST", body: JSON.stringify({ projectId: p.id, body }) }); setBody(""); onChanged(); }
    catch (e) { setErr(e.message); } finally { setSending(false); }
  };
  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await projApi(`/comments?id=${c.id}`, { method: "DELETE" }); onChanged(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ maxHeight: 460, overflowY: "auto", padding: 20 }}>
        {!comments.length ? (
          <PrjEmpty icon="💬" title="No comments yet" hint="Ask a question, share feedback, or flag something — everyone on this project sees it." />
        ) : comments.map(c => (
          <div key={c.id} style={{ display: "flex", gap: 12, marginBottom: 18 }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11.5, fontWeight: 700, color: "#fff",
              background: c.isAgency ? `linear-gradient(135deg, ${T.accent}, ${T.accentDark})` : "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            }}>{prjInitials(c.authorName)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.authorName}</span>
                <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", padding: "1px 6px", borderRadius: 4, background: c.isAgency ? T.accentGlow : "rgba(59,130,246,.15)", color: c.isAgency ? T.accent : "#60a5fa" }}>
                  {c.isAgency ? "Power Vision BI" : "Client"}
                </span>
                <span style={{ fontSize: 11, color: T.textMuted }}>{prjRelative(c.createdAt)}</span>
                <button onClick={() => remove(c)} title="Delete" style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2, opacity: .6 }}><Icon n="trash" size={12} /></button>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: T.textSecondary, whiteSpace: "pre-wrap" }}>{c.body}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ borderTop: `1px solid ${T.border}`, padding: 16, display: "flex", gap: 10, alignItems: "flex-end" }}>
        <textarea className="field-input" rows={2} placeholder="Write a comment…  (Ctrl+Enter to send)" value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
          style={{ resize: "vertical", minHeight: 44, fontSize: 13 }} />
        <button className="btn-primary" style={{ width: "auto", padding: "11px 20px", fontSize: 13, whiteSpace: "nowrap" }} disabled={sending || !body.trim()} onClick={send}>Send</button>
      </div>
    </div>
  );
}

// ── Files ─────────────────────────────────────────────────────────────────────
// Uploads go straight from the browser to Supabase Storage using a signed URL the
// backend mints — the bytes never pass through our VPS, so nginx's 1 MB body limit
// is irrelevant. Downloads are short-lived signed URLs minted behind the same auth.

const prjFileSize = b => {
  b = Number(b) || 0;
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
};
const prjFileIcon = (name, mime) => {
  const e = (name || "").split(".").pop().toLowerCase();
  if ((mime || "").startsWith("image/")) return "🖼️";
  if (e === "pdf") return "📕";
  if (["xlsx", "xls", "csv"].includes(e)) return "📗";
  if (["docx", "doc"].includes(e)) return "📘";
  if (["pptx", "ppt"].includes(e)) return "📙";
  if (["zip", "rar", "7z"].includes(e)) return "🗜️";
  if (["mp4", "mov", "avi", "webm"].includes(e)) return "🎬";
  return "📄";
};

function ProjectFiles({ p, isAgency, meId, onChanged, setErr }) {
  const files = p.files || [];
  const [drag, setDrag] = useState(false);
  const [queue, setQueue] = useState([]);   // [{ name, pct, error }]
  const inputRef = useRef(null);

  const uploadOne = async (file, idx) => {
    const bump = (patch) => setQueue(q => q.map((it, i) => i === idx ? { ...it, ...patch } : it));
    try {
      // 1. authorise + get a signed URL
      const { uploadUrl, path } = await projApi("/files", { method: "POST", body: JSON.stringify({
        action: "sign", projectId: p.id, name: file.name, size: file.size, mimeType: file.type,
      }) });
      bump({ pct: 10 });

      // 2. PUT the bytes straight to Supabase, with real progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        if (file.type) xhr.setRequestHeader("content-type", file.type);
        xhr.upload.onprogress = e => { if (e.lengthComputable) bump({ pct: 10 + Math.round((e.loaded / e.total) * 85) }); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error("Upload failed — check your connection"));
        xhr.send(file);
      });
      bump({ pct: 97 });

      // 3. catalogue it
      await projApi("/files", { method: "POST", body: JSON.stringify({
        action: "confirm", projectId: p.id, path, name: file.name, size: file.size, mimeType: file.type,
      }) });
      bump({ pct: 100 });
    } catch (e) {
      bump({ error: e.message });
      throw e;
    }
  };

  const addFiles = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const start = queue.length;
    setQueue(q => [...q, ...picked.map(f => ({ name: f.name, pct: 0, error: null }))]);
    for (let i = 0; i < picked.length; i++) {
      try { await uploadOne(picked[i], start + i); } catch { /* shown inline on the row */ }
    }
    onChanged();
    // Clear finished rows, leave failures on screen to be read.
    setTimeout(() => setQueue(q => q.filter(it => it.error)), 1200);
  };

  const download = async (f) => {
    try {
      const { url } = await projApi("/files", { method: "POST", body: JSON.stringify({ action: "download", id: f.id }) });
      window.open(url, "_blank", "noopener");
    } catch (e) { setErr(e.message); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This removes the file for everyone.`)) return;
    try { await projApi(`/files?id=${f.id}`, { method: "DELETE" }); onChanged(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `1.5px dashed ${drag ? T.accent : T.border}`, borderRadius: 12, padding: "30px 20px",
          textAlign: "center", cursor: "pointer", marginBottom: 18,
          background: drag ? T.accentGlow2 : "rgba(255,255,255,.02)", transition: "border-color .15s, background .15s",
        }}>
        <input ref={inputRef} type="file" multiple style={{ display: "none" }}
          onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
        <div style={{ fontSize: 26, marginBottom: 8, opacity: .8 }}>📎</div>
        <p style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
          {drag ? "Drop to upload" : "Drop files here, or click to choose"}
        </p>
        <p style={{ fontSize: 11.5, color: T.textMuted }}>
          Shared with {isAgency ? "the client" : "Power Vision BI"} on this project
        </p>
      </div>

      {queue.length > 0 && (
        <div style={{ marginBottom: 18, display: "flex", flexDirection: "column", gap: 8 }}>
          {queue.map((it, i) => (
            <div key={i} style={{ background: T.surface, border: `1px solid ${it.error ? "rgba(248,81,73,.3)" : T.border}`, borderRadius: 9, padding: "10px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: it.error ? 0 : 7 }}>
                <span style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</span>
                <span className="mono" style={{ fontSize: 11, color: it.error ? T.error : T.textMuted, flexShrink: 0 }}>
                  {it.error ? "failed" : `${it.pct}%`}
                </span>
              </div>
              {it.error ? <p style={{ fontSize: 11.5, color: T.error }}>{it.error}</p> : <PrjProgress value={it.pct} height={4} />}
            </div>
          ))}
        </div>
      )}

      {!files.length ? (
        <PrjEmpty icon="📁" title="No files yet"
          hint={isAgency ? "Drop deliverables, specs or screenshots here — the client sees them straight away."
            : "Anything Power Vision BI shares lands here, and you can upload files for them too."} />
      ) : (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
          {files.map((f, i) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: i === files.length - 1 ? "none" : `1px solid ${T.border}` }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{prjFileIcon(f.name, f.mimeType)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</p>
                <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                  {prjFileSize(f.size)} · {f.uploadedByName || "Someone"} · {prjRelative(f.createdAt)}
                </p>
              </div>
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
                padding: "1px 6px", borderRadius: 4, flexShrink: 0,
                background: f.isAgency ? T.accentGlow : "rgba(59,130,246,.15)", color: f.isAgency ? T.accent : "#60a5fa",
              }}>{f.isAgency ? "PVBI" : "Client"}</span>
              <button className="btn-secondary" style={{ fontSize: 12, padding: "5px 12px", flexShrink: 0 }} onClick={() => download(f)}>Download</button>
              {(isAgency || f.uploadedById === meId) && (
                <button onClick={() => remove(f)} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2, flexShrink: 0 }}>
                  <Icon n="trash" size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Requests ──────────────────────────────────────────────────────────────────

function ProjectRequests({ p, meta, isAgency, onChanged, setErr }) {
  const [adding, setAdding] = useState(false);
  const reqs = p.requests || [];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <p style={{ fontSize: 12.5, color: T.textSecondary }}>
          {isAgency
            ? "Work the client has asked for — and anything you need from them."
            : "What you've asked Power Vision BI for, and what they need from you."}
        </p>
        <button className="btn-secondary" onClick={() => setAdding(true)}>＋ New request</button>
      </div>
      {!reqs.length ? (
        <PrjEmpty icon="📥" title="No requests" hint={isAgency ? "Client asks land here for you to accept or decline — and you can raise one addressed at someone on their side when you need data or a sign-off." : "Need something added or changed? Raise a request and Power Vision BI will pick it up."}
          action={<button className="btn-primary" style={{ width: "auto", padding: "9px 20px", fontSize: 13 }} onClick={() => setAdding(true)}>＋ New request</button>} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {reqs.map(r => <RequestRow key={r.id} r={r} isAgency={isAgency} projects={null} roster={meta?.roster || []} onChanged={onChanged} setErr={setErr} />)}
        </div>
      )}
      {adding && <NewRequestModal meta={{ isAgency, roster: meta?.roster || [] }} projects={[{ id: p.id, name: p.name, tenantId: p.tenantId, clientName: p.clientName }]} fixedProjectId={p.id}
        onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onChanged(); }} />}
    </div>
  );
}

function RequestRow({ r, isAgency, projects, roster = [], onChanged, setErr, onOpenProject }) {
  const st = prjMeta(PRJ_REQ_STATUS, r.status);
  const pr = prjMeta(PRJ_REQ_PRIORITY, r.priority);
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState(r.response || "");
  const [target, setTarget] = useState(r.projectId || "");
  const [assignee, setAssignee] = useState(r.assignedToId || "");
  const [busy, setBusy] = useState(false);

  const triage = async (status) => {
    setBusy(true);
    try {
      await projApi(`/requests?id=${r.id}`, { method: "PATCH", body: JSON.stringify({
        status, response: response || null, projectId: target || undefined,
        assignedToId: assignee || null,
      }) });
      onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  // Re-assign without closing the request out.
  const reassign = async (id) => {
    setAssignee(id); setBusy(true);
    try { await projApi(`/requests?id=${r.id}`, { method: "PATCH", body: JSON.stringify({ assignedToId: id || null }) }); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  // Direction: PVBI asking the client for something, vs the client asking us.
  const askedOfClient = !!r.fromAgency;
  // A client can reply to what PVBI asked of them (status + a note), but not triage.
  const canReply = !isAgency && askedOfClient;
  const reply = async (status) => {
    setBusy(true);
    try { await projApi(`/requests?id=${r.id}`, { method: "PATCH", body: JSON.stringify({ status, response: response || null }) }); onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <PrjDot color={pr.color} size={8} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.title}</span>
            <PrjPill meta={st} size={10} />
            <PrjPill meta={pr} size={10} />
          </div>
          <p style={{ fontSize: 11.5, color: T.textMuted }}>
            {r.requestedByName || "Someone"} · {prjRelative(r.createdAt)}
            {r.tenant?.name && isAgency ? ` · ${r.tenant.name}` : ""}
            {r.project?.name ? ` · ${r.project.name}` : ""}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
              padding: "1px 6px", borderRadius: 4,
              background: askedOfClient ? T.accentGlow : "rgba(59,130,246,.15)",
              color: askedOfClient ? T.accent : "#60a5fa",
            }}>{askedOfClient ? "PVBI asked" : "Client asked"}</span>
            <span style={{ fontSize: 11, color: T.textMuted }}>{askedOfClient ? "of" : "for"}</span>
            {r.assignedToName ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <PrjAvatar name={r.assignedToName} size={18} />
                <span style={{ fontSize: 11.5, color: T.textSecondary, fontWeight: 500 }}>{r.assignedToName}</span>
              </span>
            ) : (
              <span style={{ fontSize: 11.5, color: T.textMuted, fontStyle: "italic" }}>anyone at Power Vision BI</span>
            )}
          </div>
          {r.details && <p style={{ fontSize: 12.5, color: T.textSecondary, marginTop: 8, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{r.details}</p>}
          {r.response && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: T.accentGlow2, borderLeft: `2px solid ${T.accent}`, borderRadius: 4 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.accent, marginBottom: 3 }}>
                {r.reviewedByName || (askedOfClient ? "Reply" : "Power Vision BI")}
              </p>
              <p style={{ fontSize: 12.5, color: T.textSecondary, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{r.response}</p>
            </div>
          )}
        </div>
        {isAgency && ["new", "reviewing", "client_review"].includes(r.status) && (
          <button className="btn-secondary" style={{ flexShrink: 0, fontSize: 12, padding: "6px 12px" }} onClick={() => setOpen(o => !o)}>{open ? "Close" : "Triage"}</button>
        )}
        {canReply && r.status !== "completed" && (
          <button className="btn-primary" style={{ flexShrink: 0, width: "auto", fontSize: 12, padding: "6px 14px" }} onClick={() => setOpen(o => !o)}>{open ? "Close" : "Respond"}</button>
        )}
        {onOpenProject && r.projectId && (
          <button className="btn-ghost" style={{ flexShrink: 0, fontSize: 12 }} onClick={() => onOpenProject(r.projectId)}>Open →</button>
        )}
      </div>

      {open && isAgency && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
          {projects && (
            <div className="field-group">
              <label className="field-label">Assign to project</label>
              <select className="field-input" value={target} onChange={e => setTarget(e.target.value)} style={{ background: T.bg, fontSize: 13, padding: "9px 12px" }}>
                <option value="">— pick a project —</option>
                {projects.filter(p => p.tenantId === r.tenantId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div className="field-group">
            <label className="field-label">Owned by</label>
            <select className="field-input" value={assignee} disabled={busy} onChange={e => reassign(e.target.value)} style={{ background: T.bg, fontSize: 13, padding: "9px 12px" }}>
              <option value="">Unassigned</option>
              {roster.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
            <p style={{ fontSize: 11, color: T.textMuted }}>Changing this emails the new owner and hands them the task when you accept.</p>
          </div>
          <div className="field-group">
            <label className="field-label">Reply to the client</label>
            <textarea className="field-input" rows={3} value={response} onChange={e => setResponse(e.target.value)} placeholder="What you did, what you'd like them to check…" style={{ fontSize: 13, resize: "vertical" }} />
            <p style={{ fontSize: 11, color: T.textMuted }}>Emailed to whoever raised the request, and shown on it in the portal.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {/* Accepting into the task board only makes sense for work the CLIENT
                asked of us. Our own ask of them is closed out, not queued up. */}
            {!askedOfClient && (
              <>
                <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 13 }} disabled={busy} onClick={() => triage("accepted")}>✓ Accept → create task</button>
                <button className="btn-secondary" disabled={busy} onClick={() => triage("client_review")}
                  style={{ color: T.accent, borderColor: "rgba(196,122,16,.4)" }}>↩ Done — back to client</button>
                <button className="btn-secondary" disabled={busy} onClick={() => triage("completed")}>Mark completed</button>
              </>
            )}
            {askedOfClient && (
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 13 }} disabled={busy} onClick={() => triage("completed")}>✓ Mark received</button>
            )}
            <button className="btn-secondary" disabled={busy} onClick={() => triage("reviewing")}>Mark reviewing</button>
            <button className="btn-secondary" disabled={busy} onClick={() => triage("declined")} style={{ color: T.error, borderColor: "rgba(248,81,73,.3)" }}>{askedOfClient ? "Withdraw" : "Decline"}</button>
          </div>
        </div>
      )}

      {open && canReply && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="field-group">
            <label className="field-label">Your reply to Power Vision BI</label>
            <textarea className="field-input" rows={2} value={response} onChange={e => setResponse(e.target.value)}
              placeholder="Optional — anything they should know" style={{ fontSize: 13, resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 13 }} disabled={busy} onClick={() => reply("completed")}>✓ Done — sent</button>
            <button className="btn-secondary" disabled={busy} onClick={() => reply("reviewing")}>Working on it</button>
            <button className="btn-secondary" disabled={busy} onClick={() => reply("declined")} style={{ color: T.error, borderColor: "rgba(248,81,73,.3)" }}>Can't do this</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RequestInbox({ meta, isAgency, projects, onClose, onOpenProject }) {
  const [reqs, setReqs] = useState(null);
  const [filter, setFilter] = useState("open");
  const [err, setErr] = useState(null);
  const load = () => projApi(`/requests${filter === "open" ? "?status=open" : ""}`).then(d => setReqs(d.requests || [])).catch(e => setErr(e.message));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  return (
    <PrjModal title={isAgency ? "Request inbox" : "Your requests"} onClose={onClose} width={760}>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[["open", "Open"], ["all", "All"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setFilter(id)} style={{
            background: filter === id ? T.accentGlow : "transparent", border: `1px solid ${filter === id ? T.accent : T.border}`,
            color: filter === id ? T.accent : T.textSecondary, borderRadius: 7, cursor: "pointer",
            fontSize: 12, fontWeight: 600, padding: "5px 14px", fontFamily: "'Plus Jakarta Sans',sans-serif",
          }}>{lbl}</button>
        ))}
      </div>
      {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
      {!reqs ? <p style={{ fontSize: 13, color: T.textMuted, padding: 20, textAlign: "center" }}>Loading…</p>
        : !reqs.length ? <PrjEmpty icon="📭" title="Nothing here" hint={filter === "open" ? "No open requests." : "No requests yet."} />
        : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {reqs.map(r => <RequestRow key={r.id} r={r} isAgency={isAgency} projects={projects} roster={meta?.roster || []} onChanged={load} setErr={setErr} onOpenProject={onOpenProject} />)}
          </div>}
    </PrjModal>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────────

const PRJ_ACT_ICON = { status: "🔀", progress: "📊", milestone: "🎯", task: "✅", request: "📥", comment: "💬", system: "⚙️" };

function ActivityFeed({ items }) {
  if (!items.length) return <PrjEmpty icon="🕘" title="Nothing yet" hint="Every status change, milestone and comment lands here." />;
  const groups = {};
  for (const a of items) {
    const k = new Date(a.createdAt).toDateString();
    (groups[k] = groups[k] || []).push(a);
  }
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 22 }}>
      {Object.entries(groups).map(([day, list]) => (
        <div key={day} style={{ marginBottom: 22 }}>
          <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.textMuted, marginBottom: 12 }}>
            {new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
          </p>
          {list.map(a => (
            <div key={a.id} style={{ display: "flex", gap: 11, alignItems: "flex-start", marginBottom: 11 }}>
              <span style={{ fontSize: 13, width: 20, textAlign: "center", flexShrink: 0 }}>{PRJ_ACT_ICON[a.type] || "•"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 12.5, color: T.textSecondary, lineHeight: 1.5 }}>{a.body}</p>
                <p style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>{a.authorName || "System"} · {new Date(a.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Modals ────────────────────────────────────────────────────────────────────

function PrjModal({ title, subtitle, onClose, width = 620, children, footer }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.65)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(3px)" }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, width: "100%", maxWidth: width,
        maxHeight: "88vh", display: "flex", flexDirection: "column", animation: "fadeUp .25s ease both", boxShadow: "0 24px 64px rgba(0,0,0,.5)",
      }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>{title}</h2>
            {subtitle && <p style={{ fontSize: 12.5, color: T.textSecondary, marginTop: 4 }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 4 }}><Icon n="x" size={18} /></button>
        </div>
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>{footer}</div>}
      </div>
    </div>
  );
}

function NewProjectModal({ meta, onClose, onSaved }) {
  const [f, setF] = useState({
    tenantId: meta.clients?.[0]?.id || "", name: "", description: "", status: "planning",
    health: "on_track", priority: "normal", startDate: prjInput(new Date()), targetDate: "",
    ownerId: meta.me?.id || "", clientLead: "", color: PRJ_COLORS[0],
  });
  const [milestones, setMilestones] = useState([{ title: "", dueDate: "" }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return setErr("Give the project a name");
    if (!f.tenantId) return setErr("Pick the client this project is for");
    setSaving(true); setErr(null);
    const owner = (meta.roster || []).find(u => u.id === f.ownerId);
    try {
      const d = await projApi("/", { method: "POST", body: JSON.stringify({
        ...f,
        ownerName: owner ? (owner.name || owner.email) : meta.me?.name,
        milestones: milestones.filter(m => m.title.trim()),
      }) });
      onSaved(d.project.id);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <PrjModal title="New project" subtitle="Set it up now — you can add tasks and detail once it exists." onClose={onClose} width={640}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ width: "auto", padding: "10px 22px", fontSize: 13.5 }} disabled={saving} onClick={save}>{saving ? "Creating…" : "Create project"}</button>
      </>}>
      {err && <div className="alert alert-error" style={{ marginBottom: 16 }}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="field-group">
          <label className="field-label">Client *</label>
          <select className="field-input" value={f.tenantId} onChange={e => set("tenantId", e.target.value)} style={{ background: T.bg }}>
            <option value="">— pick a client —</option>
            {(meta.clients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field-group">
          <label className="field-label">Project name *</label>
          <input className="field-input" autoFocus value={f.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Inventory alerting dashboard" />
        </div>
        <div className="field-group">
          <label className="field-label">What is it?</label>
          <textarea className="field-input" rows={3} value={f.description} onChange={e => set("description", e.target.value)}
            placeholder="A sentence or two the client will read on their project card." style={{ resize: "vertical" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
          <div className="field-group">
            <label className="field-label">Status</label>
            <select className="field-input" value={f.status} onChange={e => set("status", e.target.value)} style={{ background: T.bg }}>
              {PRJ_STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Priority</label>
            <select className="field-input" value={f.priority} onChange={e => set("priority", e.target.value)} style={{ background: T.bg }}>
              {PRJ_PRIORITY.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Start</label>
            <input className="field-input" type="date" value={f.startDate} onChange={e => set("startDate", e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">Target date</label>
            <input className="field-input" type="date" value={f.targetDate} onChange={e => set("targetDate", e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label">PVBI lead</label>
            <select className="field-input" value={f.ownerId} onChange={e => set("ownerId", e.target.value)} style={{ background: T.bg }}>
              <option value="">Unassigned</option>
              {(meta.roster || []).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Client contact</label>
            <input className="field-input" value={f.clientLead} onChange={e => set("clientLead", e.target.value)} placeholder="Name" />
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Card colour</label>
          <div style={{ display: "flex", gap: 8 }}>
            {PRJ_COLORS.map(c => (
              <button key={c} onClick={() => set("color", c)} style={{
                width: 26, height: 26, borderRadius: 7, background: c, cursor: "pointer",
                border: f.color === c ? `2px solid ${T.textPrimary}` : "2px solid transparent",
              }} />
            ))}
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Milestones (drive the progress bar)</label>
          {milestones.map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input className="field-input" placeholder={`Milestone ${i + 1}`} value={m.title}
                onChange={e => setMilestones(ms => ms.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} style={{ flex: 1 }} />
              <input className="field-input" type="date" value={m.dueDate}
                onChange={e => setMilestones(ms => ms.map((x, j) => j === i ? { ...x, dueDate: e.target.value } : x))} style={{ width: 160 }} />
              {milestones.length > 1 && (
                <button onClick={() => setMilestones(ms => ms.filter((_, j) => j !== i))}
                  style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer", color: T.textMuted, padding: "0 10px" }}>×</button>
              )}
            </div>
          ))}
          <button className="btn-ghost" onClick={() => setMilestones(ms => [...ms, { title: "", dueDate: "" }])}>＋ Add another</button>
        </div>
      </div>
    </PrjModal>
  );
}

function EditProjectModal({ p, meta, onClose, onSaved, onDeleted }) {
  const [f, setF] = useState({
    name: p.name || "", code: p.code || "", description: p.description || "",
    status: p.status, health: p.health, priority: p.priority,
    startDate: prjInput(p.startDate), targetDate: prjInput(p.targetDate),
    ownerId: p.ownerId || "", clientLead: p.clientLead || "", color: p.color || PRJ_COLORS[0],
    archived: !!p.archived,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return setErr("Name can't be empty");
    setSaving(true); setErr(null);
    const owner = (meta.roster || []).find(u => u.id === f.ownerId);
    try {
      await projApi(`/${p.id}`, { method: "PATCH", body: JSON.stringify({ ...f, ownerName: owner ? (owner.name || owner.email) : null }) });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${p.name}" and everything on it? This can't be undone.`)) return;
    try { await projApi(`/${p.id}`, { method: "DELETE" }); onDeleted(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <PrjModal title="Edit project" subtitle={p.clientName} onClose={onClose} width={640}
      footer={<>
        <button className="btn-secondary" onClick={del} style={{ color: T.error, borderColor: "rgba(248,81,73,.3)", marginRight: "auto" }}>Delete</button>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ width: "auto", padding: "10px 22px", fontSize: 13.5 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
      </>}>
      {err && <div className="alert alert-error" style={{ marginBottom: 16 }}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="field-group"><label className="field-label">Project name</label>
          <input className="field-input" value={f.name} onChange={e => set("name", e.target.value)} /></div>
        <div className="field-group"><label className="field-label">Description</label>
          <textarea className="field-input" rows={3} value={f.description} onChange={e => set("description", e.target.value)} style={{ resize: "vertical" }} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
          <div className="field-group"><label className="field-label">Reference</label>
            <input className="field-input" value={f.code} onChange={e => set("code", e.target.value)} /></div>
          <div className="field-group"><label className="field-label">Status</label>
            <select className="field-input" value={f.status} onChange={e => set("status", e.target.value)} style={{ background: T.bg }}>
              {PRJ_STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
          <div className="field-group"><label className="field-label">Health</label>
            <select className="field-input" value={f.health} onChange={e => set("health", e.target.value)} style={{ background: T.bg }}>
              {PRJ_HEALTH.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
          <div className="field-group"><label className="field-label">Priority</label>
            <select className="field-input" value={f.priority} onChange={e => set("priority", e.target.value)} style={{ background: T.bg }}>
              {PRJ_PRIORITY.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
          <div className="field-group"><label className="field-label">Start</label>
            <input className="field-input" type="date" value={f.startDate} onChange={e => set("startDate", e.target.value)} /></div>
          <div className="field-group"><label className="field-label">Target date</label>
            <input className="field-input" type="date" value={f.targetDate} onChange={e => set("targetDate", e.target.value)} /></div>
          <div className="field-group"><label className="field-label">PVBI lead</label>
            <select className="field-input" value={f.ownerId} onChange={e => set("ownerId", e.target.value)} style={{ background: T.bg }}>
              <option value="">Unassigned</option>
              {(meta.roster || []).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</select></div>
          <div className="field-group"><label className="field-label">Client contact</label>
            <input className="field-input" value={f.clientLead} onChange={e => set("clientLead", e.target.value)} /></div>
        </div>
        <div className="field-group">
          <label className="field-label">Card colour</label>
          <div style={{ display: "flex", gap: 8 }}>
            {PRJ_COLORS.map(c => (
              <button key={c} onClick={() => set("color", c)} style={{
                width: 26, height: 26, borderRadius: 7, background: c, cursor: "pointer",
                border: f.color === c ? `2px solid ${T.textPrimary}` : "2px solid transparent",
              }} />
            ))}
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.textSecondary, cursor: "pointer" }}>
          <input type="checkbox" checked={f.archived} onChange={e => set("archived", e.target.checked)} />
          Archive this project (hides it from the default list, including the client's)
        </label>
      </div>
    </PrjModal>
  );
}

function NewRequestModal({ meta, projects, fixedProjectId, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", details: "", priority: "normal", projectId: fixedProjectId || "", tenantId: "", assignedToId: "", assignedToEmail: "", assignedToName: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const isAgency = !!meta?.isAgency;
  const roster = meta?.roster || [];
  // As PVBI you can also address a request AT the client — "please send me July's
  // data". Their people are only fetched once we know which client we're on, and
  // only the backend will hand them over (agency callers only).
  const [clientRoster, setClientRoster] = useState([]);
  const [clientLabel, setClientLabel] = useState("");
  const targetTenantId = f.tenantId
    || (projects || []).find(p => p.id === (f.projectId || fixedProjectId))?.tenantId
    || "";
  useEffect(() => {
    if (!isAgency || !targetTenantId) { setClientRoster([]); return; }
    let live = true;
    projApi(`/meta?tenantId=${targetTenantId}`)
      .then(d => { if (live) setClientRoster(d.clientRoster || []); })
      .catch(() => { if (live) setClientRoster([]); });
    const proj = (projects || []).find(p => p.tenantId === targetTenantId);
    setClientLabel(proj?.clientName || "the client");
    return () => { live = false; };
  }, [isAgency, targetTenantId]); // eslint-disable-line

  const save = async () => {
    if (!f.title.trim()) return setErr("Say what you need");
    if (f.assignedToEmail !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.assignedToEmail.trim())) {
      return setErr("Enter a valid email address for the person you're asking");
    }
    setSaving(true); setErr(null);
    try {
      await projApi("/requests", { method: "POST", body: JSON.stringify({
        title: f.title, details: f.details, priority: f.priority,
        projectId: f.projectId || null, tenantId: f.tenantId || undefined,
        assignedToId: f.assignedToId || null,
        assignedToEmail: f.assignedToEmail.trim() || null,
        assignedToName: f.assignedToName.trim() || null,
      }) });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <PrjModal title="Request work" subtitle="Power Vision BI will review this and turn it into a task." onClose={onClose} width={560}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ width: "auto", padding: "10px 22px", fontSize: 13.5 }} disabled={saving} onClick={save}>{saving ? "Sending…" : "Send request"}</button>
      </>}>
      {err && <div className="alert alert-error" style={{ marginBottom: 16 }}>{err}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {isAgency && !fixedProjectId && (
          <div className="field-group">
            <label className="field-label">Client</label>
            <select className="field-input" value={f.tenantId} onChange={e => set("tenantId", e.target.value)} style={{ background: T.bg }}>
              <option value="">— pick a client —</option>
              {(meta.clients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        {!fixedProjectId && (
          <div className="field-group">
            <label className="field-label">Project (optional)</label>
            <select className="field-input" value={f.projectId} onChange={e => set("projectId", e.target.value)} style={{ background: T.bg }}>
              <option value="">Not tied to a project yet</option>
              {(projects || []).filter(p => !f.tenantId || p.tenantId === f.tenantId).map(p => (
                <option key={p.id} value={p.id}>{p.clientName ? `${p.clientName} — ` : ""}{p.name}</option>
              ))}
            </select>
          </div>
        )}
        {/* A request always goes to the OTHER side: as PVBI you're asking the client
            for something, so you only see their contacts; a client is asking us, so
            they only see the PVBI team. Showing both sides made the list long and
            let you address a request at your own colleague by accident. */}
        <div className="field-group">
          <label className="field-label">{isAgency ? "Who at the client is this for?" : "Who is this for?"}</label>
          <select className="field-input" value={f.assignedToId === "" && f.assignedToEmail !== "" ? "__email__" : f.assignedToId}
            onChange={e => {
              const v = e.target.value;
              if (v === "__email__") setF(prev => ({ ...prev, assignedToId: "", assignedToEmail: prev.assignedToEmail || " " }));
              else setF(prev => ({ ...prev, assignedToId: v, assignedToEmail: "", assignedToName: "" }));
            }} style={{ background: T.bg }}>
            <option value="">
              {isAgency ? (clientLabel ? `Anyone at ${clientLabel}` : "Anyone at the client") : "Anyone at Power Vision BI"}
            </option>
            {(isAgency ? clientRoster : roster).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            {isAgency && <option value="__email__">✉ Someone else — type their email…</option>}
          </select>
          {f.assignedToEmail !== "" ? (
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <input className="field-input" type="email" autoFocus placeholder="their@email.com"
                value={f.assignedToEmail.trim()} onChange={e => set("assignedToEmail", e.target.value || " ")}
                style={{ flex: "1 1 200px" }} />
              <input className="field-input" placeholder="Their name (optional)"
                value={f.assignedToName} onChange={e => set("assignedToName", e.target.value)}
                style={{ flex: "1 1 140px" }} />
            </div>
          ) : null}
          <p style={{ fontSize: 11, color: T.textMuted }}>
            {f.assignedToEmail !== ""
              ? "They'll get the email even without a portal account — but they can't reply in the portal, so they'll answer you directly."
              : isAgency
                ? (clientRoster.length
                    ? "They get the notification email. Not listed? Choose “Someone else” and type any address."
                    : "Pick a client or project above to see their contacts — or choose “Someone else” and type any address.")
                : "They get the notification email. Leave it open and the whole team is told."}
          </p>
        </div>
        <div className="field-group">
          <label className="field-label">What do you need? *</label>
          <input className="field-input" autoFocus value={f.title} onChange={e => set("title", e.target.value)} placeholder="Short summary" />
        </div>
        <div className="field-group">
          <label className="field-label">Detail</label>
          <textarea className="field-input" rows={4} value={f.details} onChange={e => set("details", e.target.value)}
            placeholder="Any context that helps — which report, what's wrong, when you need it by." style={{ resize: "vertical" }} />
        </div>
        <div className="field-group">
          <label className="field-label">How urgent?</label>
          <div style={{ display: "flex", gap: 8 }}>
            {PRJ_REQ_PRIORITY.map(s => (
              <button key={s.id} onClick={() => set("priority", s.id)} style={{
                flex: 1, background: f.priority === s.id ? `${s.color}20` : "transparent",
                border: `1px solid ${f.priority === s.id ? s.color : T.border}`, borderRadius: 8, cursor: "pointer",
                color: f.priority === s.id ? s.color : T.textSecondary, fontSize: 12.5, fontWeight: 600,
                padding: "9px 0", fontFamily: "'Plus Jakarta Sans',sans-serif",
              }}>{s.label}</button>
            ))}
          </div>
        </div>
      </div>
    </PrjModal>
  );
}

// ─── CRM ──────────────────────────────────────────────────────────────────────
const CRM_STAGES = [
  { id: "lead",      label: "Lead",      color: "#8b949e" },
  { id: "qualified", label: "Qualified", color: "#3b82f6" },
  { id: "demo",      label: "Demo",      color: "#a78bfa" },
  { id: "proposal",  label: "Proposal",  color: "#f59e0b" },
  { id: "won",       label: "Won",       color: "#2ea043" },
  { id: "lost",      label: "Lost",      color: "#ef4444" },
];
const CRM_OPEN_STAGES = CRM_STAGES.filter(s => s.id !== "won" && s.id !== "lost");

function crmApi(path, opts = {}) {
  const imp = sessionStorage.getItem("pvbi_impersonate");
  return fetch(`${API_BASE}/crm${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}`,
      "Content-Type": "application/json",
      ...(imp ? { "X-Impersonate-Tenant": imp } : {}),
      ...(opts.headers || {}),
    },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || ("HTTP " + r.status)); return d; });
}
const crmMoney = v => { v = Number(v) || 0; return v >= 1e6 ? "$" + (v / 1e6).toFixed(1) + "M" : v >= 1e3 ? "$" + (v / 1e3).toFixed(0) + "K" : "$" + Math.round(v); };
const crmDate = d => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

// Loggable activity types (the model also has system/stage_change which are auto-generated)
const CRM_ACT_TYPES = [
  { id: "note",    label: "Note",    icon: "📝" },
  { id: "call",    label: "Call",    icon: "📞" },
  { id: "email",   label: "Email",   icon: "✉️" },
  { id: "meeting", label: "Meeting", icon: "🤝" },
];
const crmActMeta = t => CRM_ACT_TYPES.find(x => x.id === t)
  || (t === "stage_change" ? { icon: "🔀", label: "Stage", color: "#a78bfa" }
    : t === "system" ? { icon: "⚙️", label: "System", color: "#8b949e" }
    : { icon: "•", label: t, color: "#8b949e" });
const CRM_PRIORITIES = [
  { id: "high",   label: "High",   color: "#ef4444" },
  { id: "normal", label: "Normal", color: "#8b949e" },
  { id: "low",    label: "Low",    color: "#3b82f6" },
];

// ─── HUB (company-wide task workflow) ───────────────────────────────────────────
const TASK_GROUPS = ["Administration", "Engineering", "Manufacturing", "Operations", "Product Development", "Purchasing", "Sales"];
const TASK_REQ_PRIORITIES = [
  { id: "low",      label: "Low",      color: "#8b949e" },
  { id: "medium",   label: "Medium",   color: "#3b82f6" },
  { id: "high",     label: "High",     color: "#f59e0b" },
  { id: "critical", label: "Critical", color: "#ef4444" },
  { id: "p1",       label: "#1 Priority — work on nothing else", color: "#dc2626" },
];
const TASK_STATUSES = [
  { code: "requested",           label: "Requested — Awaiting Approval, Priority & Assignment" },
  { code: "approved",            label: "Approved — Assigned in Task Queue" },
  { code: "in_progress",         label: "Completion in Progress" },
  { code: "delayed_info",        label: "Delayed — Additional Info Needed" },
  { code: "delayed_engineering", label: "Delayed — Engineering Support" },
  { code: "delayed_freight",     label: "Delayed — Freight" },
  { code: "delayed_cutsheet",    label: "Delayed — Cut Sheet" },
  { code: "delayed_mfg",         label: "Delayed — Mfg. Pending" },
  { code: "delayed_newproduct",  label: "Delayed — New Product Creation" },
  { code: "delayed_stock",       label: "Delayed — Out of Stock-Supplier" },
  { code: "delayed_pricing",     label: "Delayed — PN / Pricing" },
  { code: "delayed_renderings",  label: "Delayed — Renderings/Feature List" },
  { code: "delayed_subtask",     label: "Delayed — Waiting on completion of Sub Task" },
  { code: "delayed_model",       label: "Delayed — Waiting on Model" },
  { code: "delayed_assignment",  label: "Delayed — Waiting for Assignment" },
  { code: "abandoned",           label: "Abandoned" },
  { code: "completed",           label: "Completed" },
];
const TASK_TERMINAL = ["completed", "abandoned"];
const taskStatusLabel = c => (TASK_STATUSES.find(s => s.code === c) || {}).label || c;
const taskReqPri = c => TASK_REQ_PRIORITIES.find(p => p.id === c) || { label: c, color: "#8b949e" };
const taskStatusColor = c => c === "completed" ? "#2ea043" : c === "abandoned" ? "#8b949e"
  : c === "requested" ? "#f59e0b" : c === "in_progress" ? "#3b82f6"
  : (c || "").startsWith("delayed") ? "#ef4444" : T.accent;
// Assigned priority (1–5) — set by the Task Assigner.
const TASK_ASSIGNED_PRIORITIES = [
  { n: 1, label: "Critical #1", rule: "Work on nothing else until complete", color: "#dc2626" },
  { n: 2, label: "Critical #2", rule: "Start immediately after Critical #1 is complete", color: "#f97316" },
  { n: 3, label: "High", rule: "", color: "#f59e0b" },
  { n: 4, label: "Medium", rule: "", color: "#3b82f6" },
  { n: 5, label: "Low", rule: "", color: "#8b949e" },
];
const taskAssignedPri = n => TASK_ASSIGNED_PRIORITIES.find(p => p.n === Number(n));
const taskIsTerminal = c => c === "completed" || c === "abandoned";
// KPI category from a status code.
const taskCategory = c => c === "requested" ? "awaiting" : c === "approved" ? "queued" : c === "in_progress" ? "progress"
  : c === "completed" ? "completed" : c === "abandoned" ? "abandoned" : (c || "").startsWith("delayed") ? "delayed" : "other";

// ─── Opportunity workflow (RDP model) ───────────────────────────────────────────
const OPP_STATUSES = [
  { id: "in_play",     label: "In Play",       color: "#3b82f6" },
  { id: "on_hold",     label: "On Hold",       color: "#f59e0b" },
  { id: "closed_won",  label: "Closed — Won",  color: "#2ea043" },
  { id: "closed_lost", label: "Closed — Lost", color: "#ef4444" },
];
const OPP_CONFIDENCE = [
  { level: 1, label: "PO Imminent",   full: "PO Imminent — >95%, PO within 60 days",              color: "#16a34a" },
  { level: 2, label: "PO Probable",   full: "PO Probable — >80%, PO within 90 days",              color: "#2ea043" },
  { level: 3, label: "PO Possible",   full: "PO Possible — >50%, PO within 120 days",             color: "#3b82f6" },
  { level: 4, label: "PO Unlikely",   full: "PO Unlikely — within 120 days",                      color: "#a78bfa" },
  { level: 5, label: "Lead Qualified", full: "Lead Qualified, contacts being made — undetermined", color: "#8b949e" },
  { level: 6, label: "Delayed",       full: "Delayed but not Abandoned",                          color: "#f59e0b" },
  { level: 7, label: "At Risk",       full: "Not yet lost but not looking good",                  color: "#ef4444" },
];
const LEAD_SOURCES = ["Trade Show", "SafeSlide", "Architect/Engineer Referral", "Partner Sales Rep", "General Bid", "Construct Connect", "RDP Website", "Called into RDP", "Existing Relationship", "Customer Referral"];
const LEAD_SOURCE_SPECIFY = ["Trade Show", "Architect/Engineer Referral", "Customer Referral"]; // show a "specify" detail field
const oppStatusMeta = id => OPP_STATUSES.find(s => s.id === id) || { label: id, color: T.textMuted };
const oppConf = lvl => OPP_CONFIDENCE.find(c => c.level === Number(lvl));
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

// --- tiny CSV helpers (no dependency) ---
function crmToCsv(rows, cols) {
  const esc = v => { v = v == null ? "" : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
  return [cols.map(c => esc(c.label)).join(","), ...rows.map(r => cols.map(c => esc(c.get(r))).join(","))].join("\n");
}
function crmDownload(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function crmParseCsv(text) {
  const rows = []; let i = 0, field = "", row = [], inQ = false;
  const pushF = () => { row.push(field); field = ""; };
  const pushR = () => { pushF(); rows.push(row); row = []; };
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") pushF();
    else if (c === "\n") pushR();
    else if (c !== "\r") field += c;
    i++;
  }
  if (field.length || row.length) pushR();
  if (!rows.length) return [];
  const headers = rows[0].map(h => h.trim());
  return rows.slice(1)
    .filter(r => r.some(c => c.trim() !== ""))
    .map(r => Object.fromEntries(headers.map((h, idx) => [h, (r[idx] || "").trim()])));
}

function CrmApp({ user }) {
  const [tab, setTab] = useState("dashboard");
  const [opps, setOpps] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [leads, setLeads] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [err, setErr] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [boardMode, setBoardMode] = useState("auto"); // auto | confidence | stage
  const [pipelineLayout, setPipelineLayout] = useState("board"); // board | list
  // Pipeline list filters + sort. plGroup holds a stage id (stage mode) or confidence level (confidence mode).
  const [plGroup, setPlGroup] = useState("");
  const [plStatus, setPlStatus] = useState("");
  const [plAccount, setPlAccount] = useState("");
  const [plSort, setPlSort] = useState({ key: "amount", dir: "desc" });
  const [listLimit, setListLimit] = useState(100);
  const [acctLimit, setAcctLimit] = useState(50);
  const [acctSearch, setAcctSearch] = useState("");
  const [acctOpenOnly, setAcctOpenOnly] = useState(false);
  const [plValueOnly, setPlValueOnly] = useState(false);
  const [detailAcct, setDetailAcct] = useState(null);
  const [detailOpp, setDetailOpp] = useState(null);
  const [showNewOpp, setShowNewOpp] = useState(false);
  const [showNewAcct, setShowNewAcct] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Salesperson scoping: cfg.isAdmin gates the "see all + filter" behaviour; the
  // roster feeds the salesperson picker; salespersonId ("" = everyone) filters client-side.
  const [cfg, setCfg] = useState(null);
  const [roster, setRoster] = useState([]);
  const [salespersonId, setSalespersonId] = useState("");

  const loadBoard = () => crmApi("/opportunities").then(d => setOpps(d.opportunities)).catch(e => setErr(e.message));
  const loadAccounts = () => crmApi("/accounts").then(d => setAccounts(d.accounts)).catch(e => setErr(e.message));
  const loadTasks = () => crmApi("/tasks").then(d => setTasks(d.tasks)).catch(e => setErr(e.message));
  const loadLeads = () => crmApi("/leads").then(d => setLeads(d.leads)).catch(e => setErr(e.message));
  const loadAnalytics = () => crmApi("/analytics").then(setAnalytics).catch(e => setErr(e.message));
  const refreshAll = () => { loadBoard(); loadAccounts(); loadAnalytics(); if (tab === "tasks") loadTasks(); if (tab === "leads") loadLeads(); };

  useEffect(() => { loadBoard(); loadAccounts(); loadAnalytics(); loadLeads(); }, []);
  useEffect(() => { if (tab === "tasks" && !tasks) loadTasks(); if (tab === "leads" && !leads) loadLeads(); }, [tab]); // eslint-disable-line
  useEffect(() => {
    crmApi("/config").then(setCfg).catch(() => {});
    crmApi("/users").then(d => setRoster(d.users || [])).catch(() => {});
  }, []);

  // Roster helpers + client-side salesperson filter (admins only; reps are already
  // scoped server-side).
  const rosterName = (id) => { const u = roster.find(u => u.id === id); return u ? (u.name || u.email) : null; };
  const canFilterSalesperson = cfg?.isAdmin && roster.length > 0;
  const spFilter = (ownerId) => !salespersonId || ownerId === salespersonId;
  const oppsView = (opps || []).filter(o => spFilter(o.ownerId));
  const accountsView = accounts === null ? null : accounts.filter(a => spFilter(a.ownerId));

  const moveOpp = async (id, stage) => {
    setOpps(prev => prev.map(o => o.id === id ? { ...o, stage } : o));
    try { await crmApi(`/opportunities/${id}`, { method: "PATCH", body: JSON.stringify({ stage }) }); loadAnalytics(); }
    catch (e) { setErr(e.message); loadBoard(); }
  };
  const moveOppConfidence = async (id, confidence) => {
    setOpps(prev => prev.map(o => o.id === id ? { ...o, confidence } : o));
    try { await crmApi(`/opportunities/${id}`, { method: "PATCH", body: JSON.stringify({ confidence }) }); }
    catch (e) { setErr(e.message); loadBoard(); }
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const a = analytics?.summary;

  const exportCurrent = () => {
    if (tab === "accounts") {
      crmDownload("crm-accounts.csv", crmToCsv(accounts || [], [
        { label: "name", get: x => x.name }, { label: "status", get: x => x.status },
        { label: "industry", get: x => x.industry || "" }, { label: "website", get: x => x.website || "" },
        { label: "openPipeline", get: x => x.openPipeline || 0 },
        { label: "opportunities", get: x => x.counts?.opportunities ?? 0 },
        { label: "contacts", get: x => x.counts?.contacts ?? 0 },
      ]));
    } else {
      crmDownload("crm-pipeline.csv", crmToCsv(opps || [], [
        { label: "opportunity", get: o => o.name }, { label: "account", get: o => o.account?.name || "" },
        { label: "stage", get: o => o.stage }, { label: "amount", get: o => o.amount || 0 },
        { label: "probability", get: o => o.probability ?? "" },
        { label: "expectedClose", get: o => o.expectedCloseDate ? new Date(o.expectedCloseDate).toISOString().slice(0, 10) : "" },
      ]));
    }
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "28px 32px", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>CRM</h1>
          <p style={{ fontSize: 13, color: T.textSecondary }}>Opportunities, tasks, onboarding & pipeline analytics</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <CrmSearch onOpenAcct={setDetailAcct} onOpenOpp={setDetailOpp} />
          {canFilterSalesperson && (
            <select value={salespersonId} onChange={e => setSalespersonId(e.target.value)} title="Filter by salesperson"
              style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", color: T.textPrimary, fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif", maxWidth: 180 }}>
              <option value="">All salespeople</option>
              {roster.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          )}
          <button className="btn-secondary" style={{ width: "auto", padding: "8px 14px", fontSize: 12 }} onClick={exportCurrent} title={tab === "accounts" ? "Export accounts to CSV" : "Export pipeline to CSV"}>↓ Export</button>
          <button className="btn-secondary" style={{ width: "auto", padding: "8px 14px", fontSize: 12 }} onClick={() => setShowImport(true)}>↑ Import</button>
          <button className="btn-secondary" style={{ width: "auto", padding: "8px 12px", fontSize: 12 }} onClick={() => setShowSettings(true)} title="Products, custom fields & automation">⚙</button>
          <CrmNewMenu onLead={() => setShowNewLead(true)} onOpp={() => setShowNewOpp(true)} onAcct={() => setShowNewAcct(true)} />
        </div>
      </div>

      {err && <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#f87171" }}>⚠ {err} <button onClick={() => { setErr(null); refreshAll(); }} style={{ marginLeft: 8, background: "none", border: "none", color: "#f87171", cursor: "pointer", textDecoration: "underline" }}>retry</button></div>}

      {/* Tabs — funnel order: Leads → Pipeline → Accounts, with Dashboard first */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 20, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, width: "fit-content", flexWrap: "wrap" }}>
        {[["dashboard", "Dashboard"], null, ["leads", "Leads"], "→", ["pipeline", "Pipeline"], "→", ["accounts", "Accounts"], null, ["tasks", "Tasks"], ["activity", "Activity"]].map((t, i) => {
          if (t === null) return <span key={i} style={{ width: 1, height: 18, background: T.border, margin: "0 4px" }} />;
          if (t === "→") return <span key={i} style={{ color: T.textMuted, fontSize: 12, opacity: 0.6 }}>→</span>;
          const [id, label] = t;
          return <button key={id} onClick={() => setTab(id)} style={{ padding: "7px 16px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: tab === id ? T.bg : "transparent", color: tab === id ? T.accent : T.textSecondary }}>{label}</button>;
        })}
      </div>

      {/* ── Dashboard (interactive analytics) ── */}
      {tab === "dashboard" && <CrmDashboard opps={oppsView} leads={leads} accounts={accountsView} analytics={analytics} roster={roster} rosterName={rosterName} onOpenOpp={setDetailOpp} onGoTab={setTab} />}

      {/* ── Leads ── */}
      {tab === "leads" && <CrmLeads leads={leads} reload={loadLeads} onConvert={setConvertLead} onOpenAcct={setDetailAcct} />}

      {/* ── Pipeline board (Confidence for RDP, Stage otherwise) ── */}
      {tab === "pipeline" && (() => {
        const pipeOpps = plValueOnly ? oppsView.filter(o => (o.amount || 0) > 0) : oppsView;
        const hasConf = pipeOpps.some(o => o.confidence);
        const mode = boardMode === "auto" ? (hasConf ? "confidence" : "stage") : boardMode;
        const columns = mode === "confidence"
          ? OPP_CONFIDENCE.map(c => ({ key: c.level, label: `${c.level} · ${c.label}`, color: c.color, match: o => Number(o.confidence) === c.level }))
          : CRM_STAGES.map(s => ({ key: s.id, label: s.label, color: s.color, match: o => o.stage === s.id }));
        const drop = (key) => { if (dragId) { mode === "confidence" ? moveOppConfidence(dragId, key) : moveOpp(dragId, key); } setDragId(null); };
        const openOpps = mode === "confidence" ? pipeOpps.filter(o => !["closed_won", "closed_lost"].includes(o.status)) : pipeOpps;
        const colOf = (o) => columns.find(c => c.match(o));
        return (
          <div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 4, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 3, width: "fit-content" }}>
                {[["auto", "Auto"], ["confidence", "By Confidence"], ["stage", "By Stage"]].map(([id, label]) => (
                  <button key={id} onClick={() => setBoardMode(id)} style={{ padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: boardMode === id ? T.surface : "transparent", color: boardMode === id ? T.accent : T.textSecondary }}>{label}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 4, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 3, width: "fit-content" }}>
                {[["board", "▦ Board"], ["list", "☰ List"]].map(([id, label]) => (
                  <button key={id} onClick={() => setPipelineLayout(id)} style={{ padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: pipelineLayout === id ? T.surface : "transparent", color: pipelineLayout === id ? T.accent : T.textSecondary }}>{label}</button>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSecondary, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={plValueOnly} onChange={e => setPlValueOnly(e.target.checked)} style={{ accentColor: T.accent }} />
                Value &gt; $0 only
              </label>
              <span title={"Confidence = how likely & soon a PO is (RDP's 1–7 scale).\nStage = where the deal sits in the sales process (lead → qualified → demo → proposal).\nDrag a card between columns to update it."} style={{ fontSize: 11, color: T.textMuted, cursor: "help", borderBottom: `1px dotted ${T.border}` }}>
                {mode === "confidence" ? "Grouped by Confidence — how likely/soon a PO is" : "Grouped by Stage — where the deal is in the process"} ⓘ
              </span>
            </div>
            {pipelineLayout === "list" ? (() => {
              // Distinct accounts + statuses present, for the filter dropdowns.
              const acctOpts = Array.from(new Map(openOpps.filter(o => o.account).map(o => [o.account.id, o.account.name])).entries());
              const statusOpts = Array.from(new Set(openOpps.map(o => o.status).filter(Boolean)));
              const groupMatch = (o) => !plGroup || (mode === "confidence" ? String(o.confidence) === plGroup : o.stage === plGroup);
              const rows = openOpps.filter(o => groupMatch(o) && (!plStatus || o.status === plStatus) && (!plAccount || o.account?.id === plAccount));
              const sortVal = (o) => {
                switch (plSort.key) {
                  case "name": return (o.name || "").toLowerCase();
                  case "account": return (o.account?.name || "").toLowerCase();
                  case "group": return mode === "confidence" ? (Number(o.confidence) || 99) : CRM_STAGES.findIndex(s => s.id === o.stage);
                  case "salesperson": return (o.owner?.name || o.owner?.email || rosterName(o.ownerId) || "").toLowerCase();
                  case "meta": return mode === "confidence" ? (o.status || "") : (o.probability ?? -1);
                  case "close": return o.expectedCloseDate ? new Date(o.expectedCloseDate).getTime() : 0;
                  default: return o.amount || 0;
                }
              };
              const sorted = rows.slice().sort((a, b) => { const av = sortVal(a), bv = sortVal(b); const c = av < bv ? -1 : av > bv ? 1 : 0; return plSort.dir === "asc" ? c : -c; });
              const toggleSort = (key) => setPlSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "amount" || key === "close" || key === "meta" ? "desc" : "asc" });
              const arrow = (key) => plSort.key === key ? (plSort.dir === "asc" ? " ▲" : " ▼") : "";
              const cols = [
                ["name", "Opportunity"], ["account", "Account"], ["group", mode === "confidence" ? "Confidence" : "Stage"],
                ["salesperson", "Salesperson"], ["amount", "Amount"], ["meta", mode === "confidence" ? "Status" : "Prob."], ["close", "Close"],
              ];
              const listTotal = sorted.reduce((s, o) => s + (o.amount || 0), 0);
              const sel = { background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px", color: T.textPrimary, fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif" };
              return (
              <div style={card}>
                {/* Filter bar */}
                <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Filter</span>
                  <select value={plGroup} onChange={e => setPlGroup(e.target.value)} style={sel}>
                    <option value="">{mode === "confidence" ? "Any confidence" : "Any stage"}</option>
                    {mode === "confidence"
                      ? OPP_CONFIDENCE.map(c => <option key={c.level} value={String(c.level)}>{c.level} · {c.label}</option>)
                      : CRM_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <select value={plStatus} onChange={e => setPlStatus(e.target.value)} style={sel}>
                    <option value="">Any status</option>
                    {statusOpts.map(s => <option key={s} value={s}>{oppStatusMeta(s)?.label || s}</option>)}
                  </select>
                  <select value={plAccount} onChange={e => setPlAccount(e.target.value)} style={{ ...sel, maxWidth: 220 }}>
                    <option value="">Any account</option>
                    {acctOpts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                  {(plGroup || plStatus || plAccount) ? <button className="btn-secondary" style={{ width: "auto", padding: "0 12px", fontSize: 12, height: 32 }} onClick={() => { setPlGroup(""); setPlStatus(""); setPlAccount(""); }}>Clear</button> : null}
                  <span style={{ marginLeft: "auto", fontSize: 12, color: T.textMuted }}>{sorted.length} deal{sorted.length === 1 ? "" : "s"} · <span style={{ color: T.accent, fontWeight: 600 }}>{crmMoney(listTotal)}</span></span>
                </div>
                {opps === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> :
                  sorted.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No opportunities match these filters.</p> : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
                        {cols.map(([key, label]) => <th key={key} onClick={() => toggleSort(key)} style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, textTransform: "uppercase", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", color: plSort.key === key ? T.accent : T.textMuted }}>{label}{arrow(key)}</th>)}
                      </tr></thead>
                      <tbody>
                        {sorted.slice(0, listLimit).map(o => {
                          const col = colOf(o);
                          return (
                            <tr key={o.id} style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer" }} onClick={() => setDetailOpp(o.id)}>
                              <td style={{ padding: "10px 12px", fontWeight: 600 }}>{o.name}{o.oppNumber ? <span style={{ color: T.textMuted, fontWeight: 400, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}> · {o.oppNumber}</span> : ""}</td>
                              <td style={{ padding: "10px 12px", color: T.textSecondary }}>{o.account?.name || "—"}</td>
                              <td style={{ padding: "10px 12px" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: col?.color || T.textMuted }} />{col?.label || (mode === "confidence" ? "—" : o.stage)}</span></td>
                              <td style={{ padding: "10px 12px", color: T.textSecondary }}>{o.owner?.name || o.owner?.email || rosterName(o.ownerId) || "—"}</td>
                              <td style={{ padding: "10px 12px", color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(o.amount)}</td>
                              <td style={{ padding: "10px 12px", color: T.textMuted }}>{mode === "confidence" ? (oppStatusMeta(o.status)?.label || "—") : `${o.probability ?? 0}%`}</td>
                              <td style={{ padding: "10px 12px", color: T.textMuted }}>{o.expectedCloseDate ? crmDate(o.expectedCloseDate) : "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {sorted.length > listLimit && <button className="btn-secondary" style={{ marginTop: 12, width: "auto", padding: "7px 16px", fontSize: 12 }} onClick={() => setListLimit(l => l + 100)}>Show more ({(sorted.length - listLimit).toLocaleString()} more)</button>}
                  </div>
                )}
              </div>
              );
            })() : (
            <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }}>
              {columns.map(col => {
                const items = openOpps.filter(col.match);
                const total = items.reduce((s, o) => s + (o.amount || 0), 0);
                return (
                  <div key={col.key}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => drop(col.key)}
                    style={{ minWidth: 240, width: 240, flexShrink: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 10, display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px 10px" }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{col.label}</span>
                      <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto" }}>{items.length} · {crmMoney(total)}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 40 }}>
                      {opps === null && <p style={{ fontSize: 12, color: T.textMuted, padding: 8 }}>Loading…</p>}
                      {items.slice(0, 50).map(o => (
                        <div key={o.id} draggable
                          onDragStart={() => setDragId(o.id)} onDragEnd={() => setDragId(null)}
                          onClick={() => setDetailOpp(o.id)}
                          style={{ background: T.bg, border: `1px solid ${T.border}`, borderLeft: `3px solid ${col.color}`, borderRadius: 8, padding: "10px 12px", cursor: "pointer" }}>
                          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{o.name}</p>
                          {o.oppNumber && <p style={{ fontSize: 10, color: T.textMuted, fontFamily: "'JetBrains Mono',monospace", marginBottom: 3 }}>{o.oppNumber}</p>}
                          <p style={{ fontSize: 11, color: T.textSecondary, marginBottom: 6 }}>{o.account?.name}{o.projectState ? ` · ${o.projectState}` : ""}</p>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(o.amount)}</span>
                            <span style={{ fontSize: 10, color: T.textMuted }}>{mode === "confidence" ? (oppStatusMeta(o.status)?.label || "") : `${o.probability}%`}{o._count?.tasks ? ` · ${o._count.tasks}✓` : ""}</span>
                          </div>
                        </div>
                      ))}
                      {items.length > 50 && <p style={{ fontSize: 11, color: T.textMuted, padding: "6px 8px", textAlign: "center" }}>+{(items.length - 50).toLocaleString()} more · switch to List to see all</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        );
      })()}

      {/* ── Accounts ── */}
      {tab === "accounts" && (() => {
        const q = acctSearch.trim().toLowerCase();
        const filtered = (accountsView || []).filter(a =>
          (!q || (a.name || "").toLowerCase().includes(q) || (a.city || "").toLowerCase().includes(q) || (a.industry || "").toLowerCase().includes(q)) &&
          (!acctOpenOnly || (a.openPipeline || 0) > 0));
        return (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <input value={acctSearch} onChange={e => { setAcctSearch(e.target.value); setAcctLimit(50); }} placeholder="Search accounts…" style={{ ...crmInput, marginBottom: 0, maxWidth: 280 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSecondary, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={acctOpenOnly} onChange={e => { setAcctOpenOnly(e.target.checked); setAcctLimit(50); }} style={{ accentColor: T.accent }} />
              Open pipeline only
            </label>
            <span style={{ fontSize: 12, color: T.textMuted, marginLeft: "auto" }}>{filtered.length.toLocaleString()} of {(accountsView || []).length.toLocaleString()} account{(accountsView || []).length === 1 ? "" : "s"}</span>
          </div>
          {accountsView === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> :
            filtered.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>{q ? "No accounts match your search." : `No accounts${salespersonId ? " for this salesperson" : " yet. Add your first with “+ New”"}.`}</p> : (
            <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
                {["Account", "Status", "Salesperson", "Open Pipeline", "Deals", "Contacts", ""].map(h => <th key={h} style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filtered.slice(0, acctLimit).map(ac => (
                  <tr key={ac.id} style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer" }} onClick={() => setDetailAcct(ac.id)}>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{ac.name}{ac.industry ? <span style={{ color: T.textMuted, fontWeight: 400 }}> · {ac.industry}</span> : ""}</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: ac.status === "active" ? "rgba(46,160,67,.15)" : ac.status === "churned" ? "rgba(239,68,68,.15)" : "rgba(148,163,184,.15)", color: ac.status === "active" ? "#2ea043" : ac.status === "churned" ? "#ef4444" : T.textMuted }}>{ac.status}</span></td>
                    <td style={{ padding: "10px 12px", color: T.textSecondary }}>{ac.owner?.name || ac.owner?.email || rosterName(ac.ownerId) || "—"}</td>
                    <td style={{ padding: "10px 12px", color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(ac.openPipeline)}</td>
                    <td style={{ padding: "10px 12px", color: T.textSecondary }}>{ac.counts.opportunities}</td>
                    <td style={{ padding: "10px 12px", color: T.textSecondary }}>{ac.counts.contacts}</td>
                    <td style={{ padding: "10px 12px", color: T.textMuted, textAlign: "right" }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > acctLimit && <button className="btn-secondary" style={{ marginTop: 12, width: "auto", padding: "7px 16px", fontSize: 12 }} onClick={() => setAcctLimit(l => l + 50)}>Show more ({(filtered.length - acctLimit).toLocaleString()} more)</button>}
            </div>
          )}
        </div>
        );
      })()}

      {/* ── Tasks ── */}
      {tab === "tasks" && <CrmTasks reload={loadAnalytics} accounts={accounts} opps={opps} roster={roster} rosterName={rosterName} cfg={cfg} user={user} />}

      {/* ── Activity feed ── */}
      {tab === "activity" && <CrmActivityFeed onOpenAcct={setDetailAcct} onOpenOpp={setDetailOpp} />}

      {showImport && <CrmImportModal onClose={() => setShowImport(false)} onDone={() => { loadAccounts(); loadAnalytics(); }} />}
      {detailAcct && <CrmAccountModal accountId={detailAcct} onClose={() => { setDetailAcct(null); refreshAll(); }} />}
      {detailOpp && <CrmOppModal oppId={detailOpp} onClose={() => { setDetailOpp(null); refreshAll(); }} />}
      {showNewOpp && <CrmNewOpp accounts={accounts} onClose={() => setShowNewOpp(false)} onSaved={() => { setShowNewOpp(false); refreshAll(); }} onNeedAccount={() => { setShowNewOpp(false); setShowNewAcct(true); }} />}
      {showNewAcct && <CrmNewAccount onClose={() => setShowNewAcct(false)} onSaved={() => { setShowNewAcct(false); loadAccounts(); loadAnalytics(); }} />}
      {showSettings && <CrmSettings onClose={() => setShowSettings(false)} />}
      {showNewLead && <CrmNewLead onClose={() => setShowNewLead(false)} onSaved={() => { setShowNewLead(false); setTab("leads"); loadLeads(); }} />}
      {convertLead && <CrmConvertLead lead={convertLead} accounts={accounts} onClose={() => setConvertLead(null)} onDone={(r) => { setConvertLead(null); loadLeads(); refreshAll(); if (r?.opportunityId) setDetailOpp(r.opportunityId); else if (r?.accountId) setDetailAcct(r.accountId); }} />}
    </div>
  );
}

const crmModalWrap = { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 };
const crmInput = { width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "9px 12px", color: T.textPrimary, fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 10, colorScheme: "dark" };

// ── Global search across accounts / contacts / opportunities ──
function CrmSearch({ onOpenAcct, onOpenOpp }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const id = setTimeout(() => {
      crmApi(`/search?q=${encodeURIComponent(q.trim())}`).then(d => { setRes(d); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  const total = res ? res.accounts.length + res.contacts.length + res.opportunities.length : 0;
  const pick = (fn) => { fn(); setOpen(false); setQ(""); setRes(null); };
  const itemStyle = { padding: "8px 12px", cursor: "pointer", borderTop: `1px solid ${T.border}`, fontSize: 13 };
  return (
    <div style={{ position: "relative" }}>
      <input
        value={q} onChange={e => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search CRM…"
        style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", color: T.textPrimary, fontSize: 13, width: 220, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 320, maxHeight: 380, overflowY: "auto", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, zIndex: 1100, boxShadow: "0 10px 30px rgba(0,0,0,.4)" }}>
          {total === 0 && <p style={{ padding: 14, fontSize: 13, color: T.textMuted }}>No matches</p>}
          {res?.accounts.length > 0 && <p style={{ padding: "8px 12px 4px", fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Accounts</p>}
          {res?.accounts.map(ac => (
            <div key={ac.id} style={itemStyle} onMouseDown={() => pick(() => onOpenAcct(ac.id))}>
              <span style={{ fontWeight: 600 }}>{ac.name}</span>{ac.industry ? <span style={{ color: T.textMuted }}> · {ac.industry}</span> : ""}
            </div>
          ))}
          {res?.opportunities.length > 0 && <p style={{ padding: "8px 12px 4px", fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Opportunities</p>}
          {res?.opportunities.map(o => (
            <div key={o.id} style={itemStyle} onMouseDown={() => pick(() => onOpenOpp(o.id))}>
              <span style={{ fontWeight: 600 }}>{o.name}</span> <span style={{ color: T.accent }}>{crmMoney(o.amount)}</span>
              <div style={{ fontSize: 11, color: T.textMuted }}>{o.account?.name}</div>
            </div>
          ))}
          {res?.contacts.length > 0 && <p style={{ padding: "8px 12px 4px", fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Contacts</p>}
          {res?.contacts.map(c => (
            <div key={c.id} style={itemStyle} onMouseDown={() => c.account && pick(() => onOpenAcct(c.account.id))}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>{c.role ? <span style={{ color: T.textMuted }}> · {c.role}</span> : ""}
              <div style={{ fontSize: 11, color: T.textMuted }}>{c.account?.name}{c.email ? ` · ${c.email}` : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Unified recent-activity feed ──
function CrmActivityFeed({ onOpenAcct, onOpenOpp }) {
  const [acts, setActs] = useState(null);
  const [preset, setPreset] = useState(0); // days; 0 = all
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => { crmApi("/activities?limit=500").then(d => setActs(d.activities)).catch(() => setActs([])); }, []);
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  if (acts === null) return <div style={card}><p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p></div>;
  const inRange = (d) => {
    const t = new Date(d).getTime();
    if (from && t < new Date(from).getTime()) return false;
    if (to && t > new Date(to).getTime() + 86399999) return false; // include the whole "to" day
    if (!from && !to && preset && (Date.now() - t) > preset * 86400000) return false;
    return true;
  };
  const filtered = acts.filter(a => inRange(a.createdAt));
  const groups = {};
  for (const a of filtered) { const k = new Date(a.createdAt).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }); (groups[k] ||= []).push(a); }
  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Date-range filter */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 3, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 3 }}>
          {[[0, "All"], [7, "7 days"], [30, "30 days"], [90, "90 days"]].map(([d, l]) => {
            const active = !from && !to && preset === d;
            return <button key={d} onClick={() => { setPreset(d); setFrom(""); setTo(""); }} style={{ padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: active ? T.surface : "transparent", color: active ? T.accent : T.textSecondary }}>{l}</button>;
          })}
        </div>
        <span style={{ fontSize: 11, color: T.textMuted }}>or</span>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 150 }} />
        <span style={{ fontSize: 12, color: T.textMuted }}>→</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 150 }} />
        {(from || to) ? <button className="btn-secondary" style={{ width: "auto", padding: "0 12px", fontSize: 12, height: 34 }} onClick={() => { setFrom(""); setTo(""); }}>Clear</button> : null}
        <span style={{ fontSize: 12, color: T.textMuted, marginLeft: "auto" }}>{filtered.length} activit{filtered.length === 1 ? "y" : "ies"}</span>
      </div>
      {filtered.length === 0 ? (
        <p style={{ color: T.textMuted, fontSize: 13 }}>{acts.length === 0 ? "No activity yet. Log calls, emails and notes from any account or opportunity." : "No activity in this date range."}</p>
      ) : Object.entries(groups).map(([day, items]) => (
        <div key={day}>
          <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>{day}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map(ac => { const m = crmActMeta(ac.type); return (
              <div key={ac.id} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ fontSize: 15, width: 22, textAlign: "center", flexShrink: 0 }}>{m.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: T.textPrimary }}>{ac.body}</p>
                  <p style={{ fontSize: 11, color: T.textMuted }}>
                    {ac.authorName ? `${ac.authorName} · ` : ""}{new Date(ac.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {ac.opportunity ? <> · <span style={{ color: T.accent, cursor: "pointer" }} onClick={() => onOpenOpp(ac.opportunity.id)}>{ac.opportunity.name}</span></>
                      : ac.account ? <> · <span style={{ color: T.accent, cursor: "pointer" }} onClick={() => onOpenAcct(ac.account.id)}>{ac.account.name}</span></> : null}
                  </p>
                </div>
              </div>
            ); })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── CSV import (accounts or contacts) ──
function CrmImportModal({ onClose, onDone }) {
  const [type, setType] = useState("accounts");
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const rows = text.trim() ? crmParseCsv(text) : [];
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setText(String(r.result || "")); r.readAsText(f); };
  const run = async () => {
    if (!rows.length) return;
    setBusy(true); setResult(null);
    try { const d = await crmApi("/import", { method: "POST", body: JSON.stringify({ type, rows }) }); setResult(d); onDone(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  };
  const headers = type === "accounts"
    ? "name, website, industry, status, contactName, contactEmail, contactRole"
    : "name, email, phone, role, account";
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Import from CSV</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[["accounts", "Accounts"], ["contacts", "Contacts"]].map(([id, label]) => (
            <button key={id} onClick={() => { setType(id); setResult(null); }} style={{ padding: "6px 16px", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${type === id ? T.accent : T.border}`, background: type === id ? T.accent + "22" : "transparent", color: type === id ? T.accent : T.textSecondary }}>{label}</button>
          ))}
        </div>
        <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 6 }}>First row must be a header. Recognised columns:</p>
        <p style={{ fontSize: 12, color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>{headers}</p>
        {type === "contacts" && <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>The <strong>account</strong> column must match an existing account name exactly.</p>}
        <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }} />
        <textarea value={text} onChange={e => { setText(e.target.value); setResult(null); }} placeholder="…or paste CSV here" rows={7} style={{ ...crmInput, fontFamily: "'JetBrains Mono',monospace", resize: "vertical" }} />
        {rows.length > 0 && !result && <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12 }}>{rows.length} row{rows.length === 1 ? "" : "s"} ready to import.</p>}
        {result && (
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
            <p style={{ color: "#2ea043", fontWeight: 600 }}>✓ Imported {result.created}{result.skipped ? ` · skipped ${result.skipped}` : ""}</p>
            {result.errors?.length > 0 && <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: T.textMuted, fontSize: 12 }}>{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={busy || !rows.length} onClick={run}>{busy ? "Importing…" : `Import ${rows.length || ""}`.trim()}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>{result ? "Done" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

function CrmTasks({ reload, accounts, opps, roster = [], rosterName, cfg, user }) {
  const isAdmin = !!cfg?.isAdmin;
  const [view, setView] = useState("mine"); // mine (assigned to me) | requested (I requested) | all
  const [tasks, setTasks] = useState(null);
  const [counts, setCounts] = useState(null);
  const [assigners, setAssigners] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [q, setQ] = useState("");
  const [fAccount, setFAccount] = useState("");
  const [fOpp, setFOpp] = useState("");
  const [fPerson, setFPerson] = useState("");
  const [fGroup, setFGroup] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fPri, setFPri] = useState(""); // assigned priority (client-side)
  const [fAssigned, setFAssigned] = useState(""); // "" all | "1" assigned | "0" unassigned
  const nameFor = rosterName || (id => { const u = roster.find(u => u.id === id); return u ? (u.name || u.email) : null; });
  const isAssigner = isAdmin || assigners.some(a => (a.assignerEmail || "").toLowerCase() === (user?.email || "").toLowerCase());

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (view === "mine") p.set("scope", "mine");
    else if (view === "requested") p.set("scope", "requested");
    else if (view === "all" && !showCompleted && !fStatus) p.set("scope", "active");
    if (view !== "mine" && fAssigned) p.set("assigned", fAssigned);
    if (q.trim()) p.set("q", q.trim());
    if (fAccount) p.set("accountId", fAccount);
    if (fOpp) p.set("opportunityId", fOpp);
    if (fPerson) p.set("personId", fPerson);
    if (fGroup) p.set("assignmentGroup", fGroup);
    if (fStatus) p.set("status", fStatus);
    return p.toString();
  };
  const load = () => { setTasks(null); crmApi("/tasks?" + buildQuery()).then(d => setTasks(d.tasks)).catch(() => setTasks([])); };
  const loadCounts = () => crmApi("/tasks?counts=1").then(d => setCounts(d.counts)).catch(() => {});
  useEffect(() => { load(); }, [view, q, fAccount, fOpp, fPerson, fGroup, fStatus, fAssigned, showCompleted]); // eslint-disable-line
  useEffect(() => { loadCounts(); crmApi("/task-assigners").then(d => setAssigners(d.assigners || [])).catch(() => {}); }, []); // eslint-disable-line
  const refresh = () => { load(); loadCounts(); reload && reload(); };

  const visible = (tasks || []).filter(t => (showCompleted || !taskIsTerminal(t.status)) && (!fPri || Number(t.assignedPriority) === Number(fPri)));
  const bs = counts?.byStatus || {};
  const catCount = cat => Object.entries(bs).reduce((s, [k, v]) => taskCategory(k) === cat ? s + v : s, 0);
  const kpis = [
    { label: "Awaiting", value: catCount("awaiting"), color: "#f59e0b", icon: "📥", onClick: () => { setView("all"); setFStatus("requested"); } },
    { label: "Queued", value: catCount("queued"), color: "#a78bfa", icon: "📋", onClick: () => { setView("all"); setFStatus("approved"); } },
    { label: "In Progress", value: catCount("progress"), color: "#3b82f6", icon: "⚙️", onClick: () => { setView("all"); setFStatus("in_progress"); } },
    { label: "Delayed", value: catCount("delayed"), color: "#ef4444", icon: "⏸️" },
    { label: "My Open", value: counts?.mineOpen ?? 0, color: "#22c55e", icon: "🙋", onClick: () => { setView("mine"); setFStatus(""); } },
    { label: "Completed", value: catCount("completed"), color: "#8b949e", icon: "✅", onClick: () => { setView("all"); setFStatus("completed"); } },
  ];
  // Small per-priority sub-cards (open tasks) + Unassigned.
  const byPri = counts?.byPriority || {};
  const priCards = [...TASK_ASSIGNED_PRIORITIES.map(p => ({ n: String(p.n), label: p.label, color: p.color, value: byPri[p.n] || 0, onClick: () => { setFPri(String(p.n)); setFAssigned(""); } })),
    { n: "unassigned", label: "Unassigned", color: "#8b949e", value: counts?.openUnassigned || 0, onClick: () => { setView("all"); setFAssigned("0"); setFPri(""); } }];
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const sel = { ...crmInput, marginBottom: 0 };

  const priChip = (n) => { const p = taskAssignedPri(n); return p ? <span title={p.rule || p.label} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, background: p.color + "26", color: p.color, fontWeight: 800, fontSize: 12, flexShrink: 0 }}>{n}</span> : <span style={{ width: 22, textAlign: "center", color: T.textMuted, flexShrink: 0 }}>–</span>; };
  const statusBadge = (c) => { const col = taskStatusColor(c); return <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: col + "22", color: col, whiteSpace: "nowrap" }}>{taskStatusLabel(c)}</span>; };

  const row = (t) => {
    const overdue = t.dueDate && !taskIsTerminal(t.status) && new Date(t.dueDate).getTime() < Date.now();
    const p = taskAssignedPri(t.assignedPriority);
    const reqName = t.requestor?.name || t.requestor?.email || nameFor(t.requestorId) || "—";
    const asgName = t.assignee?.name || t.assignee?.email || nameFor(t.assigneeId) || "unassigned";
    return (
      <div key={t.id} onClick={() => setSelected(t)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 8px", borderTop: `1px solid ${T.border}`, cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, width: 108, flexShrink: 0 }}>
          {priChip(t.assignedPriority)}
          {p && <span style={{ fontSize: 11, color: p.color, whiteSpace: "nowrap" }}>{p.label}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: taskIsTerminal(t.status) ? T.textMuted : T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</p>
          <p style={{ fontSize: 11, color: T.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {t.assignmentGroup ? t.assignmentGroup + " · " : ""}{view === "requested" ? <>→ {asgName}</> : <>{reqName} → {asgName}</>}
            {t.account?.name ? ` · 🏢 ${t.account.name}` : ""}{t.opportunity?.name ? ` · 🎯 ${t.opportunity.name}` : ""}{t.regarding ? ` · re: ${t.regarding}` : ""}
          </p>
        </div>
        {t.dueDate && <span style={{ fontSize: 11, color: overdue ? "#ef4444" : T.textMuted, whiteSpace: "nowrap" }}>{overdue ? "Overdue " : ""}{crmDate(t.dueDate)}</span>}
        {statusBadge(t.status)}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
        {kpis.map(k => (
          <div key={k.label} onClick={k.onClick} style={{ background: `linear-gradient(140deg, ${k.color}22, ${T.surface} 62%)`, border: `1px solid ${k.color}55`, borderRadius: 12, padding: "12px 14px", cursor: k.onClick ? "pointer" : "default" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>{k.icon}</span>
              <span style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em" }}>{k.label}</span>
            </div>
            <p style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{counts ? k.value.toLocaleString() : "…"}</p>
          </div>
        ))}
      </div>

      {/* Priority sub-cards (open tasks by assigned priority + unassigned) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 8 }}>
        {priCards.map(p => {
          const active = (p.n !== "unassigned" && fPri === p.n) || (p.n === "unassigned" && fAssigned === "0");
          return (
            <div key={p.n} onClick={p.onClick} style={{ display: "flex", alignItems: "center", gap: 8, background: active ? p.color + "22" : T.surface, border: `1px solid ${active ? p.color : T.border}`, borderRadius: 10, padding: "8px 12px", cursor: "pointer" }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, background: p.color + "26", color: p.color, fontWeight: 800, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{p.n === "unassigned" ? "–" : p.n}</span>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, lineHeight: 1 }}>{counts ? p.value.toLocaleString() : "…"}</p>
                <p style={{ fontSize: 10, color: T.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* View tabs + New */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 3, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4 }}>
          {[["mine", "Assigned to Me"], ["requested", "I Requested"], ["all", "All Tasks"]].map(([id, label]) => (
            <button key={id} onClick={() => { setView(id); setFStatus(""); setFAssigned(""); }} style={{ padding: "7px 14px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: view === id ? T.bg : "transparent", color: view === id ? T.accent : T.textSecondary }}>{label}</button>
          ))}
        </div>
        <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 13, marginLeft: "auto" }} onClick={() => setShowNew(true)}>+ Request Task</button>
      </div>

      {/* Search + filters */}
      <div style={card}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search title, description or Regarding…" style={{ ...sel, flex: 1, minWidth: 200 }} />
          <select value={fAccount} onChange={e => { setFAccount(e.target.value); setFOpp(""); }} style={{ ...sel, width: 170 }}><option value="">All accounts</option>{(accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
          <select value={fOpp} onChange={e => setFOpp(e.target.value)} style={{ ...sel, width: 170 }}><option value="">All opportunities</option>{(opps || []).filter(o => !fAccount || o.account?.id === fAccount).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
          {roster.length > 0 && <select value={fPerson} onChange={e => setFPerson(e.target.value)} style={{ ...sel, width: 160 }}><option value="">Any salesperson</option>{roster.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</select>}
          <select value={fGroup} onChange={e => setFGroup(e.target.value)} style={{ ...sel, width: 150 }}><option value="">Any group</option>{TASK_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}</select>
          <select value={fPri} onChange={e => setFPri(e.target.value)} style={{ ...sel, width: 130 }}><option value="">Any priority</option>{TASK_ASSIGNED_PRIORITIES.map(p => <option key={p.n} value={p.n}>{p.n} · {p.label}</option>)}</select>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ ...sel, width: 190 }}><option value="">Any status</option>{TASK_STATUSES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}</select>
          {view !== "mine" && <select value={fAssigned} onChange={e => setFAssigned(e.target.value)} style={{ ...sel, width: 140 }}><option value="">All (assign.)</option><option value="1">Assigned</option><option value="0">Unassigned</option></select>}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSecondary, cursor: "pointer" }}><input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} style={{ accentColor: T.accent }} />Show completed</label>
          {(q || fAccount || fOpp || fPerson || fGroup || fPri || fStatus || fAssigned) ? <button className="btn-secondary" style={{ width: "auto", padding: "0 12px", fontSize: 12, height: 34 }} onClick={() => { setQ(""); setFAccount(""); setFOpp(""); setFPerson(""); setFGroup(""); setFPri(""); setFStatus(""); setFAssigned(""); }}>Clear</button> : null}
        </div>

        {/* List */}
        <div style={{ marginTop: 10 }}>
          {tasks === null ? <p style={{ color: T.textMuted, fontSize: 13, padding: "16px 0" }}>Loading…</p> :
            visible.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13, padding: "16px 0" }}>{view === "mine" ? "No open tasks assigned to you. 🎉" : view === "requested" ? "You haven't requested any open tasks." : "No tasks match."}</p> :
            visible.map(row)}
          {tasks && tasks.length >= 1000 && <p style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>Showing first 1,000 — refine filters to narrow.</p>}
        </div>
      </div>

      {showNew && <CrmNewTaskModal accounts={accounts} opps={opps} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}
      {selected && <CrmTaskModal task={selected} roster={roster} accounts={accounts} opps={opps} nameFor={nameFor} canAssign={isAssigner} onClose={() => setSelected(null)} onChanged={refresh} />}
    </div>
  );
}

// Rich request form (Task Owner = Requestor). Priority is set later by the assigner.
function CrmNewTaskModal({ accounts, opps, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", assignmentGroup: "", regarding: "", description: "", accountId: "", opportunityId: "", dueDate: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const descRef = useRef(null);
  const ins = (snippet) => {
    const el = descRef.current; const cur = f.description || "";
    if (el && typeof el.selectionStart === "number") {
      const s = el.selectionStart, e = el.selectionEnd;
      const next = cur.slice(0, s) + snippet + cur.slice(e);
      setF(v => ({ ...v, description: next }));
      setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = s + snippet.length; }, 0);
    } else setF(v => ({ ...v, description: cur + snippet }));
  };
  const save = async () => {
    if (!f.title.trim()) { setMsg("Title is required"); return; }
    if (!f.assignmentGroup) { setMsg("Assignment group is required"); return; }
    setSaving(true); setMsg("");
    try {
      const r = await crmApi("/tasks", { method: "POST", body: JSON.stringify({ ...f, dueDate: f.dueDate || null, accountId: f.accountId || null, opportunityId: f.opportunityId || null }) });
      onSaved(r);
    } catch (e) { setMsg(e.message); setSaving(false); }
  };
  const tools = [["📞 Call", "📞 Call: "], ["✉️ Email", "✉️ Email: "], ["📝 Note", "📝 Note: "], ["✅", "✅ "], ["⚠️", "⚠️ "], ["🕐 Stamp", `[${new Date().toLocaleString()}] `], ["— line", "\n————————\n"]];
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 620, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Request a Task</h2>
        <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>You are the requestor. Once submitted, the group's Task Assigner sets the priority and assigns it.</p>
        <input style={crmInput} placeholder="Task title *" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} />
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <select style={{ ...crmInput, marginBottom: 0 }} value={f.assignmentGroup} onChange={e => setF({ ...f, assignmentGroup: e.target.value })}><option value="">Assignment group *</option>{TASK_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}</select>
          <input style={{ ...crmInput, marginBottom: 0 }} placeholder="Regarding (order/project ref)" value={f.regarding} onChange={e => setF({ ...f, regarding: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <select style={{ ...crmInput, marginBottom: 0 }} value={f.accountId} onChange={e => setF({ ...f, accountId: e.target.value, opportunityId: "" })}><option value="">Link account</option>{(accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
          <select style={{ ...crmInput, marginBottom: 0 }} value={f.opportunityId} onChange={e => setF({ ...f, opportunityId: e.target.value })}><option value="">Link opportunity</option>{(opps || []).filter(o => !f.accountId || o.account?.id === f.accountId).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
          <input type="date" style={{ ...crmInput, marginBottom: 0, width: 150 }} value={f.dueDate} onChange={e => setF({ ...f, dueDate: e.target.value })} />
        </div>
        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em", margin: "4px 0 6px" }}>Description / Notes</p>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
          {tools.map(([label, snip]) => <button key={label} onClick={() => ins(snip)} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 8px", fontSize: 12, color: T.textSecondary, cursor: "pointer" }}>{label}</button>)}
        </div>
        <textarea ref={descRef} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Describe the task and where the notes came from — use the buttons above to log calls, emails, notes…" style={{ ...crmInput, minHeight: 160, resize: "vertical", lineHeight: 1.5 }} />
        {msg && <p style={{ fontSize: 12, color: "#f87171", marginBottom: 8 }}>{msg}</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Submitting…" : "Submit Request"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Task detail: assign (priority 1–5 + assignee), move through the 17 statuses, delete.
function CrmTaskModal({ task: initial, roster, accounts, opps, nameFor, canAssign, onClose, onChanged }) {
  const [t, setT] = useState(initial);
  const [assigneeId, setAssigneeId] = useState(initial.assigneeId || "");
  const [pri, setPri] = useState(initial.assignedPriority || "");
  const [busy, setBusy] = useState(false);
  const patch = async (body) => {
    setBusy(true);
    try {
      const d = await crmApi(`/tasks/${t.id}`, { method: "PATCH", body: JSON.stringify(body) });
      setT(prev => ({ ...prev, ...d.task, requestor: prev.requestor, account: prev.account, opportunity: prev.opportunity, assignee: d.task.assigneeId === prev.assigneeId ? prev.assignee : null }));
      onChanged && onChanged();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const approve = () => { if (!assigneeId || !pri) { alert("Pick an assignee and a priority"); return; } patch({ status: "approved", assigneeId, assignedPriority: Number(pri) }); };
  const del = async () => { if (!window.confirm("Delete this task?")) return; await crmApi(`/tasks/${t.id}`, { method: "DELETE" }); onChanged && onChanged(); onClose(); };
  const col = taskStatusColor(t.status);
  const field = { fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 620, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24, borderTop: `3px solid ${col}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>{t.title}</h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: col + "22", color: col }}>{taskStatusLabel(t.status)}</span>
              {t.assignedPriority && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: (taskAssignedPri(t.assignedPriority)?.color || T.accent) + "22", color: taskAssignedPri(t.assignedPriority)?.color }}>{t.assignedPriority} · {taskAssignedPri(t.assignedPriority)?.label}</span>}
              {t.assignmentGroup && <span style={{ fontSize: 11, color: T.textSecondary }}>{t.assignmentGroup}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", margin: "16px 0", fontSize: 13 }}>
          <div><p style={field}>Requestor</p><p style={{ color: T.textPrimary }}>{t.requestor?.name || t.requestor?.email || nameFor(t.requestorId) || "—"}</p></div>
          <div><p style={field}>Assigned to</p><p style={{ color: T.textPrimary }}>{t.assignee?.name || t.assignee?.email || nameFor(t.assigneeId) || "unassigned"}</p></div>
          <div><p style={field}>Regarding</p><p style={{ color: T.textPrimary }}>{t.regarding || "—"}</p></div>
          <div><p style={field}>Due</p><p style={{ color: T.textPrimary }}>{t.dueDate ? crmDate(t.dueDate) : "—"}</p></div>
          {(t.opportunity?.name || t.account?.name) && <div><p style={field}>Linked</p><p style={{ color: T.accent }}>{t.opportunity?.name || t.account?.name}</p></div>}
        </div>

        {t.description && <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px", fontSize: 13, color: T.textSecondary, whiteSpace: "pre-wrap", marginBottom: 16, lineHeight: 1.5 }}>{t.description}</div>}

        {/* Assign panel */}
        {canAssign && (
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, marginBottom: 10 }}>ASSIGN & PRIORITISE</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 190 }}><option value="">Assign to…</option>{roster.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</select>
              <select value={pri} onChange={e => setPri(e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 210 }}><option value="">Priority…</option>{TASK_ASSIGNED_PRIORITIES.map(p => <option key={p.n} value={p.n}>{p.n} · {p.label}{p.rule ? " — " + p.rule : ""}</option>)}</select>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} disabled={busy} onClick={approve}>Approve &amp; Assign</button>
            </div>
          </div>
        )}

        {/* Status + delete */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em" }}>Set status</span>
          <select value={t.status} onChange={e => patch({ status: e.target.value })} disabled={busy} style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 220 }}>
            {TASK_STATUSES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
          <button onClick={del} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", color: "#ef4444", cursor: "pointer", fontSize: 12 }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── HUB: company-wide task workflow (requested → approved/assigned → done) ─────
function HubApp({ user }) {
  const myEmail = (user?.email || "").toLowerCase();
  const [tasks, setTasks] = useState(null);
  const [users, setUsers] = useState([]);
  const [assigners, setAssigners] = useState([]);
  const [tab, setTab] = useState("mine");
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [approve, setApprove] = useState(null);

  const loadTasks = () => crmApi("/tasks?scope=active").then(d => setTasks(d.tasks)).catch(e => setErr(e.message));
  const loadUsers = () => crmApi("/users").then(d => setUsers(d.users)).catch(() => {});
  const loadAssigners = () => crmApi("/task-assigners").then(d => setAssigners(d.assigners)).catch(() => {});
  useEffect(() => { loadTasks(); loadUsers(); loadAssigners(); }, []);

  const isAdmin = isDeveloper(user?.email);
  const list = tasks || [];
  const myGroups = assigners.filter(a => (a.assignerEmail || "").toLowerCase() === myEmail).map(a => a.assignmentGroup);
  const awaiting = list.filter(t => t.status === "requested" && myGroups.includes(t.assignmentGroup));
  const mine = list.filter(t => (t.assignee?.email || "").toLowerCase() === myEmail)
    .sort((a, b) => (a.assignedPriority || 99) - (b.assignedPriority || 99));

  const setStatus = async (t, status) => {
    try { await crmApi(`/tasks/${t.id}`, { method: "PATCH", body: JSON.stringify({ status }) }); loadTasks(); }
    catch (e) { setErr(e.message); }
  };

  // All active work, grouped by assignee (unassigned bucket last).
  const byAssignee = {};
  for (const t of list) {
    const key = t.assignee ? (t.assignee.name || t.assignee.email) : "· Unassigned";
    (byAssignee[key] = byAssignee[key] || []).push(t);
  }
  const assigneeGroups = Object.entries(byAssignee).sort((a, b) =>
    a[0] === "· Unassigned" ? 1 : b[0] === "· Unassigned" ? -1 : a[0].localeCompare(b[0]));

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const tabs = [
    { id: "mine", label: `My Queue${mine.length ? ` · ${mine.length}` : ""}` },
    { id: "active", label: `All Active · ${list.length}` },
    ...(myGroups.length ? [{ id: "awaiting", label: `Awaiting Me · ${awaiting.length}` }] : []),
    ...(isAdmin ? [{ id: "setup", label: "Setup" }] : []),
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Hub</h1>
          <p style={{ fontSize: 12, color: T.textMuted }}>Every task, who's on it, where it sits, and why — across the whole business.</p>
        </div>
        <button className="btn-primary" style={{ width: "auto", padding: "0 18px", fontSize: 13, marginLeft: "auto" }} onClick={() => setShowNew(true)}>+ New Task</button>
      </div>

      {err && <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", color: "#ef4444", borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>{err} <button onClick={() => setErr("")} style={{ float: "right", background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}>✕</button></div>}

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{ background: tab === tb.id ? T.accent : "transparent", color: tab === tb.id ? "#fff" : T.textSecondary, border: `1px solid ${tab === tb.id ? T.accent : T.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{tb.label}</button>
        ))}
      </div>

      {tasks === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> : (
        <>
          {tab === "mine" && (
            <div style={card}>
              {mine.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>Nothing in your queue. Tasks assigned to you appear here, ordered by priority.</p>
                : mine.map(t => <HubTaskRow key={t.id} t={t} onStatus={setStatus} />)}
            </div>
          )}

          {tab === "active" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {list.length === 0 ? <div style={card}><p style={{ color: T.textMuted, fontSize: 13 }}>No active tasks.</p></div>
                : assigneeGroups.map(([name, items]) => (
                  <div key={name} style={card}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: name === "· Unassigned" ? T.textMuted : T.textSecondary, marginBottom: 6 }}>{name} <span style={{ color: T.textMuted, fontWeight: 400 }}>· {items.length}</span></p>
                    {items.sort((a, b) => (a.assignedPriority || 99) - (b.assignedPriority || 99)).map(t => <HubTaskRow key={t.id} t={t} onStatus={setStatus} />)}
                  </div>
                ))}
            </div>
          )}

          {tab === "awaiting" && (
            <div style={card}>
              {awaiting.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No tasks waiting on you to approve & assign.</p>
                : awaiting.map(t => <HubTaskRow key={t.id} t={t} onStatus={setStatus} onAssign={() => setApprove(t)} />)}
            </div>
          )}

          {tab === "setup" && <HubSetup assigners={assigners} users={users} reload={() => { loadAssigners(); loadUsers(); }} />}
        </>
      )}

      {showNew && <HubNewTaskModal onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); loadTasks(); }} />}
      {approve && <HubApproveModal task={approve} users={users} onClose={() => setApprove(null)} onDone={() => { setApprove(null); loadTasks(); }} />}
    </div>
  );
}

function HubTaskRow({ t, onStatus, onAssign }) {
  const rp = taskReqPri(t.requestedPriority);
  const due = t.dueDate ? new Date(t.dueDate) : null;
  const overdue = due && due < new Date() && !TASK_TERMINAL.includes(t.status);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      <span title={rp.label + " priority"} style={{ width: 8, height: 8, borderRadius: "50%", background: rp.color, flexShrink: 0 }} />
      {t.assignedPriority ? <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, fontFamily: "'JetBrains Mono',monospace", width: 20 }}>#{t.assignedPriority}</span> : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.textPrimary }}>{t.title}</p>
        <p style={{ fontSize: 11, color: overdue ? "#ef4444" : T.textMuted }}>
          {t.assignmentGroup || "—"}
          {t.requestor ? ` · req ${t.requestor.name || t.requestor.email}` : ""}
          {t.assignee ? ` → ${t.assignee.name || t.assignee.email}` : ""}
          {due ? ` · ${overdue ? "Overdue " : "due "}${crmDate(t.dueDate)}` : ""}
          {t.opportunity?.name || t.account?.name ? ` · ${t.opportunity?.name || t.account?.name}` : ""}
        </p>
      </div>
      <select value={t.status} onChange={e => onStatus(t, e.target.value)} title="Status"
        style={{ background: T.bg, color: taskStatusColor(t.status), border: `1px solid ${T.border}`, borderRadius: 8, padding: "5px 8px", fontSize: 11, fontWeight: 600, maxWidth: 210, cursor: "pointer" }}>
        {TASK_STATUSES.map(s => <option key={s.code} value={s.code} style={{ color: T.textPrimary }}>{s.label}</option>)}
      </select>
      {onAssign && <button className="btn-primary" style={{ width: "auto", padding: "0 12px", fontSize: 12, height: 30 }} onClick={onAssign}>Assign</button>}
    </div>
  );
}

function HubApproveModal({ task, users, onClose, onDone }) {
  const [assigneeId, setAssigneeId] = useState(task.assigneeId || "");
  const [pri, setPri] = useState(task.assignedPriority || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!assigneeId) { setErr("Pick who to assign it to."); return; }
    if (!pri) { setErr("Set a queue priority (1–5)."); return; }
    setSaving(true);
    try {
      await crmApi(`/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify({ status: "approved", assigneeId, assignedPriority: Number(pri) }) });
      onDone();
    } catch (e) { setErr(e.message); setSaving(false); }
  };
  return <HubModal title="Approve & Assign" onClose={onClose}>
    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{task.title}</p>
    <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 14 }}>{task.assignmentGroup} · requested {taskReqPri(task.requestedPriority).label}{task.dueDate ? ` · due ${crmDate(task.dueDate)}` : ""}</p>
    <label style={hubLbl}>Assign to</label>
    <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} style={crmInput}>
      <option value="">— Select staff member —</option>
      {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}{u.pending ? " (pending login)" : ""}</option>)}
    </select>
    <label style={hubLbl}>Assigned priority (queue rank)</label>
    <select value={pri} onChange={e => setPri(e.target.value)} style={crmInput}>
      <option value="">— Select 1–5 —</option>
      <option value="1">1 · Critical — work on nothing else until complete</option>
      <option value="2">2 · Critical — start immediately after #1</option>
      <option value="3">3 · High</option>
      <option value="4">4 · Medium</option>
      <option value="5">5 · Low</option>
    </select>
    {err && <p style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>{err}</p>}
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
      <button className="btn-secondary" style={{ width: "auto", padding: "0 16px" }} onClick={onClose}>Cancel</button>
      <button className="btn-primary" style={{ width: "auto", padding: "0 18px" }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Approve & Assign"}</button>
    </div>
  </HubModal>;
}

function HubNewTaskModal({ onClose, onDone }) {
  const blank = { title: "", assignmentGroup: "", requestedPriority: "medium", dueDate: "", regarding: "", description: "" };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.title.trim()) { setErr("Title is required."); return; }
    if (!f.assignmentGroup) { setErr("Assignment group is required."); return; }
    setSaving(true);
    try {
      const r = await crmApi("/tasks", { method: "POST", body: JSON.stringify(f) });
      onDone(r.emailed);
    } catch (e) { setErr(e.message); setSaving(false); }
  };
  const set = k => e => setF({ ...f, [k]: e.target.value });
  return <HubModal title="New Task" onClose={onClose} wide>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={hubLbl}>Task title *</label>
        <input value={f.title} onChange={set("title")} style={crmInput} placeholder="Short summary of what needs doing" />
      </div>
      <div><label style={hubLbl}>Assignment group *</label>
        <select value={f.assignmentGroup} onChange={set("assignmentGroup")} style={crmInput}>
          <option value="">— Select group —</option>
          {TASK_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
        </select></div>
      <div><label style={hubLbl}>Requested priority</label>
        <select value={f.requestedPriority} onChange={set("requestedPriority")} style={crmInput}>
          {TASK_REQ_PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select></div>
      <div><label style={hubLbl}>Due date</label>
        <input type="date" value={f.dueDate} onChange={set("dueDate")} style={crmInput} /></div>
      <div><label style={hubLbl}>Regarding</label>
        <input value={f.regarding} onChange={set("regarding")} style={crmInput} placeholder="Optional" /></div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={hubLbl}>Sage / Chroma item(s)</label>
        <input disabled style={{ ...crmInput, opacity: .55, cursor: "not-allowed" }} placeholder="Item lookup — enabled once ChromaDB is connected" /></div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={hubLbl}>Description / notes</label>
        <textarea value={f.description} onChange={set("description")} rows={5} style={{ ...crmInput, resize: "vertical", fontFamily: "inherit" }} placeholder="Where did this come from? Paste notes, call context, email, etc." /></div>
    </div>
    <p style={{ fontSize: 11, color: T.textMuted, margin: "4px 0 10px" }}>Requested date and requestor are set automatically to today and you. The group's Task Assigner is emailed to approve, prioritise, and assign it.</p>
    {err && <p style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>{err}</p>}
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      <button className="btn-secondary" style={{ width: "auto", padding: "0 16px" }} onClick={onClose}>Cancel</button>
      <button className="btn-primary" style={{ width: "auto", padding: "0 18px" }} disabled={saving} onClick={save}>{saving ? "Creating…" : "Create Task"}</button>
    </div>
  </HubModal>;
}

function HubSetup({ assigners, users, reload }) {
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, marginBottom: 16 };
  const [drafts, setDrafts] = useState({});
  const [newStaff, setNewStaff] = useState({ email: "", name: "" });
  const [msg, setMsg] = useState("");
  const emailFor = g => drafts[g] !== undefined ? drafts[g] : (assigners.find(a => a.assignmentGroup === g)?.assignerEmail || "");
  const saveAssigner = async (g) => {
    try { await crmApi("/task-assigners", { method: "PUT", body: JSON.stringify({ assignmentGroup: g, assignerEmail: emailFor(g) }) }); setMsg(`Saved assigner for ${g}`); reload(); }
    catch (e) { setMsg(e.message); }
  };
  const addStaff = async () => {
    if (!newStaff.email.trim()) return;
    try { await crmApi("/users", { method: "POST", body: JSON.stringify(newStaff) }); setNewStaff({ email: "", name: "" }); setMsg("Staff member added"); reload(); }
    catch (e) { setMsg(e.message); }
  };
  return (
    <div>
      {msg && <div style={{ background: "rgba(46,160,67,.1)", border: "1px solid rgba(46,160,67,.3)", color: "#2ea043", borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>{msg} <button onClick={() => setMsg("")} style={{ float: "right", background: "none", border: "none", color: "inherit", cursor: "pointer" }}>✕</button></div>}
      <div style={card}>
        <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Task Assigners</p>
        <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>Who gets the email and approves/assigns each group's tasks.</p>
        {TASK_GROUPS.map(g => (
          <div key={g} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 12, width: 160, flexShrink: 0 }}>{g}</span>
            <input value={emailFor(g)} onChange={e => setDrafts({ ...drafts, [g]: e.target.value })} placeholder="assigner@rain-drop.com" style={{ ...crmInput, marginBottom: 0, flex: 1 }} />
            <button className="btn-secondary" style={{ width: "auto", padding: "0 14px", fontSize: 12 }} onClick={() => saveAssigner(g)}>Save</button>
          </div>
        ))}
      </div>
      <div style={card}>
        <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Staff Roster</p>
        <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>Add staff so they can be assigned tasks before their first login (they reconcile by email when they sign in with Microsoft).</p>
        {(users || []).map(u => (
          <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
            <span style={{ flex: 1 }}>{u.name || u.email}{u.name ? <span style={{ color: T.textMuted }}> · {u.email}</span> : ""}</span>
            {u.role === "ADMIN" && <span style={{ fontSize: 10, color: "#a78bfa" }}>admin</span>}
            {u.pending && <span style={{ fontSize: 10, color: "#f59e0b" }}>pending login</span>}
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input value={newStaff.name} onChange={e => setNewStaff({ ...newStaff, name: e.target.value })} placeholder="Name" style={{ ...crmInput, marginBottom: 0, width: 160 }} />
          <input value={newStaff.email} onChange={e => setNewStaff({ ...newStaff, email: e.target.value })} placeholder="email@rain-drop.com" style={{ ...crmInput, marginBottom: 0, flex: 1 }} />
          <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={addStaff}>Add</button>
        </div>
      </div>
    </div>
  );
}

const hubLbl = { display: "block", fontSize: 11, color: T.textSecondary, fontWeight: 600, marginBottom: 4 };
function HubModal({ title, onClose, wide, children }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22, width: "100%", maxWidth: wide ? 640 : 460 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── "+ New" guided menu (replaces the three separate create buttons) ──
function CrmNewMenu({ onLead, onOpp, onAcct }) {
  const [open, setOpen] = useState(false);
  const items = [
    { key: "lead", icon: "🌱", label: "Lead", hint: "A new inbound enquiry to qualify", fn: onLead },
    { key: "opp", icon: "🎯", label: "Opportunity", hint: "A deal you're actively pursuing", fn: onOpp },
    { key: "acct", icon: "🏢", label: "Account", hint: "A company / customer record", fn: onAcct },
  ];
  return (
    <div style={{ position: "relative" }}>
      <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setOpen(o => !o)}>+ New ▾</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50, width: 260, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 6, boxShadow: "0 12px 32px rgba(0,0,0,.35)" }}>
            {items.map(it => (
              <button key={it.key} onClick={() => { setOpen(false); it.fn(); }} style={{ display: "flex", gap: 10, alignItems: "flex-start", width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: 8, padding: "9px 10px", cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <span style={{ fontSize: 16, lineHeight: "18px" }}>{it.icon}</span>
                <span>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textPrimary }}>{it.label}</span>
                  <span style={{ display: "block", fontSize: 11, color: T.textMuted }}>{it.hint}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Interactive dashboard: KPIs, conversion funnel, pipeline by confidence,
// salesperson leaderboard, deal aging, and a click-to-filter deals drill-down.
// Everything is computed client-side from the already-loaded opps/leads/accounts. ──
const CONF_WEIGHT = { 1: .95, 2: .8, 3: .5, 4: .25, 5: .15, 6: .1, 7: .05 };
const isWonOpp = o => ["closed_won", "won"].includes(o.status);
const isLostOpp = o => ["closed_lost", "lost"].includes(o.status);
const isOpenOpp = o => !isWonOpp(o) && !isLostOpp(o);
const oppWeight = o => (o.probability ? o.probability / 100 : (CONF_WEIGHT[Number(o.confidence)] ?? 0.1));
const AGE_BUCKETS = [
  { key: "0-30", label: "0–30 days", min: 0, max: 30, color: "#16a34a" },
  { key: "31-60", label: "31–60 days", min: 31, max: 60, color: "#3b82f6" },
  { key: "61-90", label: "61–90 days", min: 61, max: 90, color: "#f59e0b" },
  { key: "90+", label: "90+ days", min: 91, max: Infinity, color: "#ef4444" },
];
const oppAge = o => Math.max(0, Math.floor((Date.now() - new Date(o.createdAt).getTime()) / 86400000));
const ageBucket = o => AGE_BUCKETS.find(b => { const a = oppAge(o); return a >= b.min && a <= b.max; })?.key || "90+";

function CrmDashboard({ opps, leads, accounts, analytics, roster, rosterName, onOpenOpp, onGoTab }) {
  const TH = chartTheme();
  const [range, setRange] = useState(0); // days; 0 = all time
  const [filter, setFilter] = useState(null); // {dim, val, label}
  const [drill, setDrill] = useState(null); // clicked-bar breakdown popup: {title, color, deals}
  const [limit, setLimit] = useState(25);
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  const sectionTitle = { fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 2 };
  const sectionSub = { fontSize: 11, color: T.textMuted, marginBottom: 12 };

  if (opps === null) return <p style={{ color: T.textMuted, fontSize: 13 }}>Loading dashboard…</p>;

  const ownerName = o => o.owner?.name || o.owner?.email || rosterName?.(o.ownerId) || "Unassigned";
  const inRange = o => !range || (Date.now() - new Date(o.createdAt).getTime()) <= range * 86400000;
  const all = (opps || []).filter(inRange);
  const open = all.filter(isOpenOpp);
  const won = all.filter(isWonOpp);
  const lost = all.filter(isLostOpp);

  // KPIs
  const openVal = open.reduce((s, o) => s + (o.amount || 0), 0);
  const weighted = open.reduce((s, o) => s + (o.amount || 0) * oppWeight(o), 0);
  const wonVal = won.reduce((s, o) => s + (o.amount || 0), 0);
  const lostVal = lost.reduce((s, o) => s + (o.amount || 0), 0);
  const winRate = (won.length + lost.length) ? Math.round(won.length / (won.length + lost.length) * 100) : null;
  const overdue = analytics?.summary ? analytics.summary.tasksOverdue : null;
  const kpis = [
    { label: "Open Pipeline", value: crmMoney(openVal), sub: `${open.length.toLocaleString()} open deals`, color: "#f59e0b", icon: "💰" },
    { label: "Weighted Forecast", value: crmMoney(weighted), sub: "confidence-adjusted", color: "#3b82f6", icon: "🎯" },
    { label: "Won", value: crmMoney(wonVal), sub: `${won.length.toLocaleString()} deals${range ? " in range" : ""}`, color: "#22c55e", icon: "🏆" },
    { label: "Lost", value: crmMoney(lostVal), sub: `${lost.length.toLocaleString()} deals`, color: "#ef4444", icon: "📉" },
    { label: "Win Rate", value: winRate != null ? winRate + "%" : "—", sub: `${won.length.toLocaleString()}W / ${lost.length.toLocaleString()}L`, color: "#a78bfa", icon: "📊" },
    { label: "Overdue Tasks", value: overdue != null ? overdue.toLocaleString() : "…", sub: "needs attention", color: overdue ? "#ef4444" : "#22c55e", icon: "⏰", go: "tasks" },
  ];

  // Confidence breakdown (open)
  const byConf = OPP_CONFIDENCE.map(c => {
    const items = open.filter(o => Number(o.confidence) === c.level);
    return { ...c, count: items.length, value: items.reduce((s, o) => s + (o.amount || 0), 0) };
  });
  const confOption = {
    grid: { top: 10, right: 60, bottom: 10, left: 96 },
    tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.name}<br/>${crmMoney(p.data.value)} · ${p.data.count} deal${p.data.count === 1 ? "" : "s"}` },
    xAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    yAxis: { type: "category", data: byConf.map(c => `${c.level} · ${c.label}`).reverse(), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    series: [{ type: "bar", barWidth: 14, data: byConf.slice().reverse().map(c => ({ value: c.value, count: c.count, level: c.level, itemStyle: { color: TH.grad(c.color, c.color + "88"), borderRadius: [0, 4, 4, 0] } })), label: { show: true, position: "right", formatter: p => `${crmMoney(p.data.value)} · ${p.data.count}`, color: T.textMuted, fontSize: 10 } }],
  };
  const onConfClick = p => { const lvl = p.data?.level; if (lvl == null) return; const c = oppConf(lvl); setDrill({ title: `Confidence ${lvl} · ${c?.label || ""}`, color: c?.color || T.accent, deals: open.filter(o => Number(o.confidence) === lvl) }); };

  // Aging (open)
  const byAge = AGE_BUCKETS.map(b => { const items = open.filter(o => ageBucket(o) === b.key); return { ...b, count: items.length, value: items.reduce((s, o) => s + (o.amount || 0), 0) }; });
  const stalled = open.filter(o => (Date.now() - new Date(o.updatedAt || o.createdAt).getTime()) > 60 * 86400000);
  const ageOption = {
    grid: { top: 24, right: 16, bottom: 26, left: 54 },
    tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.name}<br/>${crmMoney(p.data.value)} · ${p.data.count} deal${p.data.count === 1 ? "" : "s"}` },
    xAxis: { type: "category", data: byAge.map(b => b.label), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{ type: "bar", barWidth: "48%", data: byAge.map(b => ({ value: b.value, count: b.count, key: b.key, itemStyle: { color: TH.grad(b.color, b.color + "66"), borderRadius: [4, 4, 0, 0] } })), label: { show: true, position: "top", formatter: p => p.data.count, color: T.textMuted, fontSize: 10 } }],
  };
  const onAgeClick = p => { const k = p.data?.key; if (!k) return; const b = AGE_BUCKETS.find(x => x.key === k); setDrill({ title: `Aging · ${b?.label || k}`, color: b?.color || T.accent, deals: open.filter(o => ageBucket(o) === k) }); };

  // Salesperson leaderboard
  const spMap = {};
  for (const o of all) {
    const id = o.ownerId || "unassigned"; const name = ownerName(o);
    const s = spMap[id] || (spMap[id] = { id, name, openVal: 0, openCount: 0, wonVal: 0, wonCount: 0, lostCount: 0 });
    if (isOpenOpp(o)) { s.openVal += o.amount || 0; s.openCount++; }
    else if (isWonOpp(o)) { s.wonVal += o.amount || 0; s.wonCount++; }
    else if (isLostOpp(o)) s.lostCount++;
  }
  const leaderboard = Object.values(spMap).sort((a, b) => b.openVal - a.openVal).slice(0, 12);
  const maxOpen = Math.max(1, ...leaderboard.map(s => s.openVal));

  // Conversion funnel
  const leadCount = (leads || []).length;
  const funnel = [
    { label: "Leads", count: leadCount, color: "#3b82f6", hint: "captured" },
    { label: "Opportunities", count: all.length, color: "#c47a10", hint: "in play + closed" },
    { label: "Won", count: won.length, color: "#16a34a", hint: "deals closed-won" },
  ];
  const funnelMax = Math.max(1, ...funnel.map(f => f.count));

  // Drill-down deals
  const matchFilter = o => {
    if (!filter) return isOpenOpp(o);
    if (filter.dim === "confidence") return isOpenOpp(o) && Number(o.confidence) === filter.val;
    if (filter.dim === "age") return isOpenOpp(o) && ageBucket(o) === filter.val;
    if (filter.dim === "owner") return (o.ownerId || "unassigned") === filter.val;
    return true;
  };
  const deals = all.filter(matchFilter).sort((a, b) => (b.amount || 0) - (a.amount || 0));

  const RANGES = [[0, "All time"], [1095, "3 yrs"], [365, "12 mo"], [180, "6 mo"], [90, "90 days"]];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Range control */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Created</span>
        <div style={{ display: "flex", gap: 3, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 3 }}>
          {RANGES.map(([d, l]) => (
            <button key={d} onClick={() => setRange(d)} style={{ padding: "5px 11px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: range === d ? T.bg : "transparent", color: range === d ? T.accent : T.textSecondary }}>{l}</button>
          ))}
        </div>
        {filter && <span style={{ marginLeft: "auto", fontSize: 12, color: T.textSecondary, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, padding: "4px 10px" }}>Filter: <b style={{ color: T.textPrimary }}>{filter.label}</b> <button onClick={() => setFilter(null)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", marginLeft: 4 }}>✕</button></span>}
      </div>

      {/* KPI tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
        {kpis.map(k => (
          <div key={k.label} onClick={() => k.go && onGoTab?.(k.go)}
            style={{ background: `linear-gradient(140deg, ${k.color}26, ${T.surface} 62%)`, border: `1px solid ${k.color}55`, borderRadius: 14, padding: "14px 16px", cursor: k.go ? "pointer" : "default", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -16, right: -16, width: 64, height: 64, borderRadius: "50%", background: `${k.color}18` }} />
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, position: "relative" }}>
              <span style={{ width: 28, height: 28, borderRadius: 9, background: `${k.color}2e`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{k.icon}</span>
              <span style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>{k.label}</span>
            </div>
            <p style={{ fontSize: 25, fontWeight: 800, color: k.color, lineHeight: 1, position: "relative" }}>{k.value}</p>
            <p style={{ fontSize: 11, color: T.textMuted, marginTop: 6, position: "relative" }}>{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Funnel */}
      <div style={card}>
        <p style={sectionTitle}>Conversion Funnel</p>
        <p style={sectionSub}>How the pipeline flows from leads to won deals</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {funnel.map((f, i) => {
            const prev = i > 0 ? funnel[i - 1].count : null;
            const conv = prev ? Math.round(f.count / (prev || 1) * 100) : null;
            return (
              <div key={f.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 110, fontSize: 12, color: T.textSecondary, flexShrink: 0 }}>{f.label}</span>
                <div style={{ flex: 1, background: T.bg, borderRadius: 6, height: 30, position: "relative", overflow: "hidden" }}>
                  <div style={{ width: `${Math.max(4, f.count / funnelMax * 100)}%`, height: "100%", background: TH.grad(f.color, f.color + "99"), borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{f.count.toLocaleString()}</span>
                  </div>
                </div>
                <span style={{ width: 90, fontSize: 11, color: T.textMuted, textAlign: "right", flexShrink: 0 }}>{conv != null ? `${conv}% →` : f.hint}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confidence + Aging */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={card}>
          <p style={sectionTitle}>Open Pipeline by Confidence</p>
          <p style={sectionSub}>Value & deal count · click a bar to drill in</p>
          {open.length ? <EChart option={confOption} height={230} onClick={onConfClick} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>No open pipeline</p>}
        </div>
        <div style={card}>
          <p style={sectionTitle}>Deal Aging</p>
          <p style={sectionSub}>{stalled.length} deal{stalled.length === 1 ? "" : "s"} untouched 60+ days · click to drill in</p>
          {open.length ? <EChart option={ageOption} height={230} onClick={onAgeClick} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>No open pipeline</p>}
        </div>
      </div>

      {/* Salesperson leaderboard */}
      <div style={card}>
        <p style={sectionTitle}>By Salesperson</p>
        <p style={sectionSub}>Open pipeline, won & win-rate · click a row to filter deals</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
            {["Salesperson", "Open Pipeline", "Open", "Won", "Win %"].map((h, i) => <th key={h} style={{ padding: "6px 10px", fontWeight: 500, fontSize: 11, textTransform: "uppercase", textAlign: i > 1 ? "right" : "left" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {leaderboard.map(s => {
              const wr = (s.wonCount + s.lostCount) ? Math.round(s.wonCount / (s.wonCount + s.lostCount) * 100) : null;
              const active = filter?.dim === "owner" && filter.val === s.id;
              return (
                <tr key={s.id} onClick={() => setFilter(f => active ? null : { dim: "owner", val: s.id, label: `Salesperson · ${s.name}` })} style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer", background: active ? T.bg : "transparent" }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: "8px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: T.bg, borderRadius: 3, overflow: "hidden", minWidth: 60 }}><div style={{ width: `${s.openVal / maxOpen * 100}%`, height: "100%", background: T.accent }} /></div>
                      <span style={{ color: T.accent, fontFamily: "'JetBrains Mono',monospace", fontSize: 12, width: 56, textAlign: "right" }}>{crmMoney(s.openVal)}</span>
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: T.textSecondary }}>{s.openCount}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: T.textSecondary }}>{crmMoney(s.wonVal)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: wr != null ? (wr >= 50 ? "#2ea043" : T.textSecondary) : T.textMuted }}>{wr != null ? wr + "%" : "—"}</td>
                </tr>
              );
            })}
            {leaderboard.length === 0 && <tr><td colSpan={5} style={{ padding: "20px 10px", color: T.textMuted, fontSize: 13 }}>No deals yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Drill-down deals */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <p style={sectionTitle}>{filter ? "Filtered Deals" : "Top Open Deals"}</p>
            <p style={{ ...sectionSub, marginBottom: 0 }}>{deals.length.toLocaleString()} deal{deals.length === 1 ? "" : "s"}{filter ? ` · ${filter.label}` : ""}</p>
          </div>
          <button className="btn-secondary" style={{ width: "auto", padding: "6px 12px", fontSize: 12 }} onClick={() => onGoTab?.("pipeline")}>Open Pipeline →</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
              {["Opportunity", "Account", "Confidence", "Salesperson", "Amount", "Age"].map((h, i) => <th key={h} style={{ padding: "6px 10px", fontWeight: 500, fontSize: 11, textTransform: "uppercase", textAlign: i === 4 ? "right" : "left" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {deals.slice(0, limit).map(o => {
                const c = oppConf(o.confidence);
                return (
                  <tr key={o.id} onClick={() => onOpenOpp?.(o.id)} style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{o.name}</td>
                    <td style={{ padding: "8px 10px", color: T.textSecondary }}>{o.account?.name || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>{c ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} /><span style={{ fontSize: 12 }}>{c.label}</span></span> : <span style={{ color: T.textMuted }}>—</span>}</td>
                    <td style={{ padding: "8px 10px", color: T.textSecondary }}>{ownerName(o)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(o.amount)}</td>
                    <td style={{ padding: "8px 10px", color: T.textMuted }}>{oppAge(o)}d</td>
                  </tr>
                );
              })}
              {deals.length === 0 && <tr><td colSpan={6} style={{ padding: "20px 10px", color: T.textMuted, fontSize: 13 }}>No deals match.</td></tr>}
            </tbody>
          </table>
        </div>
        {deals.length > limit && <button className="btn-secondary" style={{ marginTop: 12, width: "auto", padding: "7px 16px", fontSize: 12 }} onClick={() => setLimit(l => l + 50)}>Show more ({(deals.length - limit).toLocaleString()} more)</button>}
      </div>

      {drill && <CrmDrillModal title={drill.title} color={drill.color} deals={drill.deals} rosterName={rosterName} onOpenOpp={id => { setDrill(null); onOpenOpp?.(id); }} onClose={() => setDrill(null)} />}
    </div>
  );
}

// Breakdown popup shown when a dashboard chart bar is clicked: segment summary +
// by-salesperson and by-account breakdowns + the underlying deals.
function CrmDrillModal({ title, color, deals, rosterName, onOpenOpp, onClose }) {
  const total = deals.reduce((s, o) => s + (o.amount || 0), 0);
  const spName = o => o.owner?.name || o.owner?.email || rosterName?.(o.ownerId) || "Unassigned";
  const group = (keyFn) => {
    const g = {};
    for (const o of deals) { const k = keyFn(o); (g[k] ||= { n: 0, v: 0 }); g[k].n++; g[k].v += o.amount || 0; }
    return Object.entries(g).sort((a, b) => b[1].v - a[1].v);
  };
  const bySp = group(spName);
  const byAcct = group(o => o.account?.name || "No account");
  const sorted = deals.slice().sort((a, b) => (b.amount || 0) - (a.amount || 0));
  const bd = (heading, rows) => (
    <div style={{ flex: 1, minWidth: 200 }}>
      <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>{heading}</p>
      {rows.slice(0, 6).map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ color: T.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{k} <span style={{ color: T.textMuted }}>· {v.n}</span></span>
          <span style={{ color: T.accent, fontFamily: "'JetBrains Mono',monospace", flexShrink: 0 }}>{crmMoney(v.v)}</span>
        </div>
      ))}
      {rows.length > 6 && <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>+{rows.length - 6} more</p>}
    </div>
  );
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 640, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24, borderTop: `3px solid ${color}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>{title}</h2>
            <p style={{ fontSize: 13, color: T.textSecondary }}>{deals.length.toLocaleString()} open deal{deals.length === 1 ? "" : "s"} · <span style={{ color: color, fontWeight: 700 }}>{crmMoney(total)}</span></p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", margin: "16px 0 20px" }}>
          {bd("By salesperson", bySp)}
          {bd("By account", byAcct)}
        </div>
        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Deals</p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {sorted.slice(0, 60).map(o => (
                <tr key={o.id} onClick={() => onOpenOpp?.(o.id)} style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer" }}>
                  <td style={{ padding: "7px 8px", fontWeight: 600 }}>{o.name}</td>
                  <td style={{ padding: "7px 8px", color: T.textSecondary }}>{o.account?.name || "—"}</td>
                  <td style={{ padding: "7px 8px", color: T.textSecondary }}>{spName(o)}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(o.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sorted.length > 60 && <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>Showing top 60 of {sorted.length.toLocaleString()} by value.</p>}
      </div>
    </div>
  );
}

function CrmAnalytics({ analytics }) {
  if (!analytics) return <p style={{ color: T.textMuted, fontSize: 13 }}>Loading analytics…</p>;
  const TH = chartTheme();
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  const pipe = analytics.pipeline || [];
  const fc = analytics.forecastMonths || [];
  const pipelineOption = {
    grid: { top: 16, right: 18, bottom: 30, left: 70 },
    tooltip: { ...TH.tooltip, valueFormatter: v => crmMoney(v) },
    xAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    yAxis: { type: "category", data: pipe.map(p => CRM_STAGES.find(s => s.id === p.stage)?.label || p.stage).reverse(), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    series: [{ type: "bar", barWidth: "60%", data: pipe.slice().reverse().map(p => ({ value: p.value, itemStyle: { color: TH.grad(CRM_STAGES.find(s => s.id === p.stage)?.color || T.accent, (CRM_STAGES.find(s => s.id === p.stage)?.color || T.accent) + "66"), borderRadius: [0, 5, 5, 0] } })), label: { show: true, position: "right", formatter: p => crmMoney(p.value), color: T.textMuted, fontSize: 10 } }],
  };
  const forecastOption = {
    grid: { top: 16, right: 18, bottom: 30, left: 60 },
    tooltip: { ...TH.tooltip, valueFormatter: v => crmMoney(v) },
    xAxis: { type: "category", data: fc.map(f => f.month), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{ type: "bar", barWidth: "50%", data: fc.map(f => f.value), itemStyle: { color: TH.grad("#2ea043", "rgba(46,160,67,0.4)"), borderRadius: [5, 5, 0, 0] }, label: { show: true, position: "top", formatter: p => crmMoney(p.value), color: T.textMuted, fontSize: 10 } }],
  };
  // Value & count by probability band and by OVERALL Confidence.
  const prob = analytics.byProbability || [];
  const conf = analytics.byConfidence || [];
  const hasConf = conf.some(c => c.count > 0);
  const probOption = {
    grid: { top: 24, right: 18, bottom: 30, left: 60 },
    tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.name}%<br/>${crmMoney(p.data.value)} · ${p.data.count} deal${p.data.count === 1 ? "" : "s"}` },
    xAxis: { type: "category", data: prob.map(b => b.band), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{ type: "bar", barWidth: "55%", data: prob.map(b => ({ value: b.value, count: b.count })), itemStyle: { color: TH.grad(T.accent, T.accent + "55"), borderRadius: [5, 5, 0, 0] }, label: { show: true, position: "top", formatter: p => `${crmMoney(p.data.value)}\n${p.data.count}`, color: T.textMuted, fontSize: 10, lineHeight: 13 } }],
  };
  const confColor = (lvl) => (OPP_CONFIDENCE.find(c => c.level === lvl) || {}).color || T.accent;
  const confOption = {
    grid: { top: 24, right: 18, bottom: 30, left: 60 },
    tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.data.full}<br/>${crmMoney(p.data.value)} · ${p.data.count} deal${p.data.count === 1 ? "" : "s"}` },
    xAxis: { type: "category", data: conf.map(c => c.level), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{ type: "bar", barWidth: "55%", data: conf.map(c => ({ value: c.value, count: c.count, full: `${c.level} · ${c.label}`, itemStyle: { color: TH.grad(confColor(c.level), confColor(c.level) + "55"), borderRadius: [5, 5, 0, 0] } })), label: { show: true, position: "top", formatter: p => `${crmMoney(p.data.value)}\n${p.data.count}`, color: T.textMuted, fontSize: 10, lineHeight: 13 } }],
  };
  const ob = analytics.onboarding || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Pipeline Value by Stage</p>
          {pipe.some(p => p.value > 0) ? <EChart option={pipelineOption} height={260} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>No open pipeline yet</p>}
        </div>
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Weighted Forecast by Close Month</p>
          {fc.length ? <EChart option={forecastOption} height={260} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>Set expected close dates to forecast</p>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: hasConf ? "1fr 1fr" : "1fr", gap: 20 }}>
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 2 }}>Value & Count by Probability</p>
          <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>Open opportunities · bar = value, number = deal count</p>
          {prob.some(b => b.count > 0) ? <EChart option={probOption} height={260} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>No open pipeline yet</p>}
        </div>
        {hasConf && (
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 2 }}>Value & Count by Confidence</p>
            <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>OVERALL Confidence 1–7 · bar = value, number = deal count</p>
            <EChart option={confOption} height={260} />
          </div>
        )}
      </div>
      <div style={card}>
        <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Onboarding Steps</p>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {[["done", "Done", "#2ea043"], ["in_progress", "In Progress", "#f59e0b"], ["pending", "Pending", "#8b949e"], ["blocked", "Blocked", "#ef4444"]].map(([k, label, c]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
              <span style={{ fontSize: 13, color: T.textSecondary }}>{label}: <strong style={{ color: T.textPrimary }}>{ob[k] || 0}</strong></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CrmNewAccount({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", website: "", industry: "", status: "prospect",
    addressLine1: "", addressLine2: "", city: "", state: "", zip: "", country: "", phone: "", fax: "",
  });
  const [pc, setPc] = useState({ name: "", email: "", phone: "", title: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setC = (k) => (e) => setPc({ ...pc, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) return;
    setSaving(true);
    try {
      const hasContact = pc.name.trim() || pc.email.trim();
      await crmApi("/accounts", { method: "POST", body: JSON.stringify({ ...f, primaryContact: hasContact ? pc : undefined }) });
      onSaved();
    } catch (e) { alert(e.message); setSaving(false); }
  };
  const half = { ...crmInput, marginBottom: 0 };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 520, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>New Account</h2>
        <input style={crmInput} placeholder="Company name *" value={f.name} onChange={set("name")} />
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input style={half} placeholder="Website" value={f.website} onChange={set("website")} />
          <input style={half} placeholder="Industry" value={f.industry} onChange={set("industry")} />
        </div>
        <select style={crmInput} value={f.status} onChange={set("status")}>
          <option value="prospect">Prospect</option><option value="active">Active</option><option value="churned">Churned</option>
        </select>

        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", margin: "6px 0 8px" }}>Address</p>
        <input style={crmInput} placeholder="Address line 1" value={f.addressLine1} onChange={set("addressLine1")} />
        <input style={crmInput} placeholder="Address line 2" value={f.addressLine2} onChange={set("addressLine2")} />
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input style={{ ...half, flex: 2 }} placeholder="City" value={f.city} onChange={set("city")} />
          <input style={{ ...half, width: 80 }} placeholder="State" value={f.state} onChange={set("state")} />
          <input style={{ ...half, width: 100 }} placeholder="Zip" value={f.zip} onChange={set("zip")} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input style={half} placeholder="Country" value={f.country} onChange={set("country")} />
          <input style={half} placeholder="Phone" value={f.phone} onChange={set("phone")} />
          <input style={half} placeholder="Fax" value={f.fax} onChange={set("fax")} />
        </div>

        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", margin: "6px 0 8px" }}>Primary contact <span style={{ textTransform: "none", letterSpacing: 0 }}>(optional)</span></p>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input style={half} placeholder="Name" value={pc.name} onChange={setC("name")} />
          <input style={half} placeholder="Title" value={pc.title} onChange={setC("title")} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input style={half} placeholder="Email" value={pc.email} onChange={setC("email")} />
          <input style={half} placeholder="Phone" value={pc.phone} onChange={setC("phone")} />
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Create"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Leads list (pre-opportunity funnel) ──
const CRM_LEAD_STATUS_META = {
  new:          { label: "New",          color: "#3b82f6" },
  working:      { label: "Working",      color: "#f59e0b" },
  qualified:    { label: "Qualified",    color: "#2ea043" },
  disqualified: { label: "Disqualified", color: "#ef4444" },
  converted:    { label: "Converted",    color: "#8b5cf6" },
};
function CrmLeads({ leads, reload, onConvert, onOpenAcct }) {
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const setStatus = async (lead, status) => { await crmApi(`/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify({ status }) }); reload(); };
  const del = async (lead) => { if (!window.confirm(`Delete lead “${lead.name}”?`)) return; await crmApi(`/leads/${lead.id}`, { method: "DELETE" }); reload(); };
  const ratingDot = (r) => r === "hot" ? "#ef4444" : r === "warm" ? "#f59e0b" : r === "cold" ? "#3b82f6" : T.textMuted;
  return (
    <div style={card}>
      {leads === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> :
        leads.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No open leads. Capture one with “+ Lead”, then qualify it into an account.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
            {["Lead", "Company", "Source", "Score", "Status", ""].map(h => <th key={h} style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {leads.map(l => {
              const m = CRM_LEAD_STATUS_META[l.status] || CRM_LEAD_STATUS_META.new;
              return (
                <tr key={l.id} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                    <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: ratingDot(l.rating), marginRight: 8 }} title={l.rating || ""} />
                    {l.name}{l.title ? <span style={{ color: T.textMuted, fontWeight: 400 }}> · {l.title}</span> : ""}
                    {l.email ? <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 400, marginLeft: 15 }}>{l.email}</div> : ""}
                  </td>
                  <td style={{ padding: "10px 12px", color: T.textSecondary }}>{l.company || "—"}</td>
                  <td style={{ padding: "10px 12px", color: T.textMuted }}>{l.source || "—"}</td>
                  <td style={{ padding: "10px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace" }}>{l.score ?? 0}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <select value={l.status} onChange={e => setStatus(l, e.target.value)}
                      style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 12, padding: "2px 8px", fontSize: 11, color: m.color, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", colorScheme: "dark" }}>
                      {["new", "working", "qualified", "disqualified"].map(s => <option key={s} value={s} style={{ color: T.textPrimary, background: T.surface }}>{CRM_LEAD_STATUS_META[s].label}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn-primary" style={{ width: "auto", padding: "5px 12px", fontSize: 11 }} onClick={() => onConvert(l)}>Convert →</button>
                    <button onClick={() => del(l)} title="Delete" style={{ marginLeft: 8, background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 14 }}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CrmNewLead({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", title: "", source: "", sourceDetail: "", state: "", rating: "warm", score: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!f.name.trim()) return; setSaving(true); try { await crmApi("/leads", { method: "POST", body: JSON.stringify(f) }); onSaved(); } catch (e) { alert(e.message); setSaving(false); } };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 440, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>New Lead</h2>
        <input style={crmInput} placeholder="Contact name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <div style={{ display: "flex", gap: 10 }}>
          <input style={{ ...crmInput, flex: 1 }} placeholder="Company" value={f.company} onChange={e => setF({ ...f, company: e.target.value })} />
          <input style={{ ...crmInput, flex: 1 }} placeholder="Job title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <input style={{ ...crmInput, flex: 1 }} placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <input style={{ ...crmInput, flex: 1 }} placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <select style={{ ...crmInput, flex: 1 }} value={f.source} onChange={e => setF({ ...f, source: e.target.value })}>
            <option value="">Lead source</option>
            {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{ ...crmInput, width: 90 }} value={f.state} onChange={e => setF({ ...f, state: e.target.value })} title="State — assigns the RSD">
            <option value="">State</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {LEAD_SOURCE_SPECIFY.includes(f.source) && <input style={crmInput} placeholder="Specify (show + year / referral name)" value={f.sourceDetail} onChange={e => setF({ ...f, sourceDetail: e.target.value })} />}
        <div style={{ display: "flex", gap: 10 }}>
          <select style={{ ...crmInput, flex: 1 }} value={f.rating} onChange={e => setF({ ...f, rating: e.target.value })}>
            <option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
          </select>
          <input style={{ ...crmInput, width: 90 }} type="number" placeholder="Score" value={f.score} onChange={e => setF({ ...f, score: e.target.value })} />
        </div>
        <textarea style={{ ...crmInput, minHeight: 60, resize: "vertical" }} placeholder="Notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} />
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Create lead"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Qualify a lead → Account + Contact + (optional) Opportunity ──
function CrmConvertLead({ lead, accounts, onClose, onDone }) {
  const [f, setF] = useState({
    existingAccountId: "", createOpportunity: true,
    opportunityName: `${lead.company || lead.name} opportunity`, amount: "", expectedCloseDate: "",
  });
  const [saving, setSaving] = useState(false);
  const run = async () => {
    setSaving(true);
    try {
      const body = {
        createOpportunity: f.createOpportunity,
        existingAccountId: f.existingAccountId || undefined,
        opportunityName: f.opportunityName,
        amount: f.amount ? Number(f.amount) : undefined,
        expectedCloseDate: f.expectedCloseDate || undefined,
      };
      const r = await crmApi(`/leads/${lead.id}/convert`, { method: "POST", body: JSON.stringify(body) });
      onDone(r);
    } catch (e) { alert(e.message); setSaving(false); }
  };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 460, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Qualify lead</h2>
        <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>Convert <b>{lead.name}</b>{lead.company ? ` (${lead.company})` : ""} into CRM records.</p>
        <label style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em" }}>Account</label>
        <select style={crmInput} value={f.existingAccountId} onChange={e => setF({ ...f, existingAccountId: e.target.value })}>
          <option value="">➕ Create new account “{(lead.company || lead.name)}”</option>
          {(accounts || []).map(ac => <option key={ac.id} value={ac.id}>Link to existing · {ac.name}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, margin: "8px 0 10px", cursor: "pointer" }}>
          <input type="checkbox" checked={f.createOpportunity} onChange={e => setF({ ...f, createOpportunity: e.target.checked })} />
          Also create an opportunity
        </label>
        {f.createOpportunity && (
          <>
            <input style={crmInput} placeholder="Opportunity name" value={f.opportunityName} onChange={e => setF({ ...f, opportunityName: e.target.value })} />
            <div style={{ display: "flex", gap: 10 }}>
              <input style={{ ...crmInput, flex: 1 }} type="number" placeholder="Amount ($)" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
              <input style={{ ...crmInput, flex: 1 }} type="date" value={f.expectedCloseDate} onChange={e => setF({ ...f, expectedCloseDate: e.target.value })} />
            </div>
          </>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={run}>{saving ? "Converting…" : "Convert"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CrmNewOpp({ accounts, onClose, onSaved, onNeedAccount }) {
  const [f, setF] = useState({
    accountId: "", name: "", amount: "", status: "in_play", confidence: "",
    leadSource: "", leadSourceDetail: "", sourcewellInterest: false,
    projectState: "", projectCity: "", projectAddress: "", projectZip: "", projectCountry: "USA",
    endUserContact: "", scheduledFollowUp: "", estPoReceiptDate: "", expectedCloseDate: "",
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim() || !f.accountId) return;
    setSaving(true);
    try { await crmApi("/opportunities", { method: "POST", body: JSON.stringify({ ...f, confidence: f.confidence || null }) }); onSaved(); }
    catch (e) { alert(e.message); setSaving(false); }
  };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 620, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>New Opportunity</h2>
        {(!accounts || accounts.length === 0) ? (
          <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 12 }}>You need an account first. <button onClick={onNeedAccount} style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", textDecoration: "underline" }}>Create one</button></div>
        ) : (
          <><label style={hubLbl}>Account *</label>
          <select style={crmInput} value={f.accountId} onChange={set("accountId")}>
            <option value="">Select account</option>
            {accounts.map(ac => <option key={ac.id} value={ac.id}>{ac.name}</option>)}
          </select></>
        )}
        <label style={hubLbl}>Opportunity name *</label>
        <input style={crmInput} placeholder="Opportunity name" value={f.name} onChange={set("name")} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={hubLbl}>Estimated revenue ($)</label>
            <input style={crmInput} type="number" placeholder="0" value={f.amount} onChange={set("amount")} /></div>
          <div><label style={hubLbl}>Status</label>
            <select style={crmInput} value={f.status} onChange={set("status")}>
              {OPP_STATUSES.filter(s => s.id !== "closed_lost").map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select></div>
          <div style={{ gridColumn: "1 / -1" }}><label style={hubLbl}>OVERALL Confidence</label>
            <select style={crmInput} value={f.confidence} onChange={set("confidence")}>
              <option value="">— Select confidence —</option>
              {OPP_CONFIDENCE.map(c => <option key={c.level} value={c.level}>{c.level} · {c.full}</option>)}
            </select></div>
          <div><label style={hubLbl}>Lead source</label>
            <select style={crmInput} value={f.leadSource} onChange={set("leadSource")}>
              <option value="">— Select source —</option>
              {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label style={hubLbl}>{LEAD_SOURCE_SPECIFY.includes(f.leadSource) ? "Specify (show + year / name)" : "Source detail"}</label>
            <input style={crmInput} placeholder="Optional" value={f.leadSourceDetail} onChange={set("leadSourceDetail")} /></div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, margin: "2px 0 12px", cursor: "pointer" }}>
          <input type="checkbox" checked={f.sourcewellInterest} onChange={e => setF({ ...f, sourcewellInterest: e.target.checked })} />
          Expressed interest in the Sourcewell Contract
        </label>
        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em", margin: "4px 0 8px" }}>Project location <span style={{ textTransform: "none" }}>· state auto-assigns the Regional Sales Director</span></p>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
          <div><label style={hubLbl}>Address</label><input style={crmInput} value={f.projectAddress} onChange={set("projectAddress")} /></div>
          <div><label style={hubLbl}>City</label><input style={crmInput} value={f.projectCity} onChange={set("projectCity")} /></div>
          <div><label style={hubLbl}>State</label>
            <select style={crmInput} value={f.projectState} onChange={set("projectState")}>
              <option value="">—</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label style={hubLbl}>Zip</label><input style={crmInput} value={f.projectZip} onChange={set("projectZip")} /></div>
          <div><label style={hubLbl}>Country</label><input style={crmInput} value={f.projectCountry} onChange={set("projectCountry")} /></div>
          <div><label style={hubLbl}>End user contact</label><input style={crmInput} value={f.endUserContact} onChange={set("endUserContact")} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={hubLbl}>Scheduled follow-up</label><input style={crmInput} type="date" value={f.scheduledFollowUp} onChange={set("scheduledFollowUp")} /></div>
          <div><label style={hubLbl}>Estimated PO receipt</label><input style={crmInput} type="date" value={f.estPoReceiptDate} onChange={set("estPoReceiptDate")} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Create"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CrmOppModal({ oppId, onClose }) {
  const [opp, setOpp] = useState(null);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [reasons, setReasons] = useState([]);
  const [lostPick, setLostPick] = useState(false);
  const load = () => crmApi(`/opportunities/${oppId}`).then(d => setOpp(d.opportunity)).catch(() => {});
  useEffect(() => { load(); crmApi("/close-reasons").then(d => setReasons(d.reasons || [])).catch(() => {}); }, [oppId]); // eslint-disable-line
  const patch = async (body) => { await crmApi(`/opportunities/${oppId}`, { method: "PATCH", body: JSON.stringify(body) }); load(); };
  const setStatus = async (status) => { if (status === "closed_lost") { setLostPick(true); return; } setLostPick(false); await patch({ status }); };
  const closeLost = async (reason) => { await patch({ status: "closed_lost", lostReason: reason }); setLostPick(false); };
  const setConfidence = (v) => patch({ confidence: v || null });
  const setStage = async (stage) => { await crmApi(`/opportunities/${oppId}`, { method: "PATCH", body: JSON.stringify({ stage }) }); load(); };
  const addNote = async () => { if (!note.trim()) return; await crmApi("/activities", { method: "POST", body: JSON.stringify({ opportunityId: oppId, body: note, type: noteType }) }); setNote(""); load(); };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        {!opp ? <p style={{ color: T.textMuted }}>Loading…</p> : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <h2 style={{ fontSize: 19, fontWeight: 700 }}>{opp.name}</h2>
                {opp.oppNumber && <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'JetBrains Mono',monospace" }}>{opp.oppNumber}</span>}
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 14 }}>{opp.account?.name} · <span style={{ color: T.accent, fontWeight: 600 }}>{crmMoney(opp.amount)}</span>{opp.expectedCloseDate ? ` · close ${crmDate(opp.expectedCloseDate)}` : ""}</p>

            {/* Status + OVERALL Confidence (RDP workflow) */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {OPP_STATUSES.map(s => (
                <button key={s.id} onClick={() => setStatus(s.id)} style={{ padding: "5px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${opp.status === s.id ? s.color : T.border}`, background: opp.status === s.id ? s.color + "22" : "transparent", color: opp.status === s.id ? s.color : T.textSecondary }}>{s.label}</button>
              ))}
            </div>
            {lostPick && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>Why was this lost? <span style={{ color: T.textMuted }}>(required)</span></p>
                <select style={{ ...crmInput, marginBottom: 0 }} defaultValue="" onChange={e => e.target.value && closeLost(e.target.value)}>
                  <option value="">— Select a reason —</option>
                  {reasons.map(r => <option key={r.id} value={r.label}>{r.label}</option>)}
                </select>
              </div>
            )}
            {opp.status === "closed_lost" && opp.lostReason && !lostPick && <p style={{ fontSize: 12, color: "#ef4444", marginBottom: 10 }}>Lost — {opp.lostReason}</p>}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: T.textSecondary, whiteSpace: "nowrap" }}>Confidence</label>
              <select value={opp.confidence || ""} onChange={e => setConfidence(e.target.value)} style={{ ...crmInput, marginBottom: 0, flex: 1 }}>
                <option value="">— Not set —</option>
                {OPP_CONFIDENCE.map(c => <option key={c.level} value={c.level}>{c.level} · {c.full}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: 12, marginBottom: 18, color: T.textSecondary }}>
              {opp.rsdEmail && <div>RSD: <span style={{ color: T.textPrimary }}>{opp.rsdEmail}</span></div>}
              {opp.leadSource && <div>Source: <span style={{ color: T.textPrimary }}>{opp.leadSource}{opp.leadSourceDetail ? ` (${opp.leadSourceDetail})` : ""}</span></div>}
              {(opp.projectCity || opp.projectState) && <div>Project: <span style={{ color: T.textPrimary }}>{[opp.projectCity, opp.projectState].filter(Boolean).join(", ")}</span></div>}
              {opp.endUserContact && <div>End user: <span style={{ color: T.textPrimary }}>{opp.endUserContact}</span></div>}
              {opp.estPoReceiptDate && <div>Est. PO: <span style={{ color: T.textPrimary }}>{crmDate(opp.estPoReceiptDate)}</span></div>}
              {opp.scheduledFollowUp && <div>Follow-up: <span style={{ color: T.textPrimary }}>{crmDate(opp.scheduledFollowUp)}</span></div>}
              {opp.sourcewellInterest && <div style={{ color: "#2ea043" }}>✓ Sourcewell interest</div>}
            </div>
            <CrmDealAI oppId={oppId} />
            <CrmEmailComposer opportunityId={oppId} onSent={load} />
            <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Stage</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {CRM_STAGES.map(s => (
                <button key={s.id} onClick={() => setStage(s.id)} style={{ padding: "5px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${opp.stage === s.id ? s.color : T.border}`, background: opp.stage === s.id ? s.color + "22" : "transparent", color: opp.stage === s.id ? s.color : T.textSecondary }}>{s.label}</button>
              ))}
            </div>
            <CrmLineItems oppId={oppId} opp={opp} items={opp.lineItems || []} reload={load} />
            <CrmCustomFields entity="opportunity" customFields={opp.customFields} onSave={cf => crmApi(`/opportunities/${oppId}`, { method: "PATCH", body: JSON.stringify({ customFields: cf }) }).then(load)} />
            <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Activity</p>
            <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
              {CRM_ACT_TYPES.map(at => (
                <button key={at.id} onClick={() => setNoteType(at.id)} style={{ padding: "4px 10px", borderRadius: 14, fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${noteType === at.id ? T.accent : T.border}`, background: noteType === at.id ? T.accent + "22" : "transparent", color: noteType === at.id ? T.accent : T.textSecondary }}>{at.icon} {at.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder={`Log a ${noteType}…`} value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} />
              <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={addNote}>Log</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(opp.activities || []).map(ac => { const m = crmActMeta(ac.type); return (
                <div key={ac.id} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 13, width: 18, textAlign: "center", flexShrink: 0 }}>{m.icon}</span>
                  <div><p style={{ fontSize: 13, color: T.textPrimary }}>{ac.body}</p><p style={{ fontSize: 10, color: T.textMuted }}>{new Date(ac.createdAt).toLocaleString()}</p></div>
                </div>
              ); })}
              {(opp.activities || []).length === 0 && <p style={{ fontSize: 12, color: T.textMuted }}>No activity yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CrmAccountModal({ accountId, onClose }) {
  const [acct, setAcct] = useState(null);
  const [ob, setOb] = useState(null);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const load = () => crmApi(`/accounts/${accountId}`).then(d => setAcct(d.account)).catch(() => {});
  const loadOb = () => crmApi(`/onboarding?accountId=${accountId}`).then(setOb).catch(() => {});
  useEffect(() => { load(); loadOb(); }, [accountId]); // eslint-disable-line
  const setStep = async (stepId, status) => { await crmApi("/onboarding", { method: "PATCH", body: JSON.stringify({ stepId, status }) }); loadOb(); };
  const setStatus = async (status) => { await crmApi(`/accounts/${accountId}`, { method: "PATCH", body: JSON.stringify({ status }) }); load(); };
  const addNote = async () => { if (!note.trim()) return; await crmApi("/activities", { method: "POST", body: JSON.stringify({ accountId, body: note, type: noteType }) }); setNote(""); load(); };
  const OB_NEXT = { pending: "in_progress", in_progress: "done", done: "pending" };
  const OB_COLOR = { done: "#2ea043", in_progress: "#f59e0b", pending: "#8b949e", blocked: "#ef4444" };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 600, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        {!acct ? <p style={{ color: T.textMuted }}>Loading…</p> : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 19, fontWeight: 700 }}>{acct.name}</h2>
                <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>{acct.industry || ""}{acct.website ? ` · ${acct.website}` : ""}</p>
                <select value={acct.status} onChange={e => setStatus(e.target.value)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${T.border}`, background: acct.status === "active" ? "rgba(46,160,67,.15)" : acct.status === "churned" ? "rgba(239,68,68,.15)" : "rgba(148,163,184,.15)", color: acct.status === "active" ? "#2ea043" : acct.status === "churned" ? "#ef4444" : T.textMuted }}>
                  <option value="prospect">prospect</option><option value="active">active</option><option value="churned">churned</option>
                </select>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>

            {/* Address & contact info (Sage-style) */}
            {(acct.addressLine1 || acct.city || acct.state || acct.zip || acct.phone || acct.fax || acct.country) && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 20, fontSize: 12, color: T.textSecondary, display: "flex", flexWrap: "wrap", gap: "4px 20px" }}>
                {(acct.addressLine1 || acct.city || acct.state || acct.zip || acct.country) && (
                  <span>📍 {[acct.addressLine1, acct.addressLine2, [acct.city, acct.state].filter(Boolean).join(", "), acct.zip, acct.country].filter(Boolean).join(", ")}</span>
                )}
                {acct.phone && <span>📞 {acct.phone}</span>}
                {acct.fax && <span>🖷 {acct.fax}</span>}
              </div>
            )}

            {/* Onboarding flow */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary }}>ONBOARDING FLOW</p>
                {ob && <span style={{ fontSize: 12, color: T.accent, fontWeight: 600 }}>{ob.progress}% complete</span>}
              </div>
              {ob && <div style={{ height: 6, background: T.bg, borderRadius: 3, overflow: "hidden", marginBottom: 12 }}><div style={{ height: "100%", width: `${ob.progress}%`, background: "linear-gradient(90deg,#2ea043,#3fb950)" }} /></div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(ob?.steps || []).map(s => (
                  <div key={s.id} onClick={() => setStep(s.id, OB_NEXT[s.status] || "done")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, cursor: "pointer" }}>
                    <span style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${OB_COLOR[s.status]}`, background: s.status === "done" ? OB_COLOR[s.status] : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>{s.status === "done" ? "✓" : ""}</span>
                    <span style={{ fontSize: 13, flex: 1, color: s.status === "done" ? T.textMuted : T.textPrimary, textDecoration: s.status === "done" ? "line-through" : "none" }}>{s.label}</span>
                    <span style={{ fontSize: 10, color: OB_COLOR[s.status], textTransform: "uppercase", letterSpacing: ".04em" }}>{s.status.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Opportunities */}
            <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>OPPORTUNITIES</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
              {(acct.opportunities || []).map(o => { const st = CRM_STAGES.find(s => s.id === o.stage); return (
                <div key={o.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 13 }}>{o.name}</span>
                  <span style={{ fontSize: 12 }}><span style={{ color: st?.color }}>{st?.label}</span> · <span style={{ color: T.accent, fontWeight: 600 }}>{crmMoney(o.amount)}</span></span>
                </div>
              ); })}
              {(acct.opportunities || []).length === 0 && <p style={{ fontSize: 12, color: T.textMuted }}>No opportunities.</p>}
            </div>

            {/* Contacts */}
            <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>CONTACTS</p>
            <CrmContacts accountId={accountId} contacts={acct.contacts} reload={load} />

            <div style={{ marginTop: 20 }}>
              <CrmCustomFields entity="account" customFields={acct.customFields} onSave={cf => crmApi(`/accounts/${accountId}`, { method: "PATCH", body: JSON.stringify({ customFields: cf }) }).then(load)} />
            </div>

            {/* Activity */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "20px 0 8px" }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary }}>ACTIVITY</p>
              <CrmAccountSummary accountId={accountId} />
            </div>
            <CrmEmailComposer accountId={accountId} onSent={load} />
            <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
              {CRM_ACT_TYPES.map(at => (
                <button key={at.id} onClick={() => setNoteType(at.id)} style={{ padding: "4px 10px", borderRadius: 14, fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${noteType === at.id ? T.accent : T.border}`, background: noteType === at.id ? T.accent + "22" : "transparent", color: noteType === at.id ? T.accent : T.textSecondary }}>{at.icon} {at.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder={`Log a ${noteType}…`} value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} />
              <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={addNote}>Log</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(acct.activities || []).map(ac => { const m = crmActMeta(ac.type); return (
                <div key={ac.id} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 13, width: 18, textAlign: "center", flexShrink: 0 }}>{m.icon}</span>
                  <div><p style={{ fontSize: 13, color: T.textPrimary }}>{ac.body}</p><p style={{ fontSize: 10, color: T.textMuted }}>{new Date(ac.createdAt).toLocaleString()}</p></div>
                </div>
              ); })}
              {(acct.activities || []).length === 0 && <p style={{ fontSize: 12, color: T.textMuted }}>No activity yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── AI deal-risk scoring + next-best-action (Opus) ──
const CRM_RAG = { green: { c: "#2ea043", l: "Healthy" }, amber: { c: "#f59e0b", l: "Needs attention" }, red: { c: "#ef4444", l: "At risk" } };
function CrmDealAI({ oppId }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const run = async () => {
    setLoading(true); setErr(null);
    try { setRes(await crmApi("/ai", { method: "POST", body: JSON.stringify({ action: "deal-risk", opportunityId: oppId }) })); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  const rag = res && (CRM_RAG[res.rating] || CRM_RAG.amber);
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>✨ AI Deal Health</span>
        <button className="btn-secondary" style={{ width: "auto", padding: "5px 12px", fontSize: 12 }} disabled={loading} onClick={run}>{loading ? "Analyzing…" : res ? "Re-analyze" : "Analyze risk"}</button>
      </div>
      {err && <p style={{ fontSize: 12, color: "#f87171", marginTop: 8 }}>⚠ {err}</p>}
      {rag && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: rag.c }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: rag.c }}>{rag.l}</span>
            <span style={{ fontSize: 13, color: T.textPrimary }}>— {res.headline}</span>
          </div>
          {(res.risks || []).length > 0 && (
            <ul style={{ margin: "6px 0 8px 18px", padding: 0 }}>
              {res.risks.map((r, i) => <li key={i} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 2 }}>{r}</li>)}
            </ul>
          )}
          <div style={{ fontSize: 12, color: T.textPrimary, background: T.accent + "14", border: `1px solid ${T.accent}44`, borderRadius: 8, padding: "8px 10px" }}>
            <b style={{ color: T.accent }}>Next best action:</b> {res.nextBestAction}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI account summary (Opus) ──
function CrmAccountSummary({ accountId }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setLoading(true);
    try { setRes(await crmApi("/ai", { method: "POST", body: JSON.stringify({ action: "summarize", accountId }) })); }
    catch (e) { setRes({ summary: "⚠ " + e.message, highlights: [] }); } finally { setLoading(false); }
  };
  return (
    <>
      <button className="btn-secondary" style={{ width: "auto", padding: "4px 12px", fontSize: 11 }} disabled={loading} onClick={run}>{loading ? "Summarizing…" : "✨ Summarize"}</button>
      {res && (
        <div style={{ width: "100%", marginTop: 8, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
          <p style={{ fontSize: 13, color: T.textPrimary }}>{res.summary}</p>
          {(res.highlights || []).length > 0 && (
            <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
              {res.highlights.map((h, i) => <li key={i} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 2 }}>{h}</li>)}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

// ── Email composer: AI draft + send via Graph, logged to the timeline ──
function CrmEmailComposer({ opportunityId, accountId, onSent }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to: "", subject: "", body: "" });
  const [intent, setIntent] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const draft = async () => {
    setDrafting(true); setMsg(null);
    try {
      const d = await crmApi("/ai", { method: "POST", body: JSON.stringify({ action: "draft-email", opportunityId, accountId, intent }) });
      setF(prev => ({ ...prev, subject: d.subject, body: d.body }));
    } catch (e) { setMsg("⚠ " + e.message); } finally { setDrafting(false); }
  };
  const send = async () => {
    if (!f.to.trim() || !f.subject.trim() || !f.body.trim()) { setMsg("Fill in to, subject and body."); return; }
    setSending(true); setMsg(null);
    try {
      await crmApi("/email", { method: "POST", body: JSON.stringify({ ...f, opportunityId, accountId }) });
      setOpen(false); setF({ to: "", subject: "", body: "" }); setIntent("");
      onSent && onSent();
    } catch (e) { setMsg("⚠ " + e.message); } finally { setSending(false); }
  };
  if (!open) return (
    <button className="btn-secondary" style={{ width: "auto", padding: "6px 14px", fontSize: 12, marginBottom: 14 }} onClick={() => setOpen(true)}>✉ Email</button>
  );
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Email (sent from your mailbox · logged to timeline)</span>
        <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 14 }}>✕</button>
      </div>
      <input style={crmInput} placeholder="To (email address)" value={f.to} onChange={e => setF({ ...f, to: e.target.value })} />
      <div style={{ display: "flex", gap: 8 }}>
        <input style={{ ...crmInput, flex: 1 }} placeholder="What's the goal? (e.g. nudge for a decision)" value={intent} onChange={e => setIntent(e.target.value)} />
        <button className="btn-secondary" style={{ width: "auto", padding: "0 14px", fontSize: 12, marginBottom: 10 }} disabled={drafting} onClick={draft}>{drafting ? "Drafting…" : "✨ Draft"}</button>
      </div>
      <input style={crmInput} placeholder="Subject" value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} />
      <textarea style={{ ...crmInput, minHeight: 140, resize: "vertical" }} placeholder="Body" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} />
      {msg && <p style={{ fontSize: 12, color: "#f87171", marginBottom: 8 }}>{msg}</p>}
      <button className="btn-primary" style={{ width: "auto", padding: "8px 18px", fontSize: 13 }} disabled={sending} onClick={send}>{sending ? "Sending…" : "Send email"}</button>
    </div>
  );
}

// ── Line items + quote (Phase 3) ──
function CrmLineItems({ oppId, opp, items, reload }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ productId: "", name: "", quantity: 1, unitPrice: "" });
  // Type-to-search catalog picker (the catalog can be tens of thousands of items,
  // so we query the server per keystroke instead of loading everything).
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (!adding || f.productId || term.length < 1) { setResults([]); return; }
    const h = setTimeout(() => {
      crmApi(`/products?active=1&q=${encodeURIComponent(term)}&limit=30`)
        .then(d => { setResults(d.products || []); setOpenList(true); })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(h);
  }, [q, adding, f.productId]);
  const pickProduct = (p) => { setF({ productId: p.id, name: p.name, quantity: f.quantity || 1, unitPrice: p.unitPrice }); setQ(p.name); setOpenList(false); };
  const clearPick = (val) => { setF({ ...f, productId: "", name: val }); setQ(val); };
  const total = (items || []).reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const add = async () => {
    if (!f.productId && !f.name.trim()) return;
    await crmApi("/line-items", { method: "POST", body: JSON.stringify({ opportunityId: oppId, ...f, unitPrice: f.unitPrice === "" ? undefined : f.unitPrice }) });
    setF({ productId: "", name: "", quantity: 1, unitPrice: "" }); setQ(""); setOpenList(false); setAdding(false); reload();
  };
  const patch = async (id, field, val) => { await crmApi("/line-items", { method: "PATCH", body: JSON.stringify({ id, [field]: val }) }); reload(); };
  const del = async (id) => { await crmApi(`/line-items?id=${id}`, { method: "DELETE" }); reload(); };
  const printQuote = () => {
    const rows = (items || []).map(i => `<tr><td>${i.name}</td><td style="text-align:center">${i.quantity}</td><td style="text-align:right">${crmMoney(i.unitPrice)}</td><td style="text-align:right">${crmMoney(i.quantity * i.unitPrice)}</td></tr>`).join("");
    const html = `<html><head><title>Quote — ${opp.name}</title><style>body{font-family:Arial,sans-serif;color:#1a1a1a;padding:40px;max-width:760px;margin:auto}h1{font-size:20px}h2{font-size:15px;color:#c47a10}table{width:100%;border-collapse:collapse;margin-top:16px}th,td{padding:8px 10px;border-bottom:1px solid #ddd;font-size:13px}th{text-align:left;color:#666;font-size:11px;text-transform:uppercase}tfoot td{font-weight:700;border-top:2px solid #333;border-bottom:none}.muted{color:#888;font-size:12px}</style></head><body><h2>PowerVision BI</h2><h1>Quote: ${opp.name}</h1><p class="muted">${opp.account?.name || ""} · ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="3" style="text-align:right">Total</td><td style="text-align:right">${crmMoney(total)}</td></tr></tfoot></table></body></html>`;
    const w = window.open("", "_blank"); if (!w) return;
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Products / Line Items</span>
        <div style={{ display: "flex", gap: 6 }}>
          {(items || []).length > 0 && <button className="btn-secondary" style={{ width: "auto", padding: "4px 10px", fontSize: 11 }} onClick={printQuote}>🖨 Quote</button>}
          <button className="btn-secondary" style={{ width: "auto", padding: "4px 10px", fontSize: 11 }} onClick={() => setAdding(a => !a)}>{adding ? "Cancel" : "+ Line"}</button>
        </div>
      </div>
      {(items || []).map(i => (
        <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: 13 }}>{i.name}</span>
          <input type="number" defaultValue={i.quantity} onBlur={e => Number(e.target.value) !== i.quantity && patch(i.id, "quantity", e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 56, padding: "5px 8px", textAlign: "center" }} />
          <span style={{ color: T.textMuted, fontSize: 12 }}>×</span>
          <input type="number" defaultValue={i.unitPrice} onBlur={e => Number(e.target.value) !== i.unitPrice && patch(i.id, "unitPrice", e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 90, padding: "5px 8px", textAlign: "right" }} />
          <span style={{ width: 84, textAlign: "right", fontSize: 13, color: T.accent, fontWeight: 600 }}>{crmMoney(i.quantity * i.unitPrice)}</span>
          <button onClick={() => del(i.id)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 13 }}>✕</button>
        </div>
      ))}
      {(items || []).length === 0 && !adding && <p style={{ fontSize: 12, color: T.textMuted }}>No line items. The deal amount can be itemised here.</p>}
      {adding && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <input style={{ ...crmInput, marginBottom: 0 }} placeholder="Search catalog by name or SKU… (or type a custom item)"
              value={f.productId ? f.name : q}
              onChange={e => clearPick(e.target.value)}
              onFocus={() => results.length && setOpenList(true)} />
            {f.productId && <span onClick={() => clearPick("")} title="Clear" style={{ position: "absolute", right: 10, top: 8, cursor: "pointer", color: T.textMuted, fontSize: 13 }}>✕</span>}
            {openList && !f.productId && (results.length > 0 || q.trim()) && (
              <>
                <div onClick={() => setOpenList(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, maxHeight: 260, overflowY: "auto", boxShadow: "0 12px 32px rgba(0,0,0,.35)" }}>
                  {results.map(p => (
                    <div key={p.id} onClick={() => pickProduct(p)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                        {p.sku && <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'JetBrains Mono',monospace" }}>{p.sku}</span>}
                      </span>
                      <span style={{ fontSize: 12, color: T.accent, fontFamily: "'JetBrains Mono',monospace", flexShrink: 0 }}>{crmMoney(p.unitPrice)}</span>
                    </div>
                  ))}
                  {results.length === 0 && <div style={{ padding: "10px", fontSize: 12, color: T.textMuted }}>No catalog match — press Add to use “{q.trim()}” as a custom item.</div>}
                </div>
              </>
            )}
          </div>
          <input type="number" style={{ ...crmInput, marginBottom: 0, width: 56 }} placeholder="Qty" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} />
          <input type="number" style={{ ...crmInput, marginBottom: 0, width: 90 }} placeholder="Unit $" value={f.unitPrice} onChange={e => setF({ ...f, unitPrice: e.target.value })} />
          <button className="btn-primary" style={{ width: "auto", padding: "7px 14px", fontSize: 12 }} onClick={add}>Add</button>
        </div>
      )}
      {(items || []).length > 0 && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}`, fontSize: 13 }}>Total: <span style={{ color: T.accent, fontWeight: 700, marginLeft: 8 }}>{crmMoney(total)}</span></div>}
    </div>
  );
}

// ── Per-tenant custom fields renderer (Phase 4) ──
function CrmCustomFields({ entity, customFields, onSave }) {
  const [defs, setDefs] = useState(null);
  const [vals, setVals] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { crmApi(`/fields?entity=${entity}`).then(d => setDefs(d.fields)).catch(() => setDefs([])); }, [entity]);
  useEffect(() => { setVals(customFields || {}); setDirty(false); }, [customFields]);
  if (!defs || defs.length === 0) return null;
  const set = (k, v) => { setVals(prev => ({ ...prev, [k]: v })); setDirty(true); };
  const save = async () => { setSaving(true); try { await onSave(vals); setDirty(false); } finally { setSaving(false); } };
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Details</p>
        {dirty && <button className="btn-primary" style={{ width: "auto", padding: "4px 12px", fontSize: 11 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
        {defs.map(fd => (
          <div key={fd.id}>
            <label style={{ fontSize: 11, color: T.textSecondary, display: "block", marginBottom: 3 }}>{fd.label}</label>
            {fd.type === "select" ? (
              <select style={{ ...crmInput, marginBottom: 0 }} value={vals[fd.key] || ""} onChange={e => set(fd.key, e.target.value)}>
                <option value="">—</option>
                {(fd.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : fd.type === "checkbox" ? (
              <input type="checkbox" checked={!!vals[fd.key]} onChange={e => set(fd.key, e.target.checked)} />
            ) : (
              <input type={fd.type === "number" ? "number" : fd.type === "date" ? "date" : "text"} style={{ ...crmInput, marginBottom: 0 }} value={vals[fd.key] || ""} onChange={e => set(fd.key, e.target.value)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CRM Settings: products, custom fields, automation (Phase 3/4) ──
function CrmSettings({ onClose }) {
  const [tab, setTab] = useState("products");
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 720, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>CRM Settings</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 4, marginBottom: 18, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, width: "fit-content" }}>
          {[["products", "Products"], ["fields", "Custom Fields"], ["rules", "Automation"], ["territories", "Territories"], ["reasons", "Close Reasons"], ["ids", "Opportunity IDs"], ["access", "Access"], ["tasks", "Task Workflow"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: "6px 16px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: tab === id ? T.surface : "transparent", color: tab === id ? T.accent : T.textSecondary }}>{label}</button>
          ))}
        </div>
        {tab === "products" && <CrmProducts />}
        {tab === "fields" && <CrmFieldDefs />}
        {tab === "rules" && <CrmRules />}
        {tab === "territories" && <CrmTerritories />}
        {tab === "reasons" && <CrmCloseReasons />}
        {tab === "ids" && <CrmOppConfig />}
        {tab === "access" && <CrmAccessConfig />}
        {tab === "tasks" && <CrmTaskSettings />}
      </div>
    </div>
  );
}

function CrmTerritories() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ state: "", rsdName: "", rsdEmail: "" });
  const load = () => crmApi("/territories").then(d => setList(d.territories)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const save = async () => { if (!f.state || !f.rsdEmail.trim()) return; try { await crmApi("/territories", { method: "PUT", body: JSON.stringify(f) }); setF({ state: "", rsdName: "", rsdEmail: "" }); load(); } catch (e) { alert(e.message); } };
  const remove = async (t) => { try { await crmApi("/territories", { method: "PUT", body: JSON.stringify({ state: t.state, rsdEmail: "" }) }); load(); } catch (e) { alert(e.message); } };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>Map each state to its Regional Sales Director. New opportunities and leads auto-assign the RSD from their project state.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <select style={{ ...crmInput, marginBottom: 0, width: 90 }} value={f.state} onChange={e => setF({ ...f, state: e.target.value })}><option value="">State</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <input style={{ ...crmInput, marginBottom: 0, width: 150 }} placeholder="RSD name" value={f.rsdName} onChange={e => setF({ ...f, rsdName: e.target.value })} />
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 160 }} placeholder="rsd@rain-drop.com" value={f.rsdEmail} onChange={e => setF({ ...f, rsdEmail: e.target.value })} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={save}>Set</button>
      </div>
      {list === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> : list.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No territories mapped yet.</p> : list.map(t => (
        <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ width: 40, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{t.state}</span>
          <span style={{ flex: 1 }}>{t.rsdName ? `${t.rsdName} · ` : ""}<span style={{ color: T.textMuted }}>{t.rsdEmail}</span></span>
          <button onClick={() => remove(t)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmCloseReasons() {
  const [list, setList] = useState(null);
  const [label, setLabel] = useState("");
  const load = () => crmApi("/close-reasons").then(d => setList(d.reasons)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!label.trim()) return; try { await crmApi("/close-reasons", { method: "POST", body: JSON.stringify({ label }) }); setLabel(""); load(); } catch (e) { alert(e.message); } };
  const del = async (r) => { try { await crmApi(`/close-reasons?id=${r.id}`, { method: "DELETE" }); load(); } catch (e) { alert(e.message); } };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>Reasons offered when an opportunity is Closed-Lost. Tailor them to your business.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder="Add a reason…" value={label} onChange={e => setLabel(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {list === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> : list.map(r => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ flex: 1 }}>{r.label}</span>
          <button onClick={() => del(r)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmOppConfig() {
  const [prefix, setPrefix] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => { crmApi("/config").then(d => setPrefix(d.config.oppPrefix || "")).catch(() => {}); }, []);
  const save = async () => { try { const d = await crmApi("/config", { method: "PUT", body: JSON.stringify({ oppPrefix: prefix }) }); setPrefix(d.config.oppPrefix || ""); setMsg("Saved"); } catch (e) { setMsg(e.message); } };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>Prefix for opportunity IDs, e.g. <b>RDP</b> → RDP-0001. Numbers increment automatically per opportunity.</p>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input style={{ ...crmInput, marginBottom: 0, width: 140 }} placeholder="RDP" value={prefix} onChange={e => setPrefix(e.target.value)} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={save}>Save</button>
        {msg && <span style={{ fontSize: 12, color: T.textMuted }}>{msg}</span>}
      </div>
    </div>
  );
}

function CrmTaskSettings() {
  const [cfg, setCfg] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [emails, setEmails] = useState({});
  const [msg, setMsg] = useState("");
  const loadCfg = () => crmApi("/config").then(d => { setCfg(d.config); setIsAdmin(!!d.isAdmin); }).catch(() => {});
  const loadAssigners = () => crmApi("/task-assigners").then(d => { const m = {}; (d.assigners || []).forEach(a => m[a.assignmentGroup] = a.assignerEmail); setEmails(m); }).catch(() => {});
  useEffect(() => { loadCfg(); loadAssigners(); }, []);
  const toggleEmail = async (next) => { try { const d = await crmApi("/config", { method: "PUT", body: JSON.stringify({ taskAssignerEmailsEnabled: next }) }); setCfg(d.config); setMsg("Saved"); setTimeout(() => setMsg(""), 1500); } catch (e) { setMsg(e.message); } };
  const saveAssigner = async (group) => { try { await crmApi("/task-assigners", { method: "PUT", body: JSON.stringify({ assignmentGroup: group, assignerEmail: emails[group] || "" }) }); setMsg("Saved " + group); setTimeout(() => setMsg(""), 1500); } catch (e) { setMsg(e.message); } };
  if (cfg === null) return <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p>;
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12 }}>When a task is requested, its group's Task Assigner can be emailed to approve, prioritise and assign it. Toggle the emails and map each group to its assigner below.</p>
      <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: isAdmin ? "pointer" : "not-allowed", opacity: isAdmin ? 1 : 0.6, marginBottom: 18 }}>
        <input type="checkbox" checked={!!cfg.taskAssignerEmailsEnabled} disabled={!isAdmin} onChange={e => toggleEmail(e.target.checked)} style={{ width: 16, height: 16, accentColor: T.accent }} />
        <span>Email the Task Assigner when a task is created <span style={{ color: T.textMuted, fontSize: 12 }}>(off during testing)</span></span>
      </label>
      <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>Assigner by group</p>
      {TASK_GROUPS.map(g => (
        <div key={g} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0", borderTop: `1px solid ${T.border}` }}>
          <span style={{ width: 170, fontSize: 13, color: T.textSecondary }}>{g}</span>
          <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder="assigner@rain-drop.com" value={emails[g] || ""} onChange={e => setEmails({ ...emails, [g]: e.target.value })} disabled={!isAdmin} />
          <button className="btn-secondary" style={{ width: "auto", padding: "0 14px", fontSize: 12, height: 34 }} disabled={!isAdmin} onClick={() => saveAssigner(g)}>Save</button>
        </div>
      ))}
      {!isAdmin && <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>Only an admin can change these settings.</p>}
      {msg && <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>{msg}</p>}
    </div>
  );
}

function CrmAccessConfig() {
  const [cfg, setCfg] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { crmApi("/config").then(d => { setCfg(d.config); setIsAdmin(!!d.isAdmin); }).catch(() => {}); }, []);
  const save = async (next) => {
    try { const d = await crmApi("/config", { method: "PUT", body: JSON.stringify({ restrictBySalesperson: next }) }); setCfg(d.config); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); }
    catch (e) { setMsg(e.message); }
  };
  if (cfg === null) return <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p>;
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12 }}>Control who can see CRM records. When restricted, each salesperson sees only the accounts, opportunities and tasks they own; admins always see everyone and can filter by salesperson.</p>
      <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: isAdmin ? "pointer" : "not-allowed", opacity: isAdmin ? 1 : 0.6 }}>
        <input type="checkbox" checked={!!cfg.restrictBySalesperson} disabled={!isAdmin} onChange={e => save(e.target.checked)} style={{ width: 16, height: 16, accentColor: T.accent }} />
        <span>Restrict visibility by salesperson</span>
      </label>
      {!isAdmin && <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>Only an admin can change this setting.</p>}
      {msg && <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>{msg}</p>}
    </div>
  );
}

function CrmProducts() {
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [limited, setLimited] = useState(false);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ name: "", sku: "", unitPrice: "" });
  const load = (term) => {
    const query = term !== undefined ? term : q;
    crmApi(`/products?limit=100${query.trim() ? `&q=${encodeURIComponent(query.trim())}` : ""}`)
      .then(d => { setList(d.products); setTotal(d.total ?? d.products.length); setLimited(!!d.limited); })
      .catch(() => setList([]));
  };
  useEffect(() => { load(""); }, []); // eslint-disable-line
  useEffect(() => { const h = setTimeout(() => load(), 220); return () => clearTimeout(h); }, [q]); // eslint-disable-line
  const add = async () => { if (!f.name.trim()) return; await crmApi("/products", { method: "POST", body: JSON.stringify(f) }); setF({ name: "", sku: "", unitPrice: "" }); load(); };
  const toggle = async (p) => { await crmApi(`/products/${p.id}`, { method: "PATCH", body: JSON.stringify({ active: !p.active }) }); load(); };
  const del = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await crmApi(`/products/${p.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 2 }} placeholder="Product name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder="SKU" value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} />
        <input type="number" style={{ ...crmInput, marginBottom: 0, width: 110 }} placeholder="Unit $" value={f.unitPrice} onChange={e => setF({ ...f, unitPrice: e.target.value })} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder="Search products by name or SKU…" value={q} onChange={e => setQ(e.target.value)} />
        <span style={{ fontSize: 12, color: T.textMuted, whiteSpace: "nowrap" }}>{total.toLocaleString()} product{total === 1 ? "" : "s"}</span>
      </div>
      {limited && <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Showing first {list?.length} — refine your search to narrow.</p>}
      {list === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> : list.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>{q ? "No products match." : "No products yet."}</p> : list.map(p => (
        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ flex: 1, fontSize: 13, opacity: p.active ? 1 : 0.5 }}>{p.name}{p.sku ? <span style={{ color: T.textMuted }}> · {p.sku}</span> : ""}</span>
          <span style={{ fontSize: 13, color: T.accent, fontWeight: 600 }}>{crmMoney(p.unitPrice)}</span>
          <button className="btn-secondary" style={{ width: "auto", padding: "3px 10px", fontSize: 11 }} onClick={() => toggle(p)}>{p.active ? "Active" : "Inactive"}</button>
          <button onClick={() => del(p)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmFieldDefs() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ entity: "account", label: "", type: "text", options: "" });
  const load = () => crmApi("/fields").then(d => setList(d.fields)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.label.trim()) return;
    const body = { entity: f.entity, label: f.label, type: f.type, options: f.type === "select" ? f.options.split(",").map(s => s.trim()).filter(Boolean) : undefined };
    try { await crmApi("/fields", { method: "POST", body: JSON.stringify(body) }); setF({ ...f, label: "", options: "" }); load(); } catch (e) { alert(e.message); }
  };
  const del = async (fd) => { await crmApi(`/fields?id=${fd.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>Custom fields appear in the Details section of each record.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <select style={{ ...crmInput, marginBottom: 0, width: 130 }} value={f.entity} onChange={e => setF({ ...f, entity: e.target.value })}>
          <option value="account">Account</option><option value="opportunity">Opportunity</option>
        </select>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }} placeholder="Field label" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} />
        <select style={{ ...crmInput, marginBottom: 0, width: 120 }} value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
          {["text", "number", "select", "date", "checkbox"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {f.type === "select" && <input style={{ ...crmInput }} placeholder="Options, comma-separated (e.g. SMB, Mid, Enterprise)" value={f.options} onChange={e => setF({ ...f, options: e.target.value })} />}
      {(list || []).map(fd => (
        <div key={fd.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 11, color: T.textMuted, width: 90, textTransform: "capitalize" }}>{fd.entity}</span>
          <span style={{ flex: 1, fontSize: 13 }}>{fd.label}</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{fd.type}</span>
          <button onClick={() => del(fd)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmRules() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", toStage: "", taskTitle: "", taskType: "task", priority: "normal", dueInDays: 3 });
  const load = () => crmApi("/rules").then(d => setList(d.rules)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name.trim() || !f.taskTitle.trim()) return;
    const body = { name: f.name, config: { toStage: f.toStage || null, taskTitle: f.taskTitle, taskType: f.taskType, priority: f.priority, dueInDays: f.dueInDays === "" ? null : Number(f.dueInDays) } };
    try { await crmApi("/rules", { method: "POST", body: JSON.stringify(body) }); setF({ ...f, name: "", taskTitle: "" }); load(); } catch (e) { alert(e.message); }
  };
  const toggle = async (r) => { await crmApi("/rules", { method: "PATCH", body: JSON.stringify({ id: r.id, active: !r.active }) }); load(); };
  const del = async (r) => { await crmApi(`/rules?id=${r.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>When an opportunity moves into a stage, automatically create a follow-up task. Use <code>{"{opp}"}</code> for the deal name.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }} placeholder="Rule name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <select style={{ ...crmInput, marginBottom: 0, width: 130 }} value={f.toStage} onChange={e => setF({ ...f, toStage: e.target.value })}>
          <option value="">Any stage</option>
          {CRM_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 160 }} placeholder="Task title (e.g. Send proposal to {opp})" value={f.taskTitle} onChange={e => setF({ ...f, taskTitle: e.target.value })} />
        <select style={{ ...crmInput, marginBottom: 0, width: 110 }} value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}>
          {["low", "normal", "high"].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <input type="number" style={{ ...crmInput, marginBottom: 0, width: 100 }} placeholder="Due in days" value={f.dueInDays} onChange={e => setF({ ...f, dueInDays: e.target.value })} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {(list || []).map(r => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ flex: 1, fontSize: 13, opacity: r.active ? 1 : 0.5 }}>{r.name} <span style={{ color: T.textMuted, fontSize: 11 }}>→ {r.config?.toStage || "any"}: “{r.config?.taskTitle}”</span></span>
          <button className="btn-secondary" style={{ width: "auto", padding: "3px 10px", fontSize: 11 }} onClick={() => toggle(r)}>{r.active ? "On" : "Off"}</button>
          <button onClick={() => del(r)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmContacts({ accountId, contacts, reload }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "" });
  const save = async () => { if (!f.name.trim()) return; await crmApi("/contacts", { method: "POST", body: JSON.stringify({ ...f, accountId }) }); setF({ name: "", email: "", role: "" }); setAdding(false); reload(); };
  const del = async (id) => { await crmApi(`/contacts?id=${id}`, { method: "DELETE" }); reload(); };
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
        {(contacts || []).map(c => (
          <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}` }}>
            <div><span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>{c.role ? <span style={{ fontSize: 12, color: T.textMuted }}> · {c.role}</span> : ""}<div style={{ fontSize: 11, color: T.textMuted }}>{c.email}</div></div>
            <button onClick={() => del(c.id)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
          </div>
        ))}
        {(contacts || []).length === 0 && !adding && <p style={{ fontSize: 12, color: T.textMuted }}>No contacts.</p>}
      </div>
      {adding ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 120 }} placeholder="Name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
          <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 120 }} placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <input style={{ ...crmInput, marginBottom: 0, width: 120 }} placeholder="Role" value={f.role} onChange={e => setF({ ...f, role: e.target.value })} />
          <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={save}>Add</button>
        </div>
      ) : <button onClick={() => setAdding(true)} style={{ background: "none", border: `1px solid ${T.accent}`, borderRadius: 6, padding: "5px 12px", color: T.accent, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>+ Contact</button>}
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("login");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // On mount, check if user is already signed in (MSAL session)
// On mount, check if user is already signed in (MSAL session)
useEffect(() => {
  async function checkSession() {
    const msal = getMsalInstance();
    if (!msal) return;
    await msal.initialize();
    
    // Handle redirect coming back from Microsoft
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult) {
      setUser({
        email: redirectResult.account.username,
        name: redirectResult.account.name || redirectResult.account.username.split("@")[0],
      });
      setToken(redirectResult.idToken);
      sessionStorage.setItem("pvbi_token", redirectResult.idToken);
      setScreen("dashboard");
      return;
    }

    // Check if already signed in
    const accounts = msal.getAllAccounts();
    if (accounts.length) {
      try {
        const result = await msal.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: accounts[0],
          forceRefresh: true, // on refresh, always get a fresh (non-expired) ID token
        });
        setUser({ email: accounts[0].username, name: accounts[0].name || accounts[0].username.split("@")[0] });
        setToken(result.idToken);
        sessionStorage.setItem("pvbi_token", result.idToken);
        setScreen("dashboard");
      } catch {
        // Silent token failed, stay on login
      }
    }
  }
  checkSession();
}, []);

// Keep the Entra ID token fresh so long-lived sessions never hit 401s.
// ID tokens live ~1h; refresh every 25 min and whenever the tab regains focus.
useEffect(() => {
  if (screen !== "dashboard") return;
  const refresh = async () => { const t = await refreshStoredToken(); if (t) setToken(t); };
  const id = setInterval(refresh, 25 * 60 * 1000);
  window.addEventListener("focus", refresh);
  return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
}, [screen]);

 const handleLogin = (u, t) => {
    setUser(u); 
    setToken(t); 
    sessionStorage.setItem("pvbi_token", t);
    setScreen("dashboard");
  };

  const handleLogout = async () => {
    try { await signOut(); } catch {}
    try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch {}
    sessionStorage.removeItem("pvbi_provider");
    setUser(null); setToken(null); setScreen("login");
  };

  return (
    <ErrorBoundary>
      <>
        <style>{css}</style>
        {/* Grid background removed — the faint amber lattice competed with content
            once the home screen gained real charts. The .grid-bg rule is still in
            the stylesheet if it's ever wanted back. */}
        <div className="orb" style={{ width: 500, height: 500, top: -160, left: -120, background: "rgba(196,122,16,0.07)" }} />
        <div className="orb" style={{ width: 350, height: 350, bottom: -100, right: -80, background: "rgba(196,122,16,0.04)" }} />
        {screen === "login" && <Login onLogin={handleLogin} />}
        {screen === "dashboard" && <Dashboard user={user} token={token} onLogout={handleLogout} />}
      </>
    </ErrorBoundary>
  );
}
